import React, { useState, useEffect, useRef } from 'react';
import ReactDOM, { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Zap, Settings, History, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Trash2, MessageCircle, Edit3, MoreVertical, CheckSquare, User, Mail, ArrowLeft, ChevronRight, Tag, Star, Phone, Bot, GitBranch, Share2, ClipboardList, Layers, Sparkles } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import AutomationStepTimeline from '../components/automations/AutomationStepTimeline';
import { 
  getAutomations, 
  createAutomation, 
  toggleAutomationLive, 
  updateAutomation,
  deleteAutomation,
  getCurrentBusinessId,
  getAutomationTriggers,
  saveAutomationTriggers,
  getAutomationActions,
  saveAutomationActions,
  getAutomationConnections,
  saveWireConnections,
  createAutomationConnection,
  getAutomationExecutions,

  getExecutionSteps,
  getReceiverAutomationOptions
} from '../lib/automationsV2';
import { getBoards } from '../lib/boards';
import { getBoardWithColumns } from '../lib/kanban';
import { emailTemplateService } from '../services/emailTemplateService';
import { listIntakeFormTemplates } from '../lib/crmIntakeFormTemplates';
import { getServices } from '../lib/services';
import { getCustomerTags, createCustomerTag } from '../lib/customers';
import { AutomationV2, TriggerNode as TriggerNodeType, ActionNode as ActionNodeType, AutomationExecutionV2, AutomationActionExecutionV2 } from '../types/automationsV2';
import { CRM_OPPORTUNITY_VARIABLE_DEFS, parsePipelineStages } from '../lib/crm/opportunityAutomationContext';
import AIAutomationBuilderModal from '../components/AIAutomationBuilderModal';
import AgentTypePickerModal from '../components/agents/AgentTypePickerModal';
import CreateMenuModal from '../components/automations/CreateMenuModal';
import { listAgents, type AgentV2 } from '../lib/agentsV2';
import { nodesToFlow, type AiWorkflowFlow } from '../lib/aiAutomationBuilder';

interface WireState {
  isDragging: boolean;
  startNodeId: string | null;
  startPosition: { x: number; y: number };
  currentPosition: { x: number; y: number };
}

interface WireConnection {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  fromPort: 'outflow';
  toPort: 'inflow';
}

// Connection Port Component
interface ConnectionPortProps {
  nodeId: string;
  type: 'inflow' | 'outflow';
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onWireDropped?: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
  isConnecting?: boolean;
}

const ConnectionPort: React.FC<ConnectionPortProps> = ({ nodeId, type, onStartWireDrag, onWireDropped, isConnecting }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start wire drags from outflow ports
    if (type !== 'outflow') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get the canvas element to calculate relative position
    const canvasElement = e.currentTarget.closest('[data-canvas]') as HTMLElement;
    if (!canvasElement) return;
    
    const portRect = e.currentTarget.getBoundingClientRect();
    const canvasRect = canvasElement.getBoundingClientRect();
    
    // Calculate position relative to canvas
    const position = {
      x: portRect.left + portRect.width / 2 - canvasRect.left,
      y: portRect.top + portRect.height / 2 - canvasRect.top
    };
    
    console.log(`🔗 Starting wire drag from ${nodeId} (${type}) at position:`, position);
    onStartWireDrag(nodeId, position);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Only handle wire drops on inflow ports
    if (type !== 'inflow' || !onWireDropped) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`🔗 Wire dropped on ${nodeId} (${type})`);
    onWireDropped(nodeId, type);
  };

  const basePos = type === 'inflow'
    ? 'top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
    : 'bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2';
  return (
    <div
      className={`absolute w-4 h-4 rounded-full border-2 shadow-lg flex items-center justify-center ${basePos} ${
        isConnecting
          ? 'border-blue-500 bg-blue-50 cursor-wait'
          : type === 'inflow' 
            ? 'bg-white border-gray-500 cursor-pointer hover:scale-110 hover:bg-gray-50 transition-transform' 
            : 'bg-gray-500 border-white cursor-crosshair hover:scale-110 hover:bg-gray-600 transition-transform'
      }`}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      title={isConnecting ? 'Connecting...' : type === 'inflow' ? 'Input connection' : 'Output connection'}
      data-node-id={nodeId}
      data-port-type={type}
    >
      {isConnecting && (
        <div className="w-2.5 h-2.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      )}
    </div>
  );
};

// Trigger Node Component
interface TriggerNodeComponentProps {
  trigger: TriggerNodeType;
  onMouseDown: (e: React.MouseEvent, triggerId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (triggerId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
  isConnectingOutflow?: boolean;
}

const TriggerNode: React.FC<TriggerNodeComponentProps> = ({ 
  trigger, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped,
  isConnectingOutflow
}) => {
  const formatEventType = (eventType: string, triggerType?: string, config?: any) => {
    if (triggerType === 'feedback') {
      const minRating = config?.minRating || 5;
      return `${minRating} Star${minRating > 1 ? 's' : ''} or More`;
    }
    if (triggerType === 'form_submission') {
      return 'Form Submitted';
    }
    if (triggerType === 'missed_call') {
      return 'Call Missed';
    }
    if (triggerType === 'enter_automation') {
      return 'Receiver';
    }
    if (triggerType === 'enter_crm_stage') {
      return config?.uiLabel || 'CRM stage not configured';
    }
    if (triggerType === 'webhook') {
      return config?.webhook_delivery === 'zapier' ? 'Zapier webhook' : 'Webhook';
    }
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: trigger.position.x,
        top: trigger.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, trigger.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSettingsClick(trigger.id);
        }}
        title="Configure trigger"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          trigger.type === 'enter_automation' ? 'bg-teal-100' :
          trigger.type === 'enter_crm_stage' ? 'bg-indigo-100' : 'bg-purple-100'
        }`}>
          {trigger.type === 'enter_automation' ? (
            <GitBranch className="h-5 w-5 text-teal-600" />
          ) : trigger.type === 'enter_crm_stage' ? (
            <Layers className="h-5 w-5 text-indigo-600" />
          ) : (
            <Zap className="h-5 w-5 text-purple-600" />
          )}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{trigger.name}</h4>
          <p className="text-xs text-gray-500">{formatEventType(trigger.config?.eventType || 'created', trigger.type, trigger.config)}</p>
          {trigger.config?.uiLabel && (
            <p className="text-xs text-purple-600 font-medium mt-1 bg-purple-50 px-2 py-1 rounded-md">
              {trigger.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={trigger.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
        isConnecting={isConnectingOutflow}
      />
    </div>
  );
};

// Action Node Component
interface ActionNodeComponentProps {
  action: ActionNodeType;
  onMouseDown: (e: React.MouseEvent, actionId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (actionId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
  isConnectingInflow?: boolean;
  isConnectingOutflow?: boolean;
}

const ActionNode: React.FC<ActionNodeComponentProps> = ({ 
  action, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped,
  isConnectingInflow,
  isConnectingOutflow
}) => {
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'sms':
        return <MessageCircle className="h-5 w-5 text-green-600" />;
      case 'email':
        return <Mail className="h-5 w-5 text-blue-600" />;
      case 'delay':
        return <Clock className="h-5 w-5 text-orange-600" />;
      case 'create_crm_lead':
        return <Plus className="h-5 w-5 text-purple-600" />;
      case 'check':
        return <Eye className="h-5 w-5 text-yellow-600" />;
      case 'create_task':
        return <CheckSquare className="h-5 w-5 text-indigo-600" />;
      case 'assign_tags':
        return <Tag className="h-5 w-5 text-pink-600" />;
      case 'add_to_automation':
        return <Share2 className="h-5 w-5 text-teal-600" />;
      case 'send_live_intake_form':
        return <ClipboardList className="h-5 w-5 text-cyan-600" />;
      case 'feedback_agent':
        return <Phone className="h-5 w-5 text-amber-600" />;
      default:
        return '⚡';
    }
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: action.position.x,
        top: action.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, action.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void onSettingsClick(action.id);
        }}
        title="Configure action"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      {/* Inflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="inflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
        isConnecting={isConnectingInflow}
      />

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          {getActionIcon(action.type)}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{action.name}</h4>
          <p className="text-xs text-gray-500">Action</p>
          {action.type === 'add_to_automation' && action.config?.targetAutomationName && (
            <p className="text-xs text-teal-700 mt-0.5">→ {action.config.targetAutomationName}</p>
          )}
          {action.type === 'feedback_agent' && action.config?.agentName && (
            <p className="text-xs text-amber-700 mt-0.5">→ {action.config.agentName}</p>
          )}
          {action.type === 'send_live_intake_form' && (action.config?.intake_template_title || action.config?.send_mode) && (
            <p className="text-xs text-cyan-700 mt-0.5">
              {action.config?.intake_template_title ? `${action.config.intake_template_title} · ` : ''}
              {action.config?.send_mode === 'both'
                ? 'SMS + email'
                : action.config?.send_mode === 'sms'
                  ? 'SMS'
                  : 'Email'}
            </p>
          )}
          {action.config?.uiLabel && (
            <p className="text-xs text-green-600 font-medium mt-1 bg-green-50 px-2 py-1 rounded-md">
              {action.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
        isConnecting={isConnectingOutflow}
      />
    </div>
  );
};

const AutomationsV2 = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTriggerLibraryOpen, setIsTriggerLibraryOpen] = useState(false);
  const [isActionLibraryOpen, setIsActionLibraryOpen] = useState(false);
  const [isTriggerSettingsOpen, setIsTriggerSettingsOpen] = useState(false);
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  const [automationTitle, setAutomationTitle] = useState('');
  const [automations, setAutomations] = useState<AutomationV2[]>([]);
  const [selectedAutomation, setSelectedAutomation] = useState<AutomationV2 | null>(null);
  const [triggers, setTriggers] = useState<TriggerNodeType[]>([]);
  const [actions, setActions] = useState<ActionNodeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [wireState, setWireState] = useState<WireState>({
    isDragging: false,
    startNodeId: null,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 }
  });
  
  // New state for execution history
  const [activeTab, setActiveTab] = useState<'workflow' | 'workflow-new' | 'history'>('workflow-new');
  
  // Linear view connection state
  const [selectedOutflowPort, setSelectedOutflowPort] = useState<string | null>(null);
  const [crmBranchConnectModal, setCrmBranchConnectModal] = useState<{
    fromNodeId: string;
    toNodeId: string;
    branch: 'left' | 'right';
    mode: 'linear' | 'canvas';
  } | null>(null);
  /** After picking left/right under an advanced CRM, wire the new action to CRM with this branch. */
  const [pendingConnectAfterActionAdd, setPendingConnectAfterActionAdd] = useState<{
    parentId: string;
    branch: 'left' | 'right';
  } | null>(null);
  /** While saving CRM-branch wiring, keep the new action in the correct column (avoids flash in "orphan" list). */
  const [pendingCrmBranchPlacement, setPendingCrmBranchPlacement] = useState<{
    parentId: string;
    branch: 'left' | 'right';
    actionId: string;
    /** Temp id before reload; orphan filter ignores this until placement clears */
    prevTempActionId?: string;
  } | null>(null);
  const [pendingActionParent, setPendingActionParent] = useState<string | null>(null);
  const [disconnectPopup, setDisconnectPopup] = useState<{
    show: boolean;
    nodeId: string;
    portType: 'inflow' | 'outflow';
    position: { x: number; y: number };
  } | null>(null);
  const [executions, setExecutions] = useState<AutomationExecutionV2[]>([]);
  const [executionSummary, setExecutionSummary] = useState<{
    running_executions: number;
    successful_executions: number;
    failed_executions: number;
    last_execution?: AutomationExecutionV2;
  } | null>(null);
  
  // Execution details modal state
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [executionSteps, setExecutionSteps] = useState<AutomationActionExecutionV2[]>([]);
  const [isExecutionDetailsOpen, setIsExecutionDetailsOpen] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState(false);

  // State for permanent wire connections
  const [wireConnections, setWireConnections] = useState<WireConnection[]>([]);
  
  // Flag to prevent debounced saves during immediate saves
  const [isSavingImmediately, setIsSavingImmediately] = useState(false);

  // Connection in progress - show loading spinner on the target port until persisted
  const [connectingBetween, setConnectingBetween] = useState<{ fromNodeId: string; toNodeId: string } | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const wireRerenderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedBusiness, updateBusiness } = useBusiness();

  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Customer Deliverability settings state
  const [deliverabilitySettings, setDeliverabilitySettings] = useState<{
    [key: string]: {
      enabled: boolean;
      startHour: number; // 0-23
      startMinute: number; // 0-59
      endHour: number; // 0-23
      endMinute: number; // 0-59
    }
  }>({
    monday: { enabled: true, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    tuesday: { enabled: true, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    wednesday: { enabled: true, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    thursday: { enabled: true, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    friday: { enabled: true, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    saturday: { enabled: false, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 },
    sunday: { enabled: false, startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 }
  });
  // Master switch for delivery hours. Off = send anytime (no restriction).
  const [deliveryHoursEnabled, setDeliveryHoursEnabled] = useState(false);
  const [savingDeliveryHours, setSavingDeliveryHours] = useState(false);

  // Hydrate the deliverability editor from the saved business config whenever the
  // settings panel opens (or the business changes).
  useEffect(() => {
    if (!isSettingsOpen) return;
    const cfg = selectedBusiness?.automation_delivery_hours;
    setDeliveryHoursEnabled(!!cfg?.enabled);
    if (cfg?.days) {
      setDeliverabilitySettings((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          const d = (cfg.days as any)[key];
          if (d) {
            const [sh, sm] = String(d.start || '09:00').split(':').map(Number);
            const [eh, em] = String(d.end || '17:00').split(':').map(Number);
            next[key] = {
              enabled: !!d.enabled,
              startHour: Number.isFinite(sh) ? sh : 9,
              startMinute: Number.isFinite(sm) ? sm : 0,
              endHour: Number.isFinite(eh) ? eh : 17,
              endMinute: Number.isFinite(em) ? em : 0,
            };
          }
        }
        return next;
      });
    }
  }, [isSettingsOpen, selectedBusiness?.id]);

  const handleSaveDeliveryHours = async () => {
    if (!selectedBusiness?.id) return;
    setSavingDeliveryHours(true);
    try {
      const pad = (n: number) => String(n).padStart(2, '0');
      const days: Record<string, { enabled: boolean; start: string; end: string }> = {};
      for (const key of Object.keys(deliverabilitySettings)) {
        const d = deliverabilitySettings[key];
        days[key] = {
          enabled: d.enabled,
          start: `${pad(d.startHour)}:${pad(d.startMinute)}`,
          end: `${pad(d.endHour)}:${pad(d.endMinute)}`,
        };
      }
      await updateBusiness(selectedBusiness.id, {
        automation_delivery_hours: { enabled: deliveryHoursEnabled, days } as any,
      });
      toast.success('Delivery hours saved');
      setIsSettingsOpen(false);
    } catch (err) {
      console.error('Failed to save delivery hours:', err);
      toast.error('Could not save delivery hours');
    } finally {
      setSavingDeliveryHours(false);
    }
  };

  // Available trigger types
  const availableTriggers = [
    {
      id: 'booking',
      name: 'Booking',
      description: 'Triggers when a booking is created, updated, or cancelled',
      icon: <Zap className="h-6 w-6 text-purple-600" />,
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 'feedback',
      name: 'Feedback Result',
      description: 'Triggers when a customer submits feedback with a specified star rating',
      icon: <Star className="h-6 w-6 text-yellow-600" />,
      color: 'bg-yellow-100 border-yellow-300'
    },
    {
      id: 'form_submission',
      name: 'Form Submission',
      description: 'Triggers when a form is submitted by a customer',
      icon: <Zap className="h-6 w-6 text-orange-600" />,
      color: 'bg-orange-100 border-orange-300'
    },
    {
      id: 'webhook',
      name: 'Webhooks',
      description: 'Triggers when data is received from a website form or external webhook',
      icon: <Zap className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 'enter_crm_stage',
      name: 'Enter CRM Stage',
      description: 'Triggers when an opportunity enters a specific pipeline stage',
      icon: <Layers className="h-6 w-6 text-indigo-600" />,
      color: 'bg-indigo-100 border-indigo-300'
    },
    {
      id: 'enter_automation',
      name: 'Enter Automation',
      description: 'Receiver only: starts when another automation uses “Add to Automation” and passes the full context into this flow',
      icon: <GitBranch className="h-6 w-6 text-teal-600" />,
      color: 'bg-teal-100 border-teal-300'
    },
    {
      id: 'missed_call',
      name: 'Missed Call',
      description: 'Triggers when an incoming call is missed',
      icon: <Phone className="h-6 w-6 text-red-600" />,
      color: 'bg-red-100 border-red-300'
    }
  ];

  // Available action types
  const availableActions = [
    {
      id: 'sms',
      name: 'SMS',
      description: 'Send an SMS message to customers or team members',
      icon: <MessageCircle className="h-6 w-6 text-green-600" />,
      color: 'bg-green-100 border-green-300'
    },
    {
      id: 'email',
      name: 'Email',
      description: 'Send an email to customers or team members via Gmail',
      icon: <Mail className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 'delay',
      name: 'Wait',
      description: 'Pause the automation for a specified amount of time',
      icon: <Clock className="h-6 w-6 text-orange-600" />,
      color: 'bg-orange-100 border-orange-300'
    },
    {
      id: 'create_crm_lead',
      name: 'Create CRM Lead',
      description: 'Create a new lead card in a CRM pipeline',
      icon: <Plus className="h-6 w-6 text-purple-600" />,
      color: 'bg-purple-100 border-purple-300'
    },
    {
      id: 'check',
      name: 'Check',
      description: 'Check the status of feedback or other conditions',
      icon: <Eye className="h-6 w-6 text-yellow-600" />,
      color: 'bg-yellow-100 border-yellow-300'
    },
    {
      id: 'create_task',
      name: 'Create Task',
      description: 'Create a new task for team members',
      icon: <CheckSquare className="h-6 w-6 text-indigo-600" />,
      color: 'bg-indigo-100 border-indigo-300'
    },
    {
      id: 'add_to_automation',
      name: 'Add to Automation',
      description: 'Hand off to another automation that has an Enter Automation trigger; all current data is forwarded',
      icon: <Share2 className="h-6 w-6 text-teal-600" />,
      color: 'bg-teal-100 border-teal-300'
    },
    {
      id: 'assign_tags',
      name: 'Assign Tags',
      description: 'Assign tags to customers based on booking conditions',
      icon: <Tag className="h-6 w-6 text-pink-600" />,
      color: 'bg-pink-100 border-pink-300'
    },
    {
      id: 'feedback_agent',
      name: 'Feedback Agent',
      description: 'Your AI feedback agent calls the customer and collects a star rating',
      icon: <Phone className="h-6 w-6 text-amber-600" />,
      color: 'bg-amber-100 border-amber-300'
    }
  ];

  // New state for trigger settings
  const [triggerSettingsModal, setTriggerSettingsModal] = useState<{
    isOpen: boolean;
    triggerId: string | null;
    triggerType: string;
    eventType: string;
  }>({
    isOpen: false,
    triggerId: null,
    triggerType: '',
    eventType: 'created'
  });

  // New state for SMS action settings
  const [smsActionSettingsModal, setSmsActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    message: string;
    recipient: string;
    customPhone: string;
  }>({
    isOpen: false,
    actionId: null,
    message: '',
    recipient: '',
    customPhone: ''
  });

  // New state for Email action settings
  const [emailActionSettingsModal, setEmailActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    subject: string;
    message: string;
    recipient: string;
    customEmail: string;
    useTemplate: boolean;
    selectedTemplateId: string | null;
    pdfAttachment: { storagePath: string; filename: string } | null;
    pdfUploading: boolean;
  }>({
    isOpen: false,
    actionId: null,
    subject: '',
    message: '',
    recipient: '',
    customEmail: '',
    useTemplate: false,
    selectedTemplateId: null,
    pdfAttachment: null,
    pdfUploading: false
  });

  // New state for Email Template Selector Modal
  const [emailTemplateSelector, setEmailTemplateSelector] = useState<{
    isOpen: boolean;
    templates: any[];
    loading: boolean;
  }>({
    isOpen: false,
    templates: [],
    loading: false
  });

  // New state for Delay action settings
  const [delayActionSettingsModal, setDelayActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    waitType: 'duration' | 'reminder';
    duration: number;
    unit: 'minutes' | 'hours' | 'days';
    reminderHoursBefore: number;
    reminderReference: 'drop_off_datetime' | 'start_datetime';
  }>({
    isOpen: false,
    actionId: null,
    waitType: 'duration',
    duration: 1,
    unit: 'hours',
    reminderHoursBefore: 24,
    reminderReference: 'drop_off_datetime'
  });

  // New state for Create CRM Lead action settings
  const [crmLeadActionSettingsModal, setCrmLeadActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
  }>({
    isOpen: false,
    actionId: null
  });

  // Feedback Agent action settings: pick which feedback agent places the call.
  // feedbackEnabled mirrors businesses.feedback_enabled — the agent's calls are
  // skipped when the feedback system is off, so the modal surfaces that status.
  const [feedbackAgentActionSettingsModal, setFeedbackAgentActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    agentId: string;
    agents: Array<{ id: string; name: string; status: string }>;
    loading: boolean;
    feedbackEnabled?: boolean | null;
  }>({
    isOpen: false,
    actionId: null,
    agentId: '',
    agents: [],
    loading: false
  });

  // New state for Check action settings
  const [checkActionSettingsModal, setCheckActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    checkType: 'feedback';
    ifCompleted: 'stop' | 'continue';
    ifNotCompleted: 'stop' | 'continue';
  }>({
    isOpen: false,
    actionId: null,
    checkType: 'feedback',
    ifCompleted: 'stop',
    ifNotCompleted: 'continue'
  });

  // New state for Assign Tags action settings
  const [assignTagsActionSettingsModal, setAssignTagsActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    conditions: Array<{
      type: 'services';
      selectedServices: string[];
    }>;
    selectedTags: string[];
  }>({
    isOpen: false,
    actionId: null,
    conditions: [],
    selectedTags: []
  });

  // New state for Create Task action settings
  const [createTaskActionSettingsModal, setCreateTaskActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    selectedBoardId: string;
    selectedColumnId: string;
    taskTitle: string;
    taskDescription: string;
    taskUrgency: 'low' | 'medium' | 'high' | 'alert';
    taskStatus: 'pending' | 'scheduled';
    scheduleOffset: number;
    scheduleUnit: 'hours' | 'days' | 'weeks';
    boards: Array<{id: string, board_title: string}>;
    columns: Array<{id: string, title: string}>;
  }>({
    isOpen: false,
    actionId: null,
    selectedBoardId: '',
    selectedColumnId: '',
    taskTitle: '',
    taskDescription: '',
    taskUrgency: 'medium',
    taskStatus: 'pending',
    scheduleOffset: 1,
    scheduleUnit: 'days',
    boards: [],
    columns: []
  });

  const [addToAutomationActionSettingsModal, setAddToAutomationActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    targetAutomationId: string;
    receiverOptions: Array<{ id: string; name: string }>;
    loadError: string | null;
  }>({
    isOpen: false,
    actionId: null,
    targetAutomationId: '',
    receiverOptions: [],
    loadError: null
  });

  const [crmLiveIntakeFormsEnabled, setCrmLiveIntakeFormsEnabled] = useState(false);

  const [sendLiveIntakeFormSettingsModal, setSendLiveIntakeFormSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    sendMode: 'sms' | 'email' | 'both';
    intakeTemplateId: string;
    templates: Array<{ id: string; title: string }>;
    loadingTemplates: boolean;
    intakeSmsBody: string;
    intakeEmailSubject: string;
    intakeEmailBody: string;
  }>({
    isOpen: false,
    actionId: null,
    sendMode: 'email',
    intakeTemplateId: '',
    templates: [],
    loadingTemplates: false,
    intakeSmsBody: '',
    intakeEmailSubject: '',
    intakeEmailBody: ''
  });

  // Task Components Modal State
  const [taskComponentsModal, setTaskComponentsModal] = useState<{
    isOpen: boolean;
    currentDescription: string;
  }>({
    isOpen: false,
    currentDescription: ''
  });

  // New state for Variables modal
  const [variablesModal, setVariablesModal] = useState<{
    isOpen: boolean;
    currentField: string | null;
    currentValue: string;
  }>({
    isOpen: false,
    currentField: null,
    currentValue: ''
  });

  // State for edit automation title modal
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  
  // State for delete automation modal
  const [isDeleteAutomationModalOpen, setIsDeleteAutomationModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add testing state
  const [isTesting, setIsTesting] = useState(false);

  // State for lead forms
  const [leadForms, setLeadForms] = useState<any[]>([]);
  const [loadingLeadForms, setLoadingLeadForms] = useState(false);

  // State for CRM mapping  
  const [pipelines, setPipelines] = useState<Array<{id: number, pipeline_name: string, stages: Array<{id: number, name: string}>}>>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [selectedFormFields, setSelectedFormFields] = useState<Array<{id: string, type: string, label: string}>>([]);
  const [fieldMappings, setFieldMappings] = useState<{
    selectedPipeline: number | null;
    selectedPipelineName: string;
    selectedStage: number | null;
    selectedStageName: string;
    status?: string;
    title: string;
    value: string;
    lead_info: string;
    source: string;
    custom_fields: { [key: string]: string };
    useCrmAdvancedSplit: boolean;
    useCrmAiRouting: boolean;
    crmAiRoutingPrompt: string;
    crmSplitLeft: {
      selectedPipeline: number | null;
      selectedPipelineName: string;
      selectedStage: number | null;
      selectedStageName: string;
    };
    crmSplitRight: {
      selectedPipeline: number | null;
      selectedPipelineName: string;
      selectedStage: number | null;
      selectedStageName: string;
    };
  }>({
    selectedPipeline: null,
    selectedPipelineName: '',
    selectedStage: null,
    selectedStageName: '',
    status: 'Open',
    title: '',
    value: '',
    lead_info: '',
    source: '',
    custom_fields: {},
    useCrmAdvancedSplit: false,
    useCrmAiRouting: true,
    crmAiRoutingPrompt: '',
    crmSplitLeft: {
      selectedPipeline: null,
      selectedPipelineName: '',
      selectedStage: null,
      selectedStageName: '',
    },
    crmSplitRight: {
      selectedPipeline: null,
      selectedPipelineName: '',
      selectedStage: null,
      selectedStageName: '',
    },
  });

  // Custom fields state for CRM Lead creation
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<Array<{id: string, name: string, type: string}>>([]);

  // References for message editors to insert variables
  const messageEditorRef = useRef<HTMLDivElement>(null);
  const emailMessageEditorRef = useRef<HTMLDivElement>(null);
  const emailSubjectInputRef = useRef<HTMLInputElement>(null);
  const intakeSmsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const intakeEmailSubjectInputRef = useRef<HTMLInputElement>(null);
  const intakeEmailBodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const smsIgnoreSyncRef = useRef(false);
  const emailIgnoreSyncRef = useRef(false);

  // Helper: convert HTML to plain text preserving line breaks (for SMS)
  const htmlToPlainText = (html: string): string => {
    if (!html) return '';
    let text = html;
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/div>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n');
    text = text.replace(/<[^>]+>/g, '');
    text = text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ');
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim();
  };

  // Helper: convert plain text to HTML for editor (preserving line breaks)
  const plainTextToHtml = (text: string): string => {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .split('\n')
      .map(line => `<div>${line || '<br>'}</div>`)
      .join('');
  };

  // Helper: insert text/HTML at cursor position in contentEditable
  const insertAtCursor = (editorRef: React.RefObject<HTMLDivElement>, text: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertHTML', false, text.replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load automations when component mounts or business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadAutomations();
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedBusiness?.id) {
        setCrmLiveIntakeFormsEnabled(false);
        return;
      }
      try {
        const { data } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'crm',
          p_setting_key: 'live_intake_forms',
          p_default_value: false
        });
        if (!cancelled) setCrmLiveIntakeFormsEnabled(Boolean(data));
      } catch {
        if (!cancelled) setCrmLiveIntakeFormsEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id]);

  // Track which automation's workflow is on the canvas to prevent cross-automation saves
  const lastLoadedAutomationIdRef = useRef<string | null>(null);
  const workflowLoadGenerationRef = useRef(0);
  const loadedWorkflowGenerationRef = useRef<number | null>(null);
  const [isWorkflowHydrated, setIsWorkflowHydrated] = useState(false);
  const isWorkflowHydratedRef = useRef(false);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);

  const setWorkflowHydrated = (hydrated: boolean) => {
    isWorkflowHydratedRef.current = hydrated;
    setIsWorkflowHydrated(hydrated);
  };

  const resetWorkflowCanvas = () => {
    workflowLoadGenerationRef.current += 1;
    loadedWorkflowGenerationRef.current = null;
    setWorkflowHydrated(false);
    setTriggers([]);
    setActions([]);
    setWireConnections([]);
  };

  const canPersistWorkflow = (automationId?: string | null) => {
    const id = automationId ?? selectedAutomation?.id;
    return Boolean(
      id &&
      isWorkflowHydratedRef.current &&
      id === lastLoadedAutomationIdRef.current &&
      loadedWorkflowGenerationRef.current !== null &&
      loadedWorkflowGenerationRef.current === workflowLoadGenerationRef.current
    );
  };

  type WorkflowPersistContext = {
    automationId: string;
    generation: number;
  };

  const getActivePersistContext = (): WorkflowPersistContext | null => {
    const automationId = lastLoadedAutomationIdRef.current;
    const generation = loadedWorkflowGenerationRef.current;
    if (!automationId || generation === null) return null;
    if (!canPersistWorkflow(automationId)) return null;
    return { automationId, generation };
  };

  const isPersistContextValid = (context: WorkflowPersistContext) =>
    lastLoadedAutomationIdRef.current === context.automationId &&
    loadedWorkflowGenerationRef.current === context.generation &&
    workflowLoadGenerationRef.current === context.generation;

  const loadWorkflowData = async (automationId: string, generation: number) => {
    setIsWorkflowLoading(true);
    try {
      const [triggersData, actionsData, connectionsData] = await Promise.all([
        getAutomationTriggers(automationId),
        getAutomationActions(automationId),
        getAutomationConnections(automationId)
      ]);

      if (workflowLoadGenerationRef.current !== generation) return;

      setTriggers(triggersData);
      setActions(actionsData);
      setWireConnections(connectionsData);
      loadedWorkflowGenerationRef.current = generation;
      setWorkflowHydrated(true);
    } catch (err) {
      if (workflowLoadGenerationRef.current !== generation) return;
      console.error('Error loading automation workflow:', err);
      setError('Failed to load automation workflow');
      setWorkflowHydrated(false);
    } finally {
      if (workflowLoadGenerationRef.current === generation) {
        setIsWorkflowLoading(false);
      }
    }
  };

  useEffect(() => {
    const currentId = selectedAutomation?.id || null;

    if (currentId !== lastLoadedAutomationIdRef.current) {
      if (currentId) {
        loadedWorkflowGenerationRef.current = null;
        setWorkflowHydrated(false);
        lastLoadedAutomationIdRef.current = currentId;
        const generation = workflowLoadGenerationRef.current;
        void loadWorkflowData(currentId, generation);
      } else {
        lastLoadedAutomationIdRef.current = null;
        resetWorkflowCanvas();
      }
    }
  }, [selectedAutomation?.id]);

  // Load execution history when tab changes to history
  useEffect(() => {
    if (selectedAutomation?.id && activeTab === 'history') {
      loadExecutionHistory();
    }
  }, [selectedAutomation?.id, activeTab]);



  // ResizeObserver to detect when nodes finish layout changes
  useEffect(() => {
    if (!canvasRef.current || triggers.length === 0 && actions.length === 0) return;

    const observer = new ResizeObserver((entries) => {
      // Debounce the wire re-render to avoid excessive updates
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
      wireRerenderTimeoutRef.current = setTimeout(() => {
        console.log('📐 ResizeObserver detected layout changes - updating wires');
        setWireConnections(prev => [...prev]);
      }, 100);
    });

    // Observe all node elements for layout changes
    const nodeElements = canvasRef.current.querySelectorAll('[data-node-id]');
    nodeElements.forEach(element => observer.observe(element));

    return () => {
      observer.disconnect();
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
    };
  }, [triggers.length, actions.length]);

  // Multiple trigger approach for wire positioning
  useEffect(() => {
    if (triggers.length > 0 || actions.length > 0) {
      // Immediate attempt (for cases where DOM is ready)
      setTimeout(() => {
        console.log('🔗 Immediate wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 50);

      // Delayed attempt (for slower rendering)
      setTimeout(() => {
        console.log('🔗 Delayed wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 300);

      // Final attempt (fallback)
      setTimeout(() => {
        console.log('🔗 Final wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 800);
    }
  }, [triggers.length, actions.length]);

  // Also re-render when wire connections are first loaded
  useEffect(() => {
    if (wireConnections.length > 0) {
      setTimeout(() => {
        console.log('🔗 Wire connections loaded - positioning update');
        setWireConnections(prev => [...prev]);
      }, 100);
    }
  }, [wireConnections.length]);

  // Save triggers to database when triggers change (with debounce)
  useEffect(() => {
    if (!canPersistWorkflow()) return;

    const timeoutId = setTimeout(() => {
      saveTriggers();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [triggers, selectedAutomation?.id, isWorkflowHydrated]);

  // Save actions to database when actions change (with debounce)
  useEffect(() => {
    if (!canPersistWorkflow()) return;

    const timeoutId = setTimeout(() => {
      saveActions();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [actions, selectedAutomation?.id, isWorkflowHydrated]);

  // Save wire connections to database when connections change (with debounce)
  useEffect(() => {
    if (!canPersistWorkflow() || isSavingImmediately) return;

    const timeoutId = setTimeout(() => {
      saveConnections();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [wireConnections, selectedAutomation?.id, isWorkflowHydrated, isSavingImmediately]);

  // Handle mouse events for node dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !draggedId || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const newX = Math.max(0, e.clientX - rect.left - dragOffset.x);
      const newY = Math.max(0, e.clientY - rect.top - dragOffset.y);

      // Update trigger positions
      setTriggers(prev => prev.map(trigger => 
        trigger.id === draggedId 
          ? { ...trigger, position: { x: newX, y: newY } }
          : trigger
      ));

      // Update action positions
      setActions(prev => prev.map(action => 
        action.id === draggedId 
          ? { ...action, position: { x: newX, y: newY } }
          : action
      ));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDraggedId(null);
      setDragOffset({ x: 0, y: 0 });
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, draggedId, dragOffset]);

  // Handle mouse events for wire dragging
  useEffect(() => {
    const handleWireMouseMove = (e: MouseEvent) => {
      if (!wireState.isDragging || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      setWireState(prev => ({
        ...prev,
        currentPosition: { x: currentX, y: currentY }
      }));
    };

    const handleWireMouseUp = () => {
      if (wireState.isDragging) {
        console.log('🔗 Wire drag ended - from:', wireState.startPosition, 'to:', wireState.currentPosition);
        setWireState({
          isDragging: false,
          startNodeId: null,
          startPosition: { x: 0, y: 0 },
          currentPosition: { x: 0, y: 0 }
        });
      }
    };

    if (wireState.isDragging) {
      document.addEventListener('mousemove', handleWireMouseMove);
      document.addEventListener('mouseup', handleWireMouseUp);
      document.body.style.cursor = 'crosshair';
      console.log('🔗 Wire dragging active - from node:', wireState.startNodeId);
    }

    return () => {
      document.removeEventListener('mousemove', handleWireMouseMove);
      document.removeEventListener('mouseup', handleWireMouseUp);
      if (wireState.isDragging) {
        document.body.style.cursor = '';
      }
    };
  }, [wireState.isDragging]);

  // Debug wire state changes
  useEffect(() => {
    console.log('🔗 WireState changed:', wireState);
    if (wireState.isDragging) {
      console.log('🔗 Wire should be visible now! Position from', wireState.startPosition, 'to', wireState.currentPosition);
    }
  }, [wireState]);

  // Track if we're currently loading to prevent race conditions
  const isLoadingAutomationsRef = useRef(false);

  const loadAutomations = async () => {
    if (!selectedBusiness?.id) return;
    
    // Prevent multiple simultaneous loads
    if (isLoadingAutomationsRef.current) {
      console.log('⏸️ loadAutomations already in progress, skipping...');
      return;
    }
    
    try {
      isLoadingAutomationsRef.current = true;
      setLoading(true);
      setError(null);
      const data = await getAutomations(selectedBusiness.id);
      setAutomations(data);
      
      // CRITICAL FIX: If we have a selectedAutomation, update it to point to the new object
      // This prevents stale references that cause triggers/actions to disappear
      if (selectedAutomation?.id) {
        const updatedAutomation = data.find(a => a.id === selectedAutomation.id);
        if (updatedAutomation) {
          // Update selectedAutomation to use the new object reference
          // This ensures the useEffect doesn't think the automation changed when it's the same ID
          setSelectedAutomation(prev => {
            // Only update if the ID matches and it's actually a different object
            if (prev?.id === updatedAutomation.id) {
              return updatedAutomation;
            }
            return prev;
          });
        } else {
          // Automation was deleted, clear selection
          handleClearSelectedAutomation();
        }
      }
    } catch (err) {
      console.error('Error loading automations:', err);
      setError('Failed to load automations');
    } finally {
      setLoading(false);
      isLoadingAutomationsRef.current = false;
    }
  };

  const loadExecutionHistory = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const executionsData = await getAutomationExecutions(selectedAutomation.id);
      // Cast to the correct type since the data structure matches
      const typedExecutions = executionsData as unknown as AutomationExecutionV2[];
      setExecutions(typedExecutions);
      
      // Calculate summary using smart status logic
      const runningCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'running').length;
      const successfulCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'completed').length;
      const failedCount = typedExecutions.filter(exec => getActualExecutionStatus(exec) === 'failed').length;
      
      setExecutionSummary({
        running_executions: runningCount,
        successful_executions: successfulCount,
        failed_executions: failedCount,
        last_execution: typedExecutions[0] || undefined
      });
    } catch (err) {
      console.error('Error loading execution history:', err);
      setError('Failed to load execution history');
    }
  };

  const saveTriggers = async () => {
    const persistContext = getActivePersistContext();
    if (!persistContext) return;

    const { automationId } = persistContext;
    const triggersSnapshot = triggers;

    try {
      if (!isPersistContextValid(persistContext)) return;
      await saveAutomationTriggers(automationId, triggersSnapshot);
    } catch (err) {
      console.error('Error saving triggers:', err);
      setError('Failed to save triggers');
    }
  };

  const saveActions = async () => {
    const persistContext = getActivePersistContext();
    if (!persistContext) return;

    const { automationId } = persistContext;
    const actionsSnapshot = actions;

    try {
      if (!isPersistContextValid(persistContext)) return;
      await saveAutomationActions(automationId, actionsSnapshot);
    } catch (err) {
      console.error('Error saving actions:', err);
      setError('Failed to save actions');
    }
  };

  const saveConnections = async () => {
    const persistContext = getActivePersistContext();
    if (!persistContext) return;

    const { automationId } = persistContext;
    const triggersSnapshot = triggers;
    const actionsSnapshot = actions;
    const wireConnectionsSnapshot = wireConnections;
    
    try {
      if (!isPersistContextValid(persistContext)) return;

      // First, ensure all nodes are saved to get their database IDs
      const [triggerIdMap, actionIdMap] = await Promise.all([
        saveAutomationTriggers(automationId, triggersSnapshot),
        saveAutomationActions(automationId, actionsSnapshot)
      ]);

      if (!isPersistContextValid(persistContext)) return;
      
      // Merge both maps for ID lookup
      const allIdMap = new Map<string, string>([...triggerIdMap, ...actionIdMap]);
      
      // Update triggers/actions state if IDs changed (for newly created nodes)
      const triggersUpdated = triggersSnapshot.map(t => {
        const dbId = allIdMap.get(t.id);
        return dbId && dbId !== t.id ? { ...t, id: dbId } : t;
      });
      const actionsUpdated = actionsSnapshot.map(a => {
        const dbId = allIdMap.get(a.id);
        return dbId && dbId !== a.id ? { ...a, id: dbId } : a;
      });
      
      const triggersChanged = triggersUpdated.some((t, i) => t.id !== triggersSnapshot[i]?.id);
      const actionsChanged = actionsUpdated.some((a, i) => a.id !== actionsSnapshot[i]?.id);
      
      if (!isPersistContextValid(persistContext)) return;

      if (triggersChanged) {
        setTriggers(triggersUpdated);
      }
      if (actionsChanged) {
        setActions(actionsUpdated);
      }
      
      // Map wire connections to use database UUIDs and filter out invalid ones
      const validConnections = wireConnectionsSnapshot
        .map(conn => {
          // Map fromNodeId - check in updated triggers/actions first, then ID map
          let fromDbId: string | null = null;
          const fromTrigger = triggersUpdated.find(t => t.id === conn.fromNodeId);
          const fromAction = actionsUpdated.find(a => a.id === conn.fromNodeId);
          if (fromTrigger) fromDbId = fromTrigger.id;
          else if (fromAction) fromDbId = fromAction.id;
          else fromDbId = allIdMap.get(conn.fromNodeId) || 
            (conn.fromNodeId.length === 36 && conn.fromNodeId.includes('-') ? conn.fromNodeId : null);
          
          // Map toNodeId - check in updated triggers/actions first, then ID map
          let toDbId: string | null = null;
          const toTrigger = triggersUpdated.find(t => t.id === conn.toNodeId);
          const toAction = actionsUpdated.find(a => a.id === conn.toNodeId);
          if (toTrigger) toDbId = toTrigger.id;
          else if (toAction) toDbId = toAction.id;
          else toDbId = allIdMap.get(conn.toNodeId) || 
            (conn.toNodeId.length === 36 && conn.toNodeId.includes('-') ? conn.toNodeId : null);
          
          // If we couldn't map either ID, this connection is invalid
          if (!fromDbId || !toDbId) {
            console.warn('⚠️ Skipping invalid connection (node IDs not found):', {
              from: conn.fromNodeId,
              to: conn.toNodeId,
              fromDbId,
              toDbId
            });
            return null;
          }
          
          return {
            ...conn,
            id: conn.id, // Keep existing connection ID if it exists
            fromNodeId: fromDbId,
            toNodeId: toDbId
          };
        })
        .filter((conn): conn is WireConnection => conn !== null);
      
      // Only save if we have valid connections
      if (validConnections.length > 0) {
        if (!isPersistContextValid(persistContext)) return;
        await saveWireConnections(automationId, validConnections);
        console.log('💾 Saved wire connections to database:', validConnections.length);
        
        // Update local state with mapped connections (if IDs changed)
        const connectionsChanged = validConnections.length !== wireConnectionsSnapshot.length ||
          validConnections.some((conn, i) => 
            conn.fromNodeId !== wireConnectionsSnapshot[i]?.fromNodeId || 
            conn.toNodeId !== wireConnectionsSnapshot[i]?.toNodeId
          );
        
        if (!isPersistContextValid(persistContext)) return;

        if (connectionsChanged) {
          setWireConnections(validConnections);
        }
      } else if (wireConnectionsSnapshot.length > 0) {
        console.warn('⚠️ No valid connections to save (all connections had invalid node IDs)');
        // Clear invalid connections from state
        if (isPersistContextValid(persistContext)) {
          setWireConnections([]);
        }
      }
    } catch (err) {
      console.error('Error saving wire connections:', err);
      setError('Failed to save wire connections');
    }
  };

  const loadExecutionSteps = async (executionId: string) => {
    try {
      setLoadingSteps(true);
      const steps = await getExecutionSteps(executionId);
      // Cast to the correct type since the data structure matches
      const typedSteps = steps as unknown as AutomationActionExecutionV2[];
      setExecutionSteps(typedSteps);
    } catch (err) {
      console.error('Error loading execution steps:', err);
      setError('Failed to load execution details');
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleViewExecution = async (executionId: number) => {
    setSelectedExecutionId(executionId.toString());
    setIsExecutionDetailsOpen(true);
    await loadExecutionSteps(executionId.toString());
  };

  const handleCloseExecutionDetails = () => {
    setIsExecutionDetailsOpen(false);
    setSelectedExecutionId(null);
    setExecutionSteps([]);
  };

  // Helper function to format duration
  const formatDuration = (durationMs: number): string => {
    if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string): string => {
    const now = new Date().getTime();
    const date = new Date(dateString).getTime();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Helper function to calculate actual execution status based on action progress
  const getActualExecutionStatus = (execution: any): string => {
    // If there's an explicit error, it's failed
    if (execution.error_message || execution.execution_status === 'failed') {
      return 'failed';
    }
    
    // If no actions to execute, consider it completed
    if (execution.total_actions === 0) {
      return 'completed';
    }
    
    // If all actions are executed, it's completed
    if (execution.executed_actions >= execution.total_actions) {
      return 'completed';
    }
    
    // If some actions are executed but not all, it's running
    if (execution.executed_actions > 0) {
      return 'running';
    }
    
    // If no actions executed yet, check the database status
    if (execution.execution_status === 'pending') {
      return 'pending';
    }
    
    // Default to running if execution has started
    return 'running';
  };

  // Helper function to get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  // ── Agents V2 (Create Agent → receptionist builder) ──
  const [isAgentPickerOpen, setIsAgentPickerOpen] = useState(false);
  // Single "+" entry point → Automation / AI create / Create agent
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [agentsList, setAgentsList] = useState<AgentV2[]>([]);
  const [listFilter, setListFilter] = useState<'all' | 'agents' | 'automations'>('all');
  const visibleAgentsList = listFilter === 'automations' ? [] : agentsList;
  const visibleAutomationsList = listFilter === 'agents' ? [] : automations;

  useEffect(() => {
    if (!selectedBusiness?.id) {
      setAgentsList([]);
      return;
    }
    listAgents(selectedBusiness.id)
      .then(setAgentsList)
      .catch((err) => console.error('Failed to load agents:', err));
  }, [selectedBusiness?.id]);

  // ── AI Automation Builder ──
  const [isAiBuilderOpen, setIsAiBuilderOpen] = useState(false);
  const [aiBuilderEdit, setAiBuilderEdit] = useState<{ automation: AutomationV2; flow: AiWorkflowFlow } | null>(null);

  const handleAiCreateClick = () => {
    setAiBuilderEdit(null);
    setIsAiBuilderOpen(true);
  };

  const handleAiEditClick = () => {
    if (!selectedAutomation) return;
    if (wireConnections.some((w) => (w as { crm_branch?: string | null }).crm_branch)) {
      toast.error('This workflow uses advanced CRM branching — AI editing supports linear workflows for now.');
      return;
    }
    const flow = nodesToFlow(selectedAutomation, triggers, actions, wireConnections);
    if (!flow) {
      toast.error('Add a trigger first, then the AI can edit this workflow.');
      return;
    }
    setAiBuilderEdit({ automation: selectedAutomation, flow });
    setIsAiBuilderOpen(true);
  };

  const handleAiWorkflowSaved = async (automation: AutomationV2) => {
    await loadAutomations();
    // Open (or re-hydrate) the saved automation in the builder.
    lastLoadedAutomationIdRef.current = null;
    setIsWorkflowLoading(true);
    resetWorkflowCanvas();
    setSelectedAutomation(automation);
    setActiveTab('workflow-new');
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setAutomationTitle('');
    setError(null);
  };

  const handleSaveAutomation = async () => {
    if (!automationTitle.trim() || !selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const newAutomation = await createAutomation({
        name: automationTitle.trim(),
        business_id: selectedBusiness.id
      });
      
      setAutomations(prev => [newAutomation, ...prev]);
      console.log('Created automation:', newAutomation);
      handleCloseModal();
      // Switch to the new automation so edits never bleed into the previous one
      lastLoadedAutomationIdRef.current = null;
      setIsWorkflowLoading(true);
      resetWorkflowCanvas();
      setSelectedAutomation(newAutomation);
      setActiveTab('workflow-new');
    } catch (err) {
      console.error('Error creating automation:', err);
      setError('Failed to create automation');
    } finally {
      setLoading(false);
    }
  };

  const handleAutomationClick = (automation: AutomationV2) => {
    if (selectedAutomation?.id === automation.id) return;

    // Clear stale workflow immediately so auto-save cannot write to the wrong automation
    lastLoadedAutomationIdRef.current = null;
    setIsWorkflowLoading(true);
    resetWorkflowCanvas();
    setSelectedAutomation(automation);
    setActiveTab('workflow-new');
  };

  const handleClearSelectedAutomation = () => {
    lastLoadedAutomationIdRef.current = null;
    setIsWorkflowLoading(false);
    resetWorkflowCanvas();
    setSelectedAutomation(null);
  };

  const handleToggleLive = async () => {
    if (!selectedAutomation) return;

    try {
      setError(null);
      const updatedAutomation = await toggleAutomationLive(
        selectedAutomation.id, 
        !selectedAutomation.is_live
      );

      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );
      setSelectedAutomation(updatedAutomation);
    } catch (err) {
      console.error('Error toggling automation live status:', err);
      setError('Failed to update automation status');
    }
  };

  const handleAddTrigger = () => {
    setIsTriggerLibraryOpen(true);
  };

  const handleSelectTrigger = (triggerType: any) => {
    // Generate webhook ID and secret for webhook triggers
    const isWebhookTrigger = triggerType.id === 'webhook';
    const isMissedCallTrigger = triggerType.id === 'missed_call';
    const isEnterAutomationTrigger = triggerType.id === 'enter_automation';
    const isEnterCrmStageTrigger = triggerType.id === 'enter_crm_stage';
    const webhookId = isWebhookTrigger ? `wh_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}` : undefined;
    const webhookSecret = isWebhookTrigger 
      ? `sk_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`
      : undefined;

    // Determine config based on trigger type
    let triggerConfig: any;
    if (isWebhookTrigger) {
      triggerConfig = {
        webhook_id: webhookId,
        webhook_secret: webhookSecret,
        webhook_delivery: 'http',
        discovered_fields: null,
        field_mappings: {}
      };
    } else if (isMissedCallTrigger) {
      triggerConfig = {
        eventType: 'missed'
      };
    } else if (isEnterAutomationTrigger) {
      triggerConfig = { receiver: true };
    } else if (isEnterCrmStageTrigger) {
      triggerConfig = {
        pipeline_id: null,
        stage_id: null,
        pipeline_name: '',
        stage_name: '',
        uiLabel: '',
      };
    } else {
      triggerConfig = { eventType: 'created' };
    }

    const newTrigger: TriggerNodeType = {
      id: `temp-${Date.now()}`,
      type: triggerType.id,
      name: triggerType.name,
      position: { x: 100, y: 100 },
      config: triggerConfig
    };

    setTriggers(prev => [...prev, newTrigger]);
    setIsTriggerLibraryOpen(false);
    console.log('Added trigger:', newTrigger);
  };


  const handleMouseDown = (e: React.MouseEvent, triggerId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const trigger = triggers.find(t => t.id === triggerId);
    
    if (!trigger) return;

    const offsetX = e.clientX - rect.left - trigger.position.x;
    const offsetY = e.clientY - rect.top - trigger.position.y;

    setIsDragging(true);
    setDraggedId(triggerId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleStartWireDrag = (nodeId: string, position: { x: number; y: number }) => {
    console.log(`🔗 Starting wire drag from node: ${nodeId} at position:`, position);
    setWireState({
      isDragging: true,
      startNodeId: nodeId,
      startPosition: position,
      currentPosition: position
    });
  };

  // Handle successful wire connection
  const handleWireDropped = async (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => {
    if (!wireState.isDragging || !wireState.startNodeId) return;
    
    const fromNodeId = wireState.startNodeId;
    
    // Prevent connecting to the same node
    if (fromNodeId === targetNodeId) {
      console.log('🔗 Cannot connect node to itself');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }
    
    // Check if connection already exists
    const existingConnection = wireConnections.find(
      conn => conn.fromNodeId === fromNodeId && conn.toNodeId === targetNodeId
    );
    
    if (existingConnection) {
      console.log('🔗 Connection already exists');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }

    if (isCrmAdvancedSplitSource(fromNodeId)) {
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      setCrmBranchConnectModal({
        fromNodeId,
        toNodeId: targetNodeId,
        branch: 'left',
        mode: 'canvas'
      });
      return;
    }
    
    // Clear drag wire immediately and show loading on target port
    setWireState({
      isDragging: false,
      startNodeId: null,
      startPosition: { x: 0, y: 0 },
      currentPosition: { x: 0, y: 0 }
    });
    setConnectingBetween({ fromNodeId, toNodeId: targetNodeId });
    
    try {
      // Set flag to prevent debounced saves
      setIsSavingImmediately(true);
      
      // Ensure all nodes are saved to database before creating connection
      console.log('🔗 Ensuring nodes are saved before creating connection...');
      const persistContext = getActivePersistContext();
      if (persistContext) {
        const { automationId } = persistContext;
        const triggersSnapshot = triggers;
        const actionsSnapshot = actions;
        // Save nodes and get ID mapping (temp ID -> database UUID)
        const [triggerIdMap, actionIdMap] = await Promise.all([
          saveAutomationTriggers(automationId, triggersSnapshot),
          saveAutomationActions(automationId, actionsSnapshot)
        ]);

        if (!isPersistContextValid(persistContext)) return;
        
        // Merge both maps for easy lookup
        const allIdMap = new Map<string, string>([...triggerIdMap, ...actionIdMap]);
        
        // Update local state with database UUIDs for newly created nodes
        const updatedTriggers = triggersSnapshot.map(t => {
          const dbId = allIdMap.get(t.id);
          return dbId && dbId !== t.id ? { ...t, id: dbId } : t;
        });
        const updatedActions = actionsSnapshot.map(a => {
          const dbId = allIdMap.get(a.id);
          return dbId && dbId !== a.id ? { ...a, id: dbId } : a;
        });
        
        // Only update state if IDs changed
        if (updatedTriggers.some((t, i) => t.id !== triggersSnapshot[i]?.id) || 
            updatedActions.some((a, i) => a.id !== actionsSnapshot[i]?.id)) {
          if (!isPersistContextValid(persistContext)) return;
          setTriggers(updatedTriggers);
          setActions(updatedActions);
        }
        
        console.log('🔗 Nodes saved with ID mapping:', { triggerIdMap, actionIdMap });
        
        // Map temporary IDs to database UUIDs using the ID maps
        const findActualNodeId = (tempId: string): string => {
          // Check if it's already a UUID (36 chars with dashes)
          if (tempId.length === 36 && tempId.includes('-')) {
            // Verify it exists in the map or is already a valid database ID
            return allIdMap.get(tempId) || tempId;
          }
          
          // Look up in the ID map (for newly created nodes)
          const dbId = allIdMap.get(tempId);
          if (dbId) {
            return dbId;
          }
          
          // Fallback: check if it's already a valid database ID by checking in current nodes
          const inTriggers = triggersSnapshot.find(t => t.id === tempId);
          const inActions = actionsSnapshot.find(a => a.id === tempId);
          if (inTriggers || inActions) {
            return tempId; // It's already a database ID
          }
          
          // Last resort: return as-is (will be validated by createAutomationConnection)
          console.warn('⚠️ Could not map node ID, using as-is:', tempId);
          return tempId;
        };
        
        // Update the connection IDs to use database UUIDs
        const actualFromNodeId = findActualNodeId(fromNodeId);
        const actualToNodeId = findActualNodeId(targetNodeId);
        
        console.log('🔗 Mapped IDs - From:', fromNodeId, '→', actualFromNodeId, 'To:', targetNodeId, '→', actualToNodeId);

        if (!isPersistContextValid(persistContext)) return;
        
        // Save connection to database with actual UUIDs (validation happens inside createAutomationConnection)
        const createdConnection = await createAutomationConnection({
          automation_id: automationId,
          source_node_id: actualFromNodeId,
          target_node_id: actualToNodeId
        });
        console.log('🔗 Connection saved to database successfully:', createdConnection);

        if (!isPersistContextValid(persistContext)) return;
        
        // Update connection with actual database ID (not temporary ID)
        const newConnection: WireConnection = {
          id: createdConnection.id, // Use the database UUID, not a temporary ID
          fromNodeId: actualFromNodeId,
          toNodeId: actualToNodeId,
          fromPort: 'outflow',
          toPort: 'inflow',
          crm_branch: createdConnection.crm_branch ?? undefined
        };
        
        setWireConnections(prev => [...prev, newConnection]);
        console.log('🔗 Created wire connection with database UUID:', newConnection);
      }
      
      // Clear the flag after a short delay to allow debounced saves to resume
      setTimeout(() => {
        setIsSavingImmediately(false);
      }, 1000);
      
      

    } catch (err: any) {
      console.error('Error creating connection:', err);
      // Provide more specific error message
      const errorMessage = err?.message || 'Failed to save wire connection';
      setError(errorMessage);
      // Clear flag even on error
      setIsSavingImmediately(false);
    } finally {
      setConnectingBetween(null);
    }
  };

  const handleTriggerSettings = (triggerId: string) => {
    const trigger = triggers.find((t) => t.id === triggerId);
    setSelectedTriggerId(triggerId);
    setIsTriggerSettingsOpen(true);
    if (trigger?.type === 'enter_crm_stage') {
      fetchPipelines();
    }
  };

  const handleCloseTriggerSettings = () => {
    setIsTriggerSettingsOpen(false);
    setSelectedTriggerId(null);
  };

  const handleDeleteTrigger = (triggerId: string) => {
    // Remove the trigger from the triggers array
    setTriggers(prev => prev.filter(trigger => trigger.id !== triggerId));
    
    // Remove any wire connections involving this trigger
    setWireConnections(prev => prev.filter(connection => 
      connection.fromNodeId !== triggerId && connection.toNodeId !== triggerId
    ));
    
    // Close the settings modal
    handleCloseTriggerSettings();
    
    console.log('Trigger and related connections deleted:', triggerId);
  };

  // Action handlers
  const handleAddAction = () => {
    setIsActionLibraryOpen(true);
  };

  // Helper function to find the last node in the workflow chain
  const findLastNodeInWorkflow = (): string | null => {
    // First, try to find a node that has no outgoing connections (end of chain)
    const allNodes = [...triggers.map(t => t.id), ...actions.map(a => a.id)];
    const nodesWithOutgoingConnections = new Set(wireConnections.map(conn => conn.fromNodeId));
    
    // Find nodes that don't have outgoing connections
    const endNodes = allNodes.filter(nodeId => !nodesWithOutgoingConnections.has(nodeId));
    
    if (endNodes.length > 0) {
      // If there are multiple end nodes, prefer actions over triggers
      const endActions = endNodes.filter(nodeId => actions.some(a => a.id === nodeId));
      if (endActions.length > 0) {
        return endActions[0]; // Return the first end action
      }
      return endNodes[0]; // Return the first end node (could be a trigger)
    }
    
    // If no end nodes found (circular connections), try to find the most recently added node
    if (actions.length > 0) {
      return actions[actions.length - 1].id;
    }
    
    if (triggers.length > 0) {
      return triggers[triggers.length - 1].id;
    }
    
    return null;
  };

  const handleSelectAction = async (actionType: any) => {
    const priorActionIds = new Set(actions.map((a) => a.id));
    const pendingBranchConnect = pendingConnectAfterActionAdd;
    if (pendingBranchConnect) {
      setPendingConnectAfterActionAdd(null);
    }

    // Create a unique temporary ID that includes timestamp and random component
    const uniqueId = `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newAction: ActionNodeType = {
      id: uniqueId,
      type: actionType.id,
      name: actionType.name,
      position: { x: 400, y: 100 }, // Position to the right of triggers
      config:
        actionType.id === 'send_live_intake_form'
          ? {
              send_mode: 'email' as const,
              intake_template_id: '',
              intake_sms_body: '',
              intake_email_subject: '',
              intake_email_body: ''
            }
          : {}
    };

    if (pendingBranchConnect) {
      flushSync(() => {
        setPendingCrmBranchPlacement({
          parentId: pendingBranchConnect.parentId,
          branch: pendingBranchConnect.branch,
          actionId: uniqueId
        });
        setActions((prev) => [...prev, newAction]);
      });
    } else {
      setActions((prev) => [...prev, newAction]);
    }
    setIsActionLibraryOpen(false);
    
    // Save the new action to database without auto-connecting
    const persistContext = getActivePersistContext();
    if (persistContext) {
      try {
        console.log('💾 Saving new action to database:', uniqueId);
        
        const { automationId } = persistContext;
        const triggersSnapshot = triggers;
        // Save all nodes to database to get real UUIDs
        const currentActions = [...actions, newAction];
        await Promise.all([
          saveAutomationTriggers(automationId, triggersSnapshot),
          saveAutomationActions(automationId, currentActions)
        ]);

        if (!isPersistContextValid(persistContext)) return;
        
        // Reload nodes to get database UUIDs and update local state
        const [updatedTriggers, updatedActions] = await Promise.all([
          getAutomationTriggers(automationId),
          getAutomationActions(automationId)
        ]);

        if (!isPersistContextValid(persistContext)) return;

        const addedForBranch =
          pendingBranchConnect
            ? updatedActions.find((a) => !priorActionIds.has(a.id))
            : null;
        flushSync(() => {
          if (pendingBranchConnect && addedForBranch) {
            setPendingCrmBranchPlacement({
              parentId: pendingBranchConnect.parentId,
              branch: pendingBranchConnect.branch,
              actionId: addedForBranch.id,
              prevTempActionId: uniqueId
            });
          } else if (pendingBranchConnect) {
            setPendingCrmBranchPlacement(null);
          }
          setTriggers(updatedTriggers);
          setActions(updatedActions);
        });

        if (pendingBranchConnect && isPersistContextValid(persistContext) && addedForBranch) {
          try {
            const createdWire = await handleLinearConnection(
              pendingBranchConnect.parentId,
              addedForBranch.id,
              pendingBranchConnect.branch,
              updatedTriggers,
              updatedActions
            );
            if (!isPersistContextValid(persistContext)) return;
            const wires = await getAutomationConnections(automationId);
            if (!isPersistContextValid(persistContext)) return;
            const fromId = pendingBranchConnect.parentId;
            const toId = addedForBranch.id;
            const br = pendingBranchConnect.branch;
            const branchEdgeIn = (list: WireConnection[]) =>
              list.some(
                (w) =>
                  w.fromNodeId === fromId &&
                  w.toNodeId === toId &&
                  (w.crm_branch || 'left') === br
              );
            flushSync(() => {
              setWireConnections((prev) => {
                if (branchEdgeIn(wires)) return wires;
                const sameEdge = (w: WireConnection) =>
                  w.fromNodeId === fromId &&
                  w.toNodeId === toId &&
                  (w.crm_branch || 'left') === br;
                const fallback =
                  prev.find(sameEdge) ||
                  (createdWire &&
                  createdWire.fromNodeId === fromId &&
                  createdWire.toNodeId === toId &&
                  (createdWire.crm_branch || 'left') === br
                    ? createdWire
                    : undefined);
                if (!fallback) return wires;
                return [...wires.filter((w) => !sameEdge(w)), fallback];
              });
              setPendingCrmBranchPlacement(null);
            });
          } catch (connErr) {
            console.error('Failed to auto-connect new action:', connErr);
            flushSync(() => setPendingCrmBranchPlacement(null));
          }
        }

        console.log('✅ Action saved successfully - ready for manual connection');
      } catch (err) {
        console.error('❌ Error saving action:', err);
        setError('Failed to save action');
        setPendingCrmBranchPlacement(null);
      }
    } else if (pendingBranchConnect) {
      setPendingCrmBranchPlacement(null);
    }
    
    // Clear pending parent state
    setPendingActionParent(null);
    
    console.log('✅ Added action (manual connection required):', newAction);
  };

  const handleActionMouseDown = (e: React.MouseEvent, actionId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const action = actions.find(a => a.id === actionId);
    
    if (!action) return;

    const offsetX = e.clientX - rect.left - action.position.x;
    const offsetY = e.clientY - rect.top - action.position.y;

    setIsDragging(true);
    setDraggedId(actionId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const hasUpstreamCreateCrmLead = (targetActionId: string): boolean => {
    const visited = new Set<string>();
    const queue: string[] = [];
    for (const w of wireConnections) {
      if (w.toNodeId === targetActionId) queue.push(w.fromNodeId);
    }
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      const act = actions.find((a) => a.id === id);
      if (act?.type === 'create_crm_lead') return true;
      for (const w of wireConnections) {
        if (w.toNodeId === id) queue.push(w.fromNodeId);
      }
    }
    return false;
  };

  // Handle opening action settings
  const handleActionSettingsClick = async (actionId: string) => {
    const currentAction = actions.find(action => action.id === actionId);
    
    if (currentAction?.type === 'sms') {
      // SMS Action Settings
      const existingMessage = currentAction?.config?.message || '';
      const existingRecipient = currentAction?.config?.recipient || '';
      const existingCustomPhone = currentAction?.config?.customPhone || '';
      
      setSmsActionSettingsModal({
        isOpen: true,
        actionId,
        message: existingMessage,
        recipient: existingRecipient,
        customPhone: existingCustomPhone
      });
    } else if (currentAction?.type === 'email') {
      // Email Action Settings
      const existingSubject = currentAction?.config?.subject || '';
      const existingMessage = currentAction?.config?.message || '';
      const existingRecipient = currentAction?.config?.recipient || '';
      const existingCustomEmail = currentAction?.config?.customEmail || '';
      const existingUseTemplate = currentAction?.config?.useTemplate || false;
      const existingSelectedTemplateId = currentAction?.config?.selectedTemplateId || null;
      const existingPdfAttachment = currentAction?.config?.pdfAttachment || null;
      
      setEmailActionSettingsModal({
        isOpen: true,
        actionId,
        subject: existingSubject,
        message: existingMessage,
        recipient: existingRecipient,
        customEmail: existingCustomEmail,
        useTemplate: existingUseTemplate,
        selectedTemplateId: existingSelectedTemplateId,
        pdfAttachment: existingPdfAttachment,
        pdfUploading: false
      });
    } else if (currentAction?.type === 'delay') {
      // Delay Action Settings
      const existingDuration = currentAction?.config?.displayDuration || currentAction?.config?.duration || 1;
      const existingUnit = currentAction?.config?.unit || 'hours';
      const existingWaitType = currentAction?.config?.wait_type || 'duration';
      const existingReminderHours = currentAction?.config?.reminder_hours_before || 24;
      const existingReminderReference = currentAction?.config?.reminder_reference || 'drop_off_datetime';
      
      setDelayActionSettingsModal({
        isOpen: true,
        actionId,
        waitType: existingWaitType,
        duration: existingDuration,
        unit: existingUnit,
        reminderHoursBefore: existingReminderHours,
        reminderReference: existingReminderReference
      });
    } else if (currentAction?.type === 'create_crm_lead') {
      // Create CRM Lead Action Settings
      setCrmLeadActionSettingsModal({
        isOpen: true,
        actionId
      });
      
      // Load necessary data
      fetchPipelines();
      fetchLeadForms(); // Also fetch lead forms for variables
      loadCustomFieldDefinitions(); // Load custom field definitions
      
      // Set form fields after a brief delay to allow forms to load
      setTimeout(() => {
        const formFields = getSelectedFormFields(actionId);
        setSelectedFormFields(formFields);
        console.log('📋 Set form fields for action:', actionId, formFields);
      }, 100);
      
      // Always reset field mappings, then load existing configuration if any
      const config = currentAction?.config || {};
      
      // Convert values to strings properly
      const titleValue = typeof config?.title === 'object' ? (config.title?.value || '') : (config?.title || '');
      const valueValue = typeof config?.value === 'object' ? (config.value?.value || '') : (config?.value || '');
      const leadInfoValue = typeof config?.lead_info === 'object' ? (config.lead_info?.value || '') : (config?.lead_info || '');
      const sourceValue = typeof config?.source === 'object' ? (config.source?.value || '') : (config?.source || '');
      const statusValue = typeof config?.status === 'object' ? (config.status?.value || 'Open') : (config?.status || 'Open');
      
      // Handle custom fields - convert from old object format or use existing object format
      let customFieldsValue = {};
      if (config?.custom_fields) {
        if (typeof config.custom_fields === 'string') {
          try {
            customFieldsValue = JSON.parse(config.custom_fields);
          } catch (e) {
            console.warn('Failed to parse custom_fields JSON:', e);
          }
        } else if (typeof config.custom_fields === 'object') {
          customFieldsValue = config.custom_fields;
        }
      }
      
      setFieldMappings(prev => ({
        ...prev,
        selectedPipeline: config?.selectedPipeline || null,
        selectedPipelineName: config?.selectedPipelineName || '',
        selectedStage: config?.selectedStage || null,
        selectedStageName: config?.selectedStageName || '',
        status: statusValue,
        title: titleValue,
        value: valueValue,
        lead_info: leadInfoValue,
        source: sourceValue,
        custom_fields: customFieldsValue,
        useCrmAdvancedSplit: !!config?.useCrmAdvancedSplit,
        useCrmAiRouting: config?.useCrmAiRouting !== false,
        crmAiRoutingPrompt: config?.crmAiRoutingPrompt || '',
        crmSplitLeft: {
          selectedPipeline: config?.crmSplitLeft?.selectedPipeline ?? null,
          selectedPipelineName: config?.crmSplitLeft?.selectedPipelineName || '',
          selectedStage: config?.crmSplitLeft?.selectedStage ?? null,
          selectedStageName: config?.crmSplitLeft?.selectedStageName || '',
        },
        crmSplitRight: {
          selectedPipeline: config?.crmSplitRight?.selectedPipeline ?? null,
          selectedPipelineName: config?.crmSplitRight?.selectedPipelineName || '',
          selectedStage: config?.crmSplitRight?.selectedStage ?? null,
          selectedStageName: config?.crmSplitRight?.selectedStageName || '',
        },
      }));
    } else if (currentAction?.type === 'check') {
      // Check Action Settings
      const existingCheckType = currentAction?.config?.checkType || 'feedback';
      
      setCheckActionSettingsModal({
        isOpen: true,
        actionId,
        checkType: existingCheckType,
        ifCompleted: currentAction?.config?.ifCompleted || 'stop',
        ifNotCompleted: currentAction?.config?.ifNotCompleted || 'continue'
      });
    } else if (currentAction?.type === 'assign_tags') {
      // Assign Tags Action Settings
      const existingConditions = currentAction?.config?.conditions || [];
      const existingSelectedTags = currentAction?.config?.selectedTags || [];
      
      setAssignTagsActionSettingsModal({
        isOpen: true,
        actionId,
        conditions: existingConditions,
        selectedTags: existingSelectedTags
      });
    } else if (currentAction?.type === 'create_task') {
      // Create Task Action Settings
      const existingBoardId = currentAction?.config?.selectedBoardId || '';
      const existingColumnId = currentAction?.config?.selectedColumnId || '';
      const existingTaskTitle = currentAction?.config?.taskTitle || '';
      const existingTaskDescription = currentAction?.config?.taskDescription || '';
      const existingTaskUrgency = currentAction?.config?.taskUrgency || 'medium';
      
      setCreateTaskActionSettingsModal({
        isOpen: true,
        actionId,
        selectedBoardId: existingBoardId,
        selectedColumnId: existingColumnId,
        taskTitle: existingTaskTitle,
        taskDescription: existingTaskDescription,
        taskUrgency: existingTaskUrgency,
        taskStatus: currentAction?.config?.taskStatus || 'pending',
        scheduleOffset: currentAction?.config?.scheduleOffset || 1,
        scheduleUnit: currentAction?.config?.scheduleUnit || 'days',
        boards: [],
        columns: []
      });
      
      // Load boards and columns with a delay to ensure state is updated
      setTimeout(async () => {
        await loadBoardsAndColumns();
        
        // If there's a saved board ID, load its columns
        if (existingBoardId) {
          try {
            const boardWithColumns = await getBoardWithColumns(existingBoardId);
            setCreateTaskActionSettingsModal(prev => ({
              ...prev,
              columns: boardWithColumns.columns.map(column => ({
                id: column.id,
                title: column.title
              }))
            }));
          } catch (error) {
            console.error('Error loading columns for existing board:', error);
          }
        }
      }, 100);
    } else if (currentAction?.type === 'add_to_automation') {
      const bizId = selectedBusiness?.id ?? (await getCurrentBusinessId());
      if (!bizId) {
        setError('Select a business before configuring this action.');
        return;
      }
      let receiverOptions: Array<{ id: string; name: string }> = [];
      let loadError: string | null = null;
      try {
        receiverOptions = await getReceiverAutomationOptions(bizId, {
          excludeAutomationId: selectedAutomation?.id
        });
      } catch (e: unknown) {
        loadError = e instanceof Error ? e.message : 'Failed to load receiver automations';
      }
      setAddToAutomationActionSettingsModal({
        isOpen: true,
        actionId,
        targetAutomationId: currentAction?.config?.targetAutomationId || '',
        receiverOptions,
        loadError
      });
    } else if (currentAction?.type === 'send_live_intake_form') {
      const bizId = selectedBusiness?.id;
      if (!bizId) {
        setError('Select a business before configuring this action.');
        return;
      }
      const sm = currentAction?.config?.send_mode;
      const sendMode: 'sms' | 'email' | 'both' =
        sm === 'sms' || sm === 'email' || sm === 'both' ? sm : 'email';
      setSendLiveIntakeFormSettingsModal({
        isOpen: true,
        actionId,
        sendMode,
        intakeTemplateId: currentAction?.config?.intake_template_id || '',
        templates: [],
        loadingTemplates: true,
        intakeSmsBody: typeof currentAction?.config?.intake_sms_body === 'string' ? currentAction.config.intake_sms_body : '',
        intakeEmailSubject:
          typeof currentAction?.config?.intake_email_subject === 'string'
            ? currentAction.config.intake_email_subject
            : '',
        intakeEmailBody:
          typeof currentAction?.config?.intake_email_body === 'string' ? currentAction.config.intake_email_body : ''
      });
      try {
        const list = await listIntakeFormTemplates(bizId);
        setSendLiveIntakeFormSettingsModal((prev) => ({
          ...prev,
          templates: list.map((t) => ({ id: t.id, title: t.title })),
          loadingTemplates: false
        }));
      } catch {
        setSendLiveIntakeFormSettingsModal((prev) => ({
          ...prev,
          templates: [],
          loadingTemplates: false
        }));
        setError('Failed to load intake form templates');
      }
    } else if (currentAction?.type === 'feedback_agent') {
      const bizId = selectedBusiness?.id;
      if (!bizId) {
        setError('Select a business before configuring this action.');
        return;
      }
      setFeedbackAgentActionSettingsModal({
        isOpen: true,
        actionId,
        agentId: currentAction?.config?.agentId || '',
        agents: [],
        loading: true,
        feedbackEnabled: null
      });
      try {
        const [allAgents, bizResult] = await Promise.all([
          listAgents(bizId),
          supabase.from('businesses').select('feedback_enabled').eq('id', bizId).single()
        ]);
        const feedbackAgents = allAgents
          .filter((a) => a.kind === 'feedback')
          .map((a) => ({ id: a.id, name: a.name, status: a.status }));
        setFeedbackAgentActionSettingsModal((prev) => ({
          ...prev,
          agents: feedbackAgents,
          loading: false,
          // Default (null) counts as ON — the toggle is opt-out.
          feedbackEnabled: bizResult.data?.feedback_enabled !== false
        }));
      } catch (e) {
        console.error('Failed to load feedback agents:', e);
        setFeedbackAgentActionSettingsModal((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  // Helper function to check if wait action is connected to a "created" trigger
  const isConnectedToCreatedTrigger = (actionId: string): boolean => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    
    // Check if any connected trigger is a booking trigger with any booking event type
    return connectedTriggers.some(trigger => 
      trigger?.type === 'booking'
    );
  };

  // Handle deleting action from settings modal
  const handleDeleteActionFromSettings = async () => {
    if (!smsActionSettingsModal.actionId) return;

    try {
      const actionId = smsActionSettingsModal.actionId;
      
      // Remove action from local state
      setActions(prev => prev.filter(action => action.id !== actionId));
      
      // Remove any wire connections involving this action
      setWireConnections(prev => prev.filter(connection => 
        connection.fromNodeId !== actionId && connection.toNodeId !== actionId
      ));
      
      // Close modal
      setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
      
      console.log('Action and related connections deleted:', actionId);
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  // Get available notification recipients based on connected triggers/actions (with chain traversal)
  const getAvailableRecipients = (actionId: string): Array<{value: string, label: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const recipients: Array<{value: string, label: string}> = [];
    
    // Add recipients based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking' || trigger?.type === 'feedback') {
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone' });
        }
        if (!recipients.some(r => r.value === 'resource_phone')) {
          recipients.push({ value: 'resource_phone', label: 'Resource Phone' });
        }
        // Note: Business Phone removed for booking triggers - not typically needed for booking notifications
      } else if (trigger?.type === 'missed_call') {
        // For missed call triggers, the caller phone is the primary recipient
        if (!recipients.some(r => r.value === 'caller_phone')) {
          recipients.push({ value: 'caller_phone', label: 'Caller Number (missed call)' });
        }
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // For form submission triggers, customer phone is the primary recipient
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (selectedFormId) {
          if (!recipients.some(r => r.value === 'customer_phone')) {
            recipients.push({ value: 'customer_phone', label: 'Customer Phone (from form)' });
          }
        }
      } else if (trigger?.type === 'webhook') {
        // For webhook triggers, customer phone is available if webhook provides phone data
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone (from webhook)' });
        }
      } else if (trigger?.type === 'enter_automation') {
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone (forwarded context)' });
        }
      } else if (trigger?.type === 'enter_crm_stage') {
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone (from opportunity)' });
        }
      }
    });
    
    // Always allow custom phone number option
    recipients.push({ value: 'custom', label: 'Custom Phone Number' });
    
    return recipients;
  };

  // Get available email recipients based on connected triggers/actions (similar to SMS but for emails)
  const getAvailableEmailRecipients = (actionId: string): Array<{value: string, label: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const recipients: Array<{value: string, label: string}> = [];
    
    // Add recipients based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking' || trigger?.type === 'feedback') {
        if (!recipients.some(r => r.value === 'customer_email')) {
          recipients.push({ value: 'customer_email', label: 'Customer Email' });
        }
        if (!recipients.some(r => r.value === 'business_email')) {
          recipients.push({ value: 'business_email', label: 'Business Email' });
        }
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // For form submission triggers, customer email is the primary recipient
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (selectedFormId) {
          if (!recipients.some(r => r.value === 'customer_email')) {
            recipients.push({ value: 'customer_email', label: 'Customer Email (from form)' });
          }
        }
      } else if (trigger?.type === 'webhook') {
        // For webhook triggers, customer email is available if webhook provides email data
        if (!recipients.some(r => r.value === 'customer_email')) {
          recipients.push({ value: 'customer_email', label: 'Customer Email (from webhook)' });
        }
      } else if (trigger?.type === 'enter_automation') {
        if (!recipients.some(r => r.value === 'customer_email')) {
          recipients.push({ value: 'customer_email', label: 'Customer Email (forwarded context)' });
        }
      } else if (trigger?.type === 'enter_crm_stage') {
        if (!recipients.some(r => r.value === 'customer_email')) {
          recipients.push({ value: 'customer_email', label: 'Customer Email (from opportunity)' });
        }
      }
    });
    
    // Always allow custom email address option
    recipients.push({ value: 'custom', label: 'Custom Email Address' });
    
    return recipients;
  };

  // Get available variables based on connected triggers/actions (with chain traversal)
  const getAvailableVariables = (actionId: string): Array<{value: string, label: string, description: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };

    // Traverse the connection chain backwards to find Create CRM Lead actions
    const findCrmLeadActionsInChain = (nodeId: string, visited = new Set<string>()): ActionNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a Create CRM Lead action
      const directAction = actions.find(action => action.id === nodeId && action.type === 'create_crm_lead');
      const foundActions: ActionNodeType[] = directAction ? [directAction] : [];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const actionsFromUpstream = findCrmLeadActionsInChain(connection.fromNodeId, visited);
        foundActions.push(...actionsFromUpstream);
      }
      
      return foundActions;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const connectedCrmLeadActions = findCrmLeadActionsInChain(actionId);
    const variables: Array<{value: string, label: string, description: string}> = [];
    
    // Add variables based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking' || trigger?.type === 'feedback') {
        // Customer variables
        variables.push(
          { value: '{customer_name}', label: 'Customer Name', description: 'Full name of the customer' },
          { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name' },
          { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name' },
          { value: '{customer_phone}', label: 'Customer Phone', description: 'Customer\'s phone number' },
          { value: '{customer_email}', label: 'Customer Email', description: 'Customer\'s email address' }
        );
        
        // Booking variables
        variables.push(
          { value: '{booking_number}', label: 'Booking Number', description: 'Unique booking number' },
          { value: '{booking_title}', label: 'Booking Title', description: 'Title/name of the booking' },
          { value: '{grand_total}', label: 'Grand Total', description: 'Total cost of the booking' }
        );
        
        // Schedule variables
        variables.push(
          { value: '{start_date}', label: 'Start Date', description: 'Booking start date' },
          { value: '{start_time}', label: 'Start Time', description: 'Booking start time' },
          { value: '{drop_off_date}', label: 'Drop Off Date', description: 'Drop off date' },
          { value: '{drop_off_time}', label: 'Drop Off Time', description: 'Drop off time' }
        );
        
        // Vehicle variables
        variables.push(
          { value: '{vehicle_make}', label: 'Vehicle Make', description: 'Make of the customer\'s vehicle (e.g., Toyota)' },
          { value: '{vehicle_model}', label: 'Vehicle Model', description: 'Model of the customer\'s vehicle (e.g., Camry)' },
          { value: '{vehicle_year}', label: 'Vehicle Year', description: 'Year of the customer\'s vehicle' },
          { value: '{vehicle_color}', label: 'Vehicle Color', description: 'Color of the customer\'s vehicle' },
          { value: '{vehicle_info}', label: 'Vehicle Info', description: 'Full vehicle information (Year Make Model)' }
        );
        
        // Payment variables - only for payment-related triggers
        if (trigger.config?.eventType === 'partially_paid' || trigger.config?.eventType === 'paid') {
          variables.push(
            { value: '{invoice_link}', label: 'Invoice Link', description: 'Link to view the most recent invoice/receipt (only available for payment triggers)' }
          );
        }
        
        // Feedback link variable - available for in_progress, completed, and paid booking triggers
        if (['in_progress', 'completed', 'paid'].includes(trigger.config?.eventType)) {
          variables.push(
            { value: '{feedback_link}', label: 'Feedback Link', description: 'Link for customer to leave feedback' }
          );
        }
      } else if (trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form') {
        // Handle form submission triggers
        const selectedFormId = trigger.config?.selectedForm || trigger.config?.selectedFormId || trigger.config?.formId;
        
        if (!selectedFormId) {
          // No form selected - no variables available
          console.log('⚠️ Form submission trigger found but no form selected');
          return;
        }
        
        // Find the selected lead form
        const selectedForm = leadForms.find(form => form.id === selectedFormId);
        
        if (!selectedForm?.form_config?.fields) {
          console.log('⚠️ Selected form not found or has no fields');
          return;
        }
        
        console.log('✅ Adding variables for form submission trigger with form:', selectedForm.name);
        
        // Process each field in the form to create variables
        selectedForm.form_config.fields.forEach((field: any) => {
          if (field.type === 'customer_info') {
            // Special handling for Customer Info component - add standardized customer fields
            variables.push(
              { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name from the form' },
              { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name from the form' },
              { value: '{email}', label: 'Email Address', description: 'Customer\'s email address from the form' },
              { value: '{phone}', label: 'Phone Number', description: 'Customer\'s phone number from the form' },
              { value: '{customer_name}', label: 'Full Name', description: 'Customer\'s full name (first + last)' }
            );
          } else if (field.label) {
            // Regular form field - create variable from field label
            const variableName = field.label.toLowerCase()
              .replace(/[^a-z0-9\s]/g, '') // Remove special characters
              .replace(/\s+/g, '_') // Replace spaces with underscores
              .replace(/_+/g, '_') // Remove duplicate underscores
              .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
            
            variables.push({
              value: `{${variableName}}`,
              label: field.label,
              description: `Value from the "${field.label}" field in the form`
            });
          }
        });
      } else if (trigger?.type === 'enter_automation') {
        variables.push(
          { value: '{parent_automation_id}', label: 'Parent automation ID', description: 'ID of the automation that handed off here' },
          { value: '{parent_execution_id}', label: 'Parent execution ID', description: 'Execution ID from the parent automation' }
        );
        variables.push(
          { value: '{customer_name}', label: 'Customer Name', description: 'Forwarded from parent run when present in context' },
          { value: '{first_name}', label: 'First Name', description: 'Forwarded from parent run when present in context' },
          { value: '{last_name}', label: 'Last Name', description: 'Forwarded from parent run when present in context' },
          { value: '{customer_phone}', label: 'Customer Phone', description: 'Forwarded from parent run when present in context' },
          { value: '{customer_email}', label: 'Customer Email', description: 'Forwarded from parent run when present in context' },
          { value: '{booking_number}', label: 'Booking Number', description: 'Forwarded from parent run when present in context' },
          { value: '{start_date}', label: 'Start Date', description: 'Forwarded from parent run when present in context' },
          { value: '{start_time}', label: 'Start Time', description: 'Forwarded from parent run when present in context' }
        );
      } else if (trigger?.type === 'enter_crm_stage') {
        CRM_OPPORTUNITY_VARIABLE_DEFS.forEach((v) => variables.push(v));
      } else if (trigger?.type === 'webhook') {
        // Handle webhook triggers - add variables from discovered fields
        const discoveredFields = trigger.config?.discovered_fields || [];
        
        if (discoveredFields.length > 0) {
          console.log('✅ Adding variables for webhook trigger with discovered fields:', discoveredFields);
          
          // Add standard customer variables if webhook provides them
          const hasPhone = discoveredFields.some((field: string) => 
            field.toLowerCase().includes('phone')
          );
          const hasEmail = discoveredFields.some((field: string) => 
            field.toLowerCase().includes('email')
          );
          const hasFirstName = discoveredFields.some((field: string) => 
            field.toLowerCase().includes('first') && field.toLowerCase().includes('name')
          );
          const hasLastName = discoveredFields.some((field: string) => 
            field.toLowerCase().includes('last') && field.toLowerCase().includes('name')
          );
          
          // Add standard customer variables
          if (hasFirstName || hasLastName) {
            variables.push(
              { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name from webhook' },
              { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name from webhook' },
              { value: '{customer_name}', label: 'Full Name', description: 'Customer\'s full name (first + last) from webhook' }
            );
          }
          if (hasEmail) {
            variables.push(
              { value: '{email}', label: 'Email Address', description: 'Customer\'s email address from webhook' },
              { value: '{customer_email}', label: 'Customer Email', description: 'Customer\'s email address from webhook' }
            );
          }
          if (hasPhone) {
            variables.push(
              { value: '{phone}', label: 'Phone Number', description: 'Customer\'s phone number from webhook' },
              { value: '{customer_phone}', label: 'Customer Phone', description: 'Customer\'s phone number from webhook' }
            );
          }
          
          // Add variables for all discovered fields
          discoveredFields.forEach((fieldName: string) => {
            // Skip if we already added it as a standard variable
            const lowerName = fieldName.toLowerCase();
            if (
              (lowerName.includes('first') && lowerName.includes('name')) ||
              (lowerName.includes('last') && lowerName.includes('name')) ||
              lowerName.includes('email') ||
              lowerName.includes('phone')
            ) {
              return; // Skip, already added
            }
            
            // Create a nice label from the field name
            const label = fieldName
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            variables.push({
              value: `{${fieldName}}`,
              label: label,
              description: `Value from the "${fieldName}" field in the webhook data`
            });
          });
        } else {
          console.log('⚠️ Webhook trigger found but no discovered fields yet - waiting for first webhook submission');
        }
      } else if (trigger?.type === 'missed_call') {
        // Handle missed call triggers - add caller-related variables
        console.log('✅ Adding variables for missed call trigger');
        variables.push(
          { value: '{caller_name}', label: 'Caller Name', description: 'Name of the caller (if available)' },
          { value: '{caller_phone}', label: 'Caller Phone', description: 'Phone number of the missed caller' },
          { value: '{call_time}', label: 'Call Time', description: 'Time when the call was missed' }
        );
      }
    });

    // Add variables from Create CRM Lead actions
    if (connectedCrmLeadActions.length > 0) {
      console.log('✅ Found Create CRM Lead action(s) in chain, adding CRM variables');
      variables.push(
        { value: '{pipeline_name}', label: 'Pipeline Name', description: 'Name of the CRM pipeline where the lead was created' },
        { value: '{stage_name}', label: 'Stage Name', description: 'Name of the CRM stage where the lead was placed' },
        { value: '{status}', label: 'Lead Status', description: 'Status of the created CRM lead (e.g., Open, In Progress)' }
      );
    }

    const targetAction = actions.find((a) => a.id === actionId);
    if (targetAction?.type === 'send_live_intake_form') {
      const addVar = (v: { value: string; label: string; description: string }) => {
        if (!variables.some((x) => x.value === v.value)) variables.push(v);
      };
      addVar({
        value: '{form_url}',
        label: 'Intake form link',
        description: 'Public URL for this intake send (filled in when this step runs)'
      });
      addVar({
        value: '{intake_url}',
        label: 'Intake form link (alias)',
        description: 'Same as Intake form link'
      });
      addVar({
        value: '{business_name}',
        label: 'Business name',
        description: 'Your business display name'
      });
    }

    return variables;
  };

  const insertSendLiveIntakeVariable = (
    field: 'intakeSmsBody' | 'intakeEmailSubject' | 'intakeEmailBody',
    variable: string
  ) => {
    setSendLiveIntakeFormSettingsModal((prev) => {
      const key =
        field === 'intakeSmsBody'
          ? 'intakeSmsBody'
          : field === 'intakeEmailSubject'
            ? 'intakeEmailSubject'
            : 'intakeEmailBody';
      const current = (prev[key] as string) || '';
      const ref =
        field === 'intakeSmsBody'
          ? intakeSmsTextareaRef
          : field === 'intakeEmailSubject'
            ? intakeEmailSubjectInputRef
            : intakeEmailBodyTextareaRef;
      const el = ref.current;
      if (el && 'selectionStart' in el && typeof (el as HTMLTextAreaElement).selectionStart === 'number') {
        const ta = el as HTMLTextAreaElement | HTMLInputElement;
        const start = ta.selectionStart ?? current.length;
        const end = ta.selectionEnd ?? current.length;
        const next = current.slice(0, start) + variable + current.slice(end);
        queueMicrotask(() => {
          const pos = start + variable.length;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        });
        return { ...prev, [key]: next };
      }
      return { ...prev, [key]: current + variable };
    });
  };

  // Insert variable into SMS message at cursor position
  const insertVariable = (variable: string) => {
    insertAtCursor(messageEditorRef, variable);
    const el = messageEditorRef.current;
    if (el) {
      smsIgnoreSyncRef.current = true;
      setSmsActionSettingsModal(prev => ({ ...prev, message: el.innerHTML }));
    }
  };

  // Insert variable into Email subject at cursor position
  const insertEmailSubjectVariable = (variable: string) => {
    if (!emailSubjectInputRef.current) return;
    
    const input = emailSubjectInputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentSubject = emailActionSettingsModal.subject;
    
    // Insert the variable at cursor position
    const newSubject = currentSubject.substring(0, start) + variable + currentSubject.substring(end);
    
    // Update the subject
    setEmailActionSettingsModal(prev => ({ ...prev, subject: newSubject }));
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Insert variable into Email message at cursor position
  const insertEmailMessageVariable = (variable: string) => {
    insertAtCursor(emailMessageEditorRef, variable);
    const el = emailMessageEditorRef.current;
    if (el) {
      emailIgnoreSyncRef.current = true;
      setEmailActionSettingsModal(prev => ({ ...prev, message: el.innerHTML }));
    }
  };

  // Handle saving SMS message
  const handleSaveSmsMessage = () => {
    if (!smsActionSettingsModal.actionId) return;

    // Convert HTML from contentEditable to plain text for SMS
    const plainMessage = htmlToPlainText(smsActionSettingsModal.message);

    // Update the action with the new message and recipient
    setActions(prev => prev.map(action => 
      action.id === smsActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              message: plainMessage,
              recipient: smsActionSettingsModal.recipient,
              customPhone: smsActionSettingsModal.customPhone
            }
          }
        : action
    ));

    // Close modal
    setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
  };

  // Load email templates for template selector
  const loadEmailTemplates = async () => {
    console.log('📧 Loading email templates...');
    console.log('📧 Selected business:', selectedBusiness?.id);
    
    if (!selectedBusiness?.id) {
      console.error('❌ No business selected');
      alert('No business selected. Please make sure you are logged in.');
      return;
    }
    
    setEmailTemplateSelector(prev => ({ ...prev, loading: true }));
    
    try {
      console.log('📧 Calling emailTemplateService.getTemplates...');
      const templates = await emailTemplateService.getTemplates(selectedBusiness.id);
      console.log('📧 Templates loaded:', templates?.length || 0);
      
      setEmailTemplateSelector(prev => ({ 
        ...prev, 
        templates: templates || [], 
        loading: false 
      }));
    } catch (error) {
      console.error('❌ Error loading email templates:', error);
      alert(`Failed to load email templates: ${error.message || 'Unknown error'}`);
      setEmailTemplateSelector(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle opening template selector
  const handleOpenTemplateSelector = () => {
    console.log('📧 Opening template selector...');
    console.log('📧 Business available:', !!selectedBusiness?.id);
    console.log('📧 Current emailTemplateSelector state:', emailTemplateSelector);
    
    setEmailTemplateSelector(prev => {
      console.log('📧 Setting emailTemplateSelector isOpen to true');
      return { ...prev, isOpen: true };
    });
    loadEmailTemplates();
  };

  // Handle selecting a template
  const handleSelectTemplate = (template: any) => {
    console.log('📧 Template selected:', template.name, 'ID:', template.id);
    
    // DON'T convert template to text - just store the template ID
    // The edge function will fetch and render the full template
    
    // Update email modal with template reference (not content)
    setEmailActionSettingsModal(prev => ({
      ...prev,
      subject: `[Template: ${template.name}]`, // Placeholder to show template is selected
      message: `Template "${template.name}" will be used with full design and styling.`, // Placeholder
      useTemplate: true,
      selectedTemplateId: template.id
    }));
    
    // Close template selector
    setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }));
  };

  // Handle PDF attachment upload for email action
  const handlePdfAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedBusiness?.id) return;
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file.');
      e.target.value = '';
      return;
    }
    setEmailActionSettingsModal(prev => ({ ...prev, pdfUploading: true }));
    try {
      const fileExt = file.name.split('.').pop() || 'pdf';
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `automation-attachments/${selectedBusiness.id}/${fileName}`;
      const { error } = await supabase.storage.from('user-uploads').upload(filePath, file, {
        contentType: 'application/pdf',
        upsert: false
      });
      if (error) throw error;
      setEmailActionSettingsModal(prev => ({
        ...prev,
        pdfAttachment: { storagePath: filePath, filename: file.name },
        pdfUploading: false
      }));
    } catch (err) {
      console.error('PDF upload failed:', err);
      alert('Failed to upload PDF. Please try again.');
      setEmailActionSettingsModal(prev => ({ ...prev, pdfUploading: false }));
    }
    e.target.value = '';
  };

  // Handle saving Email message
  const handleSaveEmailMessage = () => {
    if (!emailActionSettingsModal.actionId) return;

    // Update the action with the new subject, message, recipient, and pdf attachment
    setActions(prev => prev.map(action => 
      action.id === emailActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              subject: emailActionSettingsModal.subject,
              message: emailActionSettingsModal.message,
              recipient: emailActionSettingsModal.recipient,
              customEmail: emailActionSettingsModal.customEmail,
              useTemplate: emailActionSettingsModal.useTemplate,
              selectedTemplateId: emailActionSettingsModal.selectedTemplateId,
              pdfAttachment: emailActionSettingsModal.pdfAttachment
            }
          }
        : action
    ));

    // Close modal
    setEmailActionSettingsModal({ 
      isOpen: false, 
      actionId: null, 
      subject: '', 
      message: '', 
      recipient: '', 
      customEmail: '',
      useTemplate: false,
      selectedTemplateId: null,
      pdfAttachment: null,
      pdfUploading: false
    });
  };

  // Handle saving Assign Tags action
  const handleSaveAssignTagsAction = () => {
    if (!assignTagsActionSettingsModal.actionId) return;

    // Update the action with the new conditions and tags
    setActions(prev => prev.map(action => 
      action.id === assignTagsActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              conditions: assignTagsActionSettingsModal.conditions,
              selectedTags: assignTagsActionSettingsModal.selectedTags
            }
          }
        : action
    ));

    // Close modal
    setAssignTagsActionSettingsModal({ 
      isOpen: false, 
      actionId: null, 
      conditions: [],
      selectedTags: []
    });
  };

  // Function to handle opening variables modal
  const handleOpenVariablesModal = (fieldName: string, currentValue: string) => {
    setVariablesModal({
      isOpen: true,
      currentField: fieldName,
      currentValue: currentValue
    });
  };

  // Function to handle closing variables modal
  const handleCloseVariablesModal = () => {
    setVariablesModal({
      isOpen: false,
      currentField: null,
      currentValue: ''
    });
  };

  // Function to insert variable into current field
  const insertVariableIntoField = (variable: string) => {
    if (!variablesModal.currentField) return;
    
    const fieldName = variablesModal.currentField;
    const currentValue = variablesModal.currentValue;
    
    // Insert the variable at the end of the current value
    const newValue = currentValue + variable;
    
    // Handle custom fields differently
    if (fieldName.startsWith('custom_fields.')) {
      const customFieldId = fieldName.replace('custom_fields.', '');
      setFieldMappings(prev => ({
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [customFieldId]: newValue
        }
      }));
    } else {
      // Update regular field mapping
      setFieldMappings(prev => ({
        ...prev,
        [fieldName]: newValue
      }));
    }
    
    // Close the modal
    handleCloseVariablesModal();
  };

  // Get node position for wire rendering - using DOM-based positioning like drag wires
  const getNodePosition = (nodeId: string, portType: 'inflow' | 'outflow') => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    // Find the actual DOM element for the port
    const portSelector = portType === 'inflow' 
      ? `[data-node-id="${nodeId}"][data-port-type="inflow"]`
      : `[data-node-id="${nodeId}"][data-port-type="outflow"]`;
    
    const portElement = canvasRef.current.querySelector(portSelector) as HTMLElement;
    
    if (portElement) {
      // Use the same calculation as drag wires - get actual DOM position
      const portRect = portElement.getBoundingClientRect();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      
      return {
        x: portRect.left + portRect.width / 2 - canvasRect.left,
        y: portRect.top + portRect.height / 2 - canvasRect.top
      };
    }

    // Improved fallback calculation when DOM elements aren't available yet
    const trigger = triggers.find(t => t.id === nodeId);
    if (trigger) {
      // More precise calculation accounting for actual CSS structure:
      // p-4 = 32px padding, icon+text ≈ 48px, label = mt-1(4px) + py-1(8px) + text(16px) = 28px
      const basePadding = 32; // p-4 top+bottom padding  
      const contentHeight = 48; // Icon + name + type text
      const labelHeight = trigger.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      return {
        x: trigger.position.x + 100, // Center of node (min-w-[200px] / 2)
        y: trigger.position.y + totalHeight + 8 // Bottom + port radius
      };
    }
    
    const action = actions.find(a => a.id === nodeId);
    if (action) {
      // Same precise calculation for actions
      const basePadding = 32; // p-4 top+bottom padding
      const contentHeight = 48; // Icon + name + type text  
      const labelHeight = action.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      if (portType === 'inflow') {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y - 8    // Top - port radius
        };
      } else {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y + totalHeight + 8 // Bottom + port radius
        };
      }
    }
    
    return { x: 0, y: 0 };
  };

  const selectedTrigger = selectedTriggerId ? triggers.find(t => t.id === selectedTriggerId) : null;

  // Fetch lead forms when trigger settings modal opens and form type is lead_form
  useEffect(() => {
    if (isTriggerSettingsOpen && selectedTrigger?.type === 'form_submission' && selectedTrigger.config?.formType === 'lead_form') {
      fetchLeadForms();
    }
  }, [isTriggerSettingsOpen, selectedTrigger?.type, selectedTrigger?.config?.formType, selectedBusiness?.id]);

  // Update form fields when lead forms, triggers, or connections change (for CRM action settings)
  useEffect(() => {
    if (crmLeadActionSettingsModal.isOpen && crmLeadActionSettingsModal.actionId && leadForms.length > 0) {
      const formFields = getSelectedFormFields(crmLeadActionSettingsModal.actionId);
      setSelectedFormFields(formFields);
      console.log('🔄 Updated form fields for action:', crmLeadActionSettingsModal.actionId);
      console.log('🔄 Form fields:', formFields);
      console.log('🔄 Is connected:', isActionConnectedToTrigger(crmLeadActionSettingsModal.actionId));
    }
  }, [leadForms, triggers, wireConnections, crmLeadActionSettingsModal.isOpen, crmLeadActionSettingsModal.actionId]);

  // Fetch lead forms when SMS action settings modal opens and there are form submission triggers connected
  useEffect(() => {
    if (smsActionSettingsModal.isOpen && smsActionSettingsModal.actionId) {
      // Check if this SMS action is connected to any form submission triggers
      const connectedTriggers = (() => {
        const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
          if (visited.has(nodeId)) return [];
          visited.add(nodeId);
          
          const directTrigger = triggers.find(trigger => trigger.id === nodeId);
          if (directTrigger) return [directTrigger];
          
          const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
          let foundTriggers: TriggerNodeType[] = [];
          
          for (const connection of incomingConnections) {
            const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
            foundTriggers = [...foundTriggers, ...triggersFromUpstream];
          }
          
          return foundTriggers;
        };
        
        return findTriggersInChain(smsActionSettingsModal.actionId);
      })();
      
      const hasFormSubmissionTrigger = connectedTriggers.some(trigger => 
        trigger?.type === 'form_submission' && trigger.config?.formType === 'lead_form'
      );
      
      if (hasFormSubmissionTrigger && leadForms.length === 0) {
        console.log('🔄 SMS action connected to form submission trigger, fetching lead forms...');
        fetchLeadForms();
      }
    }
  }, [smsActionSettingsModal.isOpen, smsActionSettingsModal.actionId, triggers, wireConnections, leadForms.length]);

  // Sync SMS editor content when modal state changes (e.g. on open or variable insert)
  useEffect(() => {
    if (smsIgnoreSyncRef.current) { smsIgnoreSyncRef.current = false; return; }
    const el = messageEditorRef.current;
    if (!el) return;
    const html = plainTextToHtml(smsActionSettingsModal.message);
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [smsActionSettingsModal.message]);

  // Sync Email editor content when modal state changes (e.g. on open or variable insert)
  useEffect(() => {
    if (emailIgnoreSyncRef.current) { emailIgnoreSyncRef.current = false; return; }
    const el = emailMessageEditorRef.current;
    if (!el) return;
    const html = plainTextToHtml(emailActionSettingsModal.message);
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [emailActionSettingsModal.message]);

  // Load custom field definitions when business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadCustomFieldDefinitions();
    }
  }, [selectedBusiness?.id]);

  const fetchLeadForms = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoadingLeadForms(true);
    try {
      const { data, error } = await supabase
        .from('lead_forms')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeadForms(data || []);
    } catch (error) {
      console.error('Error fetching lead forms:', error);
    } finally {
      setLoadingLeadForms(false);
    }
  };

  const fetchPipelines = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoadingPipelines(true);
    try {
      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('pipeline_name', { ascending: true });

      if (error) throw error;
      setPipelines(
        (data || []).map((pipeline) => ({
          ...pipeline,
          stages: parsePipelineStages(pipeline.stages),
        }))
      );
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    } finally {
      setLoadingPipelines(false);
    }
  };

  // Load custom field definitions from database (same as CRM page)
  const loadCustomFieldDefinitions = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .select('id, field_name, field_type, display_order')
        .eq('business_id', selectedBusiness.id)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Convert database format to component format
      const definitions = (data || []).map(field => ({
        id: field.id,
        name: field.field_name,
        type: field.field_type
      }));

      setCustomFieldDefinitions(definitions);
    } catch (error) {
      console.error('Error loading custom field definitions:', error);
    }
  };

  // Load boards and columns for Create Task action
  const loadBoardsAndColumns = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Fetch all boards for the business
      const boards = await getBoards(selectedBusiness.id);
      
      // Update the modal state with boards
      setCreateTaskActionSettingsModal(prev => ({
        ...prev,
        boards: boards.map(board => ({
          id: board.id,
          board_title: board.board_title
        }))
      }));

      // If a board is selected, load its columns
      const currentBoardId = createTaskActionSettingsModal.selectedBoardId;
      if (currentBoardId) {
        const boardWithColumns = await getBoardWithColumns(currentBoardId);
        
        setCreateTaskActionSettingsModal(prev => ({
          ...prev,
          columns: boardWithColumns.columns.map(column => ({
            id: column.id,
            title: column.title
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading boards and columns:', error);
    }
  };

  // Function to load columns when a board is selected
  const loadColumnsForBoard = async (boardId: string) => {
    try {
      const boardWithColumns = await getBoardWithColumns(boardId);
      
      setCreateTaskActionSettingsModal(prev => ({
        ...prev,
        columns: boardWithColumns.columns.map(column => ({
          id: column.id,
          title: column.title
        }))
      }));
    } catch (error) {
      console.error('Error loading columns for board:', error);
    }
  };

  // Get selected form fields from the trigger configuration
  // Helper function to check if an action is connected to a trigger
  const isActionConnectedToTrigger = (actionId: string): boolean => {
    // Traverse backwards through connections to find if there's a path to any trigger
    const visited = new Set<string>();
    
    const findTriggerConnection = (nodeId: string): boolean => {
      if (visited.has(nodeId)) return false;
      visited.add(nodeId);
      
      // Check if this node is a trigger
      if (triggers.some(t => t.id === nodeId)) {
        return true;
      }
      
      // Find all connections where this node is the target (incoming connections)
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      
      // Check if any of the source nodes lead to a trigger
      for (const conn of incomingConnections) {
        if (findTriggerConnection(conn.fromNodeId)) {
          return true;
        }
      }
      
      return false;
    };
    
    return findTriggerConnection(actionId);
  };

  const getSelectedFormFields = (actionId?: string): Array<{id: string, type: string, label: string}> => {
    console.log('🔍 Getting selected form fields for action:', actionId);
    console.log('🔍 Available triggers:', triggers);
    console.log('🔍 Available lead forms:', leadForms);
    console.log('🔍 Wire connections:', wireConnections);
    
    // If actionId is provided, find triggers connected to that specific action
    let targetActionId = actionId;
    
    // If no actionId provided, try to find any CRM action
    if (!targetActionId) {
      const crmAction = actions.find(a => a.type === 'create_crm_lead');
      targetActionId = crmAction?.id;
    }
    
    if (!targetActionId) {
      console.log('❌ No CRM action found');
      return [];
    }
    
    console.log('🔍 Looking for triggers connected to action:', targetActionId);
    
    // Find trigger(s) connected to this specific action by traversing backwards through connections
    const findConnectedTriggers = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return [];
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(t => t.id === nodeId);
      if (directTrigger) {
        console.log('✅ Found trigger:', directTrigger.id, directTrigger.type);
        return [directTrigger];
      }
      
      // Find incoming connections to this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      console.log(`🔍 Found ${incomingConnections.length} incoming connections to ${nodeId}`);
      
      let foundTriggers: TriggerNodeType[] = [];
      for (const conn of incomingConnections) {
        const upstreamTriggers = findConnectedTriggers(conn.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...upstreamTriggers];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findConnectedTriggers(targetActionId);
    console.log('🔍 Found connected triggers:', connectedTriggers);
    
    // Check for webhook trigger first
    const webhookTrigger = connectedTriggers.find(t => t.type === 'webhook');
    
    if (webhookTrigger) {
      console.log('🔗 Found webhook trigger:', webhookTrigger);
      console.log('🔗 Webhook config:', webhookTrigger.config);
      
      // Get discovered fields from webhook trigger
      const discoveredFields = webhookTrigger.config?.discovered_fields;
      
      if (!discoveredFields || discoveredFields.length === 0) {
        console.log('⚠️ Webhook has no discovered fields yet - waiting for first submission');
        return [];
      }
      
      console.log('✅ Webhook discovered fields:', discoveredFields);
      
      // Convert discovered fields to form field format
      const webhookFields: Array<{id: string, type: string, label: string}> = discoveredFields.map((fieldName: string) => {
        // Determine field type based on field name
        let fieldType = 'text';
        const lowerName = fieldName.toLowerCase();
        
        if (lowerName.includes('email')) {
          fieldType = 'email';
        } else if (lowerName.includes('phone')) {
          fieldType = 'tel';
        }
        
        // Create a nice label from the field name
        const label = fieldName
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        return {
          id: fieldName,
          type: fieldType,
          label: label
        };
      });
      
      console.log('✅ Converted webhook fields to form fields:', webhookFields);
      return webhookFields;
    }
    
    // Find the form submission trigger with a lead form
    const connectedTrigger = connectedTriggers.find(t => 
      t.type === 'form_submission' && t.config?.formType === 'lead_form'
    );
    
    console.log('🔍 Found connected form trigger:', connectedTrigger);
    
    if (!connectedTrigger) {
      console.log('❌ No form submission or webhook trigger connected to CRM action');
      return [];
    }
    
    const selectedFormId = connectedTrigger.config?.selectedForm || connectedTrigger.config?.selectedFormId || connectedTrigger.config?.formId;
    console.log('🔍 Looking for form with ID:', selectedFormId);
    
    // Find the selected lead form
    const selectedForm = leadForms.find(form => {
      console.log('🔍 Comparing form:', form.id, 'with', selectedFormId);
      return form.id === selectedFormId;
    });
    console.log('🔍 Found selected form:', selectedForm);
    
    if (!selectedForm?.form_config?.fields) {
      console.log('❌ No form fields found in configuration');
      console.log('🔍 Form config:', selectedForm?.form_config);
      return [];
    }
    
    const formFields: Array<{id: string, type: string, label: string}> = [];
    
    // Process each field in the form
    selectedForm.form_config.fields.forEach((field: any) => {
      console.log('🔍 Processing field:', field);
      
      if (field.type === 'customer_info') {
        // Special handling for Customer Info component - create variables for standardized fields
        console.log('✅ Found Customer Info component, adding standardized fields');
        formFields.push(
          { id: `${field.id}_first_name`, type: 'text', label: 'First Name' },
          { id: `${field.id}_last_name`, type: 'text', label: 'Last Name' },
          { id: `${field.id}_email`, type: 'email', label: 'Email Address' },
          { id: `${field.id}_phone`, type: 'tel', label: 'Phone Number' }
        );
      } else {
        // Regular form field
        formFields.push({
          id: field.id,
          type: field.type,
          label: field.label
        });
      }
    });
    
    console.log('✅ Final form fields:', formFields);
    return formFields;
  };

  // Handle edit automation title
  const handleEditTitle = () => {
    if (selectedAutomation) {
      setEditTitle(selectedAutomation.name);
      setIsEditTitleModalOpen(true);
    }
  };

  const handleSaveTitle = async () => {
    if (!selectedAutomation?.id || !editTitle.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const updatedAutomation = await updateAutomation(selectedAutomation.id, {
        name: editTitle.trim()
      });

      // Update automations list
      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );

      // Update selected automation
      setSelectedAutomation(updatedAutomation);
      setIsEditTitleModalOpen(false);
      setEditTitle('');
    } catch (err) {
      console.error('Error updating automation title:', err);
      setError('Failed to update automation title');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete automation
  const handleDeleteAutomation = () => {
    setIsDeleteAutomationModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAutomation?.id) return;

    try {
      setDeleteLoading(true);
      setError(null);

      await deleteAutomation(selectedAutomation.id);

      // Remove from automations list
      setAutomations(prev => prev.filter(automation => automation.id !== selectedAutomation.id));
      
      // Clear selected automation
      handleClearSelectedAutomation();
      setIsDeleteAutomationModalOpen(false);
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation');
    } finally {
      setDeleteLoading(false);
    }
  };

  const isCrmAdvancedSplitSource = (nodeId: string) => {
    const n = actions.find((x) => x.id === nodeId);
    return n?.type === 'create_crm_lead' && !!n?.config?.useCrmAdvancedSplit;
  };

  /** Linear path from start until (and including) first advanced CRM, or full chain if none. */
  const buildActionChainStoppingAtAdvancedCrm = (startNodeId: string): ActionNodeType[] => {
    const chain: ActionNodeType[] = [];
    const visit = (nodeId: string) => {
      const outs = wireConnections.filter((c) => c.fromNodeId === nodeId);
      if (outs.length === 0) return;
      const sorted = [...outs].sort((a, b) => a.toNodeId.localeCompare(b.toNodeId));
      for (const connection of sorted) {
        const action = actions.find((a) => a.id === connection.toNodeId);
        if (!action) continue;
        chain.push(action);
        if (action.type === 'create_crm_lead' && action.config?.useCrmAdvancedSplit) {
          return;
        }
        visit(action.id);
        return;
      }
    };
    visit(startNodeId);
    return chain;
  };

  /** Actions after CRM on one AI branch (linear follow single successor). */
  const buildBranchSubchain = (crmId: string, branch: 'left' | 'right'): ActionNodeType[] => {
    const place = pendingCrmBranchPlacement;
    const edge = wireConnections.find(
      (c) => c.fromNodeId === crmId && (c.crm_branch || 'left') === branch
    );
    let cur: string | null = edge?.toNodeId ?? null;
    if (
      !cur &&
      place &&
      place.parentId === crmId &&
      place.branch === branch &&
      actions.some((a) => a.id === place.actionId)
    ) {
      cur = place.actionId;
    }
    if (!cur) return [];
    const sub: ActionNodeType[] = [];
    const vis = new Set<string>();
    while (cur) {
      if (vis.has(cur)) break;
      vis.add(cur);
      const act = actions.find((a) => a.id === cur);
      if (!act) break;
      sub.push(act);
      const nexts = wireConnections.filter((w) => w.fromNodeId === cur);
      if (nexts.length === 0) break;
      const next =
        nexts.length === 1
          ? nexts[0]
          : [...nexts].sort((a, b) => a.toNodeId.localeCompare(b.toNodeId))[0];
      cur = next.toNodeId;
    }
    return sub;
  };

  // Render Linear Workflow View
  const renderLinearWorkflow = () => {
    // If no triggers exist, show initial + button to add first trigger
    if (triggers.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>Start Your Workflow</h3>
            <p className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Add your first trigger to begin</p>
          </div>
          <button
            onClick={handleAddTrigger}
            className="bg-blue-600 active:bg-blue-700 text-white p-4 rounded-[10px] shadow-sm transition-all duration-150 touch-manipulation"
            style={{ 
              minHeight: '44px',
              minWidth: '44px',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>
      );
    }

    // Build linear flow from triggers and their connected actions
    const linearFlow = buildLinearFlow();
    
    return (
      <div className="space-y-0">
        {linearFlow.map((flowItem, index) => (
          <div key={`flow-${index}-${flowItem.type}`} id={`node-${flowItem.node?.id || flowItem.parentId || 'split'}-${index}`}>
            {flowItem.type === 'trigger' && flowItem.node && (
              <>
                {renderLinearTrigger(flowItem.node as TriggerNodeType)}
                {/* Show connection line if this trigger has outgoing connections */}
                {wireConnections.some(conn => conn.fromNodeId === flowItem.node!.id) && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-6 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </>
            )}
            {flowItem.type === 'action' && flowItem.node && (
              <>
                {renderLinearAction(flowItem.node as ActionNodeType, flowItem.level)}
                {wireConnections.some(conn => conn.fromNodeId === flowItem.node!.id) && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-6 bg-green-500 rounded-full"></div>
                  </div>
                )}
              </>
            )}
            {flowItem.type === 'crm-split-rows' && (
              <div className="w-full max-w-[920px] mx-auto px-2 pb-2">
                <div className="flex flex-wrap justify-center gap-8 items-start">
                  {(['left', 'right'] as const).map((side) => {
                    const branchList = side === 'left' ? flowItem.left : flowItem.right;
                    const addParentId = branchList.length
                      ? branchList[branchList.length - 1].id
                      : flowItem.crmId;
                    const connectBranchWhenEmpty: 'left' | 'right' | undefined = branchList.length
                      ? undefined
                      : side;
                    return (
                      <div
                        key={side}
                        className="flex flex-col items-center flex-1 min-w-[280px] max-w-[400px]"
                      >
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          {side} path
                        </span>
                        {branchList.map((act) => (
                          <React.Fragment key={act.id}>
                            <div className="flex justify-center py-2">
                              <div className="w-0.5 h-6 bg-green-500 rounded-full"></div>
                            </div>
                            {renderLinearAction(act, flowItem.level)}
                            {wireConnections.some((c) => c.fromNodeId === act.id) && (
                              <div className="flex justify-center py-2">
                                <div className="w-0.5 h-6 bg-green-500 rounded-full"></div>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                        <div className="py-4 w-full flex justify-center">
                          {renderAddActionButton(addParentId, flowItem.level, connectBranchWhenEmpty)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {flowItem.type === 'add-action' && flowItem.parentId && (
              <div className="py-4">
                {renderAddActionButton(flowItem.parentId, flowItem.level)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Build linear flow structure from existing triggers and actions
  const buildLinearFlow = () => {
    type FlowItem =
      | { type: 'trigger'; node?: TriggerNodeType; level: number }
      | { type: 'action'; node?: ActionNodeType; level: number }
      | {
          type: 'crm-split-rows';
          crmId: string;
          left: ActionNodeType[];
          right: ActionNodeType[];
          level: number;
        }
      | { type: 'add-action'; parentId: string; level: number };

    const flow: FlowItem[] = [];
    const connectedActionIds = new Set(wireConnections.map((conn) => conn.toNodeId));

    triggers.forEach((trigger) => {
      flow.push({ type: 'trigger', node: trigger, level: 0 });
      const actionChain = buildActionChainStoppingAtAdvancedCrm(trigger.id);
      const crmIdx = actionChain.findIndex(
        (a) => a.type === 'create_crm_lead' && !!a.config?.useCrmAdvancedSplit
      );

      if (crmIdx === -1) {
        actionChain.forEach((action) => flow.push({ type: 'action', node: action, level: 1 }));
        const lastNodeId = actionChain.length > 0 ? actionChain[actionChain.length - 1].id : trigger.id;
        if (!wireConnections.some((conn) => conn.fromNodeId === lastNodeId)) {
          flow.push({ type: 'add-action', parentId: lastNodeId, level: 1 });
        }
        return;
      }

      actionChain.slice(0, crmIdx).forEach((action) => flow.push({ type: 'action', node: action, level: 1 }));
      const crm = actionChain[crmIdx];
      flow.push({ type: 'action', node: crm, level: 1 });
      const left = buildBranchSubchain(crm.id, 'left');
      const right = buildBranchSubchain(crm.id, 'right');
      flow.push({ type: 'crm-split-rows', crmId: crm.id, left, right, level: 1 });
    });

    const unconnectedActions = actions.filter((action) => {
      if (connectedActionIds.has(action.id)) return false;
      const place = pendingCrmBranchPlacement;
      if (!place) return true;
      if (place.actionId === action.id) return false;
      if (place.prevTempActionId === action.id) return false;
      return true;
    });
    unconnectedActions.forEach((action) => {
      flow.push({ type: 'action', node: action, level: 0 });
      flow.push({ type: 'add-action', parentId: action.id, level: 0 });
    });

    return flow;
  };


  // Get actions connected to a specific node (trigger or action)
  const getActionsConnectedToNode = (nodeId: string): ActionNodeType[] => {
    const directConnections = wireConnections.filter(conn => conn.fromNodeId === nodeId);
    const connectedActions: ActionNodeType[] = [];
    
    directConnections.forEach(connection => {
      const action = actions.find(a => a.id === connection.toNodeId);
      if (action) {
        connectedActions.push(action);
      }
    });
    
    return connectedActions;
  };

  // Render trigger in linear layout (matching existing design)
  const renderLinearTrigger = (trigger: TriggerNodeType) => {
    const formatEventType = (eventType: string, triggerType?: string, config?: any) => {
      if (triggerType === 'feedback') {
        const minRating = config?.minRating || 5;
        return `${minRating} Star${minRating > 1 ? 's' : ''} or More`;
      }
      if (triggerType === 'form_submission') {
        return 'Form Submitted';
      }
      if (triggerType === 'missed_call') {
        return 'Call Missed';
      }
      if (triggerType === 'enter_automation') {
        return 'Receiver';
      }
      if (triggerType === 'enter_crm_stage') {
        return config?.uiLabel || 'CRM stage not configured';
      }
      return eventType.charAt(0).toUpperCase() + eventType.slice(1);
    };

    return (
      <div className="flex justify-center">
        <div 
          className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 p-4 min-w-[320px] max-w-[400px] w-full relative cursor-pointer touch-manipulation"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleTriggerSettings(trigger.id);
          }}
          title="Configure trigger"
          style={{
            WebkitTapHighlightColor: 'transparent',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}
        >

          <div className="flex items-center gap-4">
            {/* Icon/Avatar */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              trigger.type === 'missed_call' ? 'bg-red-600' : 
              trigger.type === 'feedback' ? 'bg-yellow-600' : 
              trigger.type === 'enter_automation' ? 'bg-teal-600' :
              trigger.type === 'enter_crm_stage' ? 'bg-indigo-600' :
              'bg-purple-600'
            }`}>
              {trigger.type === 'missed_call' ? <Phone className="h-6 w-6 text-white" /> :
               trigger.type === 'feedback' ? <Star className="h-6 w-6 text-white" /> :
               trigger.type === 'enter_automation' ? <GitBranch className="h-6 w-6 text-white" /> :
               trigger.type === 'enter_crm_stage' ? <Layers className="h-6 w-6 text-white" /> :
               <Zap className="h-6 w-6 text-white" />}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate text-[16px]" style={{ letterSpacing: '-0.01em' }}>{trigger.name}</h3>
              </div>
              <p className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>{formatEventType(trigger.config?.eventType || 'created', trigger.type, trigger.config)}</p>
              {trigger.config?.uiLabel && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-[6px] text-[11px] font-semibold ${
                    trigger.type === 'missed_call' ? 'bg-red-100 text-red-700' : 
                    trigger.type === 'feedback' ? 'bg-yellow-100 text-yellow-700' : 
                    trigger.type === 'enter_automation' ? 'bg-teal-100 text-teal-700' :
                    trigger.type === 'enter_crm_stage' ? 'bg-indigo-100 text-indigo-700' :
                    'bg-purple-100 text-purple-700'
                  }`} style={{ letterSpacing: '-0.01em' }}>
                    {trigger.config.uiLabel}
                  </span>
                </div>
              )}
            </div>
            
            {/* Status/Info */}
            <div className="text-right flex-shrink-0">
              <div className={`text-[13px] font-semibold ${
                trigger.type === 'missed_call' ? 'text-red-600' : 
                trigger.type === 'feedback' ? 'text-yellow-600' : 
                trigger.type === 'enter_automation' ? 'text-teal-600' :
                trigger.type === 'enter_crm_stage' ? 'text-indigo-600' :
                'text-purple-600'
              }`} style={{ letterSpacing: '-0.01em' }}>Trigger</div>
              <div className="text-[11px] text-gray-500 mt-1" style={{ letterSpacing: '-0.01em' }}>Active</div>
            </div>
          </div>

          {/* Outflow Port */}
          <LinearConnectionPort nodeId={trigger.id} type="outflow" />
        </div>
      </div>
    );
  };

  // Render action in linear layout (matching existing design)
  const renderLinearAction = (action: ActionNodeType, level: number) => {
    const getActionIcon = (actionType: string) => {
      switch (actionType) {
        case 'sms':
          return <MessageCircle className="h-5 w-5 text-white" />;
        case 'email':
          return <Mail className="h-5 w-5 text-white" />;
        case 'delay':
          return <Clock className="h-5 w-5 text-white" />;
        case 'create_crm_lead':
          return <Plus className="h-5 w-5 text-white" />;
        case 'check':
          return <Eye className="h-5 w-5 text-white" />;
        case 'create_task':
          return <CheckSquare className="h-5 w-5 text-white" />;
        case 'assign_tags':
          return <Tag className="h-5 w-5 text-white" />;
        case 'add_to_automation':
          return <Share2 className="h-5 w-5 text-white" />;
        case 'send_live_intake_form':
          return <ClipboardList className="h-5 w-5 text-white" />;
        case 'feedback_agent':
          return <Phone className="h-5 w-5 text-white" />;
        default:
          return <Zap className="h-5 w-5 text-white" />;
      }
    };
    
    const getActionColor = (actionType: string) => {
      switch (actionType) {
        case 'sms':
          return 'bg-green-600';
        case 'email':
          return 'bg-blue-600';
        case 'delay':
          return 'bg-orange-600';
        case 'create_crm_lead':
          return 'bg-purple-600';
        case 'check':
          return 'bg-yellow-600';
        case 'create_task':
          return 'bg-indigo-600';
        case 'assign_tags':
          return 'bg-pink-600';
        case 'add_to_automation':
          return 'bg-teal-600';
        case 'send_live_intake_form':
          return 'bg-cyan-600';
        case 'feedback_agent':
          return 'bg-amber-500';
        default:
          return 'bg-gray-600';
      }
    };

    return (
      <div className="flex justify-center">
        {/* Action card - booking card style */}
        <div 
          className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 p-4 min-w-[320px] max-w-[400px] w-full relative cursor-pointer touch-manipulation"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void handleActionSettingsClick(action.id);
          }}
          title="Configure action"
          style={{
            WebkitTapHighlightColor: 'transparent',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}
        >

          {/* Inflow Port */}
          <LinearConnectionPort nodeId={action.id} type="inflow" />

          <div className="flex items-center gap-4">
            {/* Icon/Avatar */}
            <div className={`w-12 h-12 ${getActionColor(action.type)} rounded-full flex items-center justify-center flex-shrink-0`}>
              <div className="text-white">
                {getActionIcon(action.type)}
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate text-[16px]" style={{ letterSpacing: '-0.01em' }}>{action.name}</h3>
              </div>
              <p className="text-[13px] text-gray-600 capitalize" style={{ letterSpacing: '-0.01em' }}>{action.type.replace('_', ' ')} Action</p>
              {action.type === 'add_to_automation' && action.config?.targetAutomationName && (
                <p className="text-[12px] text-teal-700 mt-1" style={{ letterSpacing: '-0.01em' }}>
                  → {action.config.targetAutomationName}
                </p>
              )}
              {action.type === 'send_live_intake_form' && (action.config?.intake_template_title || action.config?.send_mode) && (
                <p className="text-[12px] text-cyan-700 mt-1" style={{ letterSpacing: '-0.01em' }}>
                  {action.config?.intake_template_title ? `${action.config.intake_template_title} · ` : ''}
                  {action.config?.send_mode === 'both'
                    ? 'SMS + email'
                    : action.config?.send_mode === 'sms'
                      ? 'SMS'
                      : 'Email'}
                </p>
              )}
              {action.config?.uiLabel && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[11px] font-semibold bg-gray-100 text-gray-700" style={{ letterSpacing: '-0.01em' }}>
                    {action.config.uiLabel}
                  </span>
                </div>
              )}
            </div>
            
            {/* Status/Info */}
            <div className="text-right flex-shrink-0">
              <div className="text-[13px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Action</div>
              <div className="text-[11px] text-gray-500 mt-1" style={{ letterSpacing: '-0.01em' }}>Ready</div>
            </div>
          </div>

          {/* Outflow Port */}
          <LinearConnectionPort nodeId={action.id} type="outflow" />
        </div>
      </div>
    );
  };

  // Render add action button (connectBranch: first action on empty CRM branch uses labeled edge)
  const renderAddActionButton = (
    parentId: string,
    level: number,
    connectBranch?: 'left' | 'right'
  ) => {
    return (
      <div className="flex justify-center">
        <button
          onClick={() => {
            if (connectBranch) {
              setPendingConnectAfterActionAdd({ parentId, branch: connectBranch });
              handleAddAction();
              return;
            }
            handleAddActionSequential(parentId);
          }}
          className="group relative bg-gray-100 active:bg-gray-200 text-gray-600 p-3 rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 touch-manipulation"
          title="Add Action"
          style={{ 
            WebkitTapHighlightColor: 'transparent',
            minHeight: '44px',
            minWidth: '44px'
          }}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  };

  // Handle adding action in sequence (below current action/trigger)
  const handleAddActionSequential = (parentId: string) => {
    if (isCrmAdvancedSplitSource(parentId)) {
      return;
    }
    setPendingActionParent(parentId);
    handleAddAction();
  };

  // Handle adding parallel actions (left/right of current action)
  const handleAddActionParallel = (actionId: string, side: 'left' | 'right') => {
    // Find the parent of the current action to connect the new action to the same parent
    const currentActionConnection = wireConnections.find(conn => conn.toNodeId === actionId);
    if (currentActionConnection) {
      setPendingActionParent(currentActionConnection.fromNodeId);
      handleAddAction();
    } else {
      console.log('No parent found for parallel action');
    }
  };

  // Handle deleting action from linear view
  const handleDeleteActionLinear = (actionId: string) => {
    // Remove action from state
    setActions(prev => prev.filter(action => action.id !== actionId));
    
    // Remove any wire connections involving this action
    setWireConnections(prev => prev.filter(conn => 
      conn.fromNodeId !== actionId && conn.toNodeId !== actionId
    ));
  };

  // Linear Connection Port Component
  const LinearConnectionPort: React.FC<{
    nodeId: string;
    type: 'inflow' | 'outflow';
  }> = ({ nodeId, type }) => {
    const isSelected = selectedOutflowPort === nodeId && type === 'outflow';
    
    // Check if this port is currently connecting (saving to DB)
    const isConnecting = type === 'inflow'
      ? connectingBetween?.toNodeId === nodeId
      : connectingBetween?.fromNodeId === nodeId;
    
    // Check if this port is connected (only when not connecting - we don't show connected until saved)
    const isConnected = !isConnecting && (type === 'inflow' 
      ? wireConnections.some(conn => conn.toNodeId === nodeId)
      : wireConnections.some(conn => conn.fromNodeId === nodeId));
    
    const handlePortClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (isConnecting) return;
      
      // If the port is connected, show disconnect popup
      if (isConnected) {
        const rect = e.currentTarget.getBoundingClientRect();
        setDisconnectPopup({
          show: true,
          nodeId,
          portType: type,
          position: {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          }
        });
        return;
      }
      
      // Original connection logic for unconnected ports
      if (type === 'outflow') {
        // If this port is already selected, deselect it
        if (isSelected) {
          setSelectedOutflowPort(null);
        } else {
          // Select this outflow port (turn it red)
          setSelectedOutflowPort(nodeId);
        }
      } else if (type === 'inflow' && selectedOutflowPort) {
        if (isCrmAdvancedSplitSource(selectedOutflowPort)) {
          setCrmBranchConnectModal({
            fromNodeId: selectedOutflowPort,
            toNodeId: nodeId,
            branch: 'left',
            mode: 'linear'
          });
          setSelectedOutflowPort(null);
        } else {
          void handleLinearConnection(selectedOutflowPort, nodeId);
          setSelectedOutflowPort(null);
        }
      }
    };
    
    const basePos = type === 'inflow' ? 'top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2' : 'bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2';
    return (
      <div
        className={`absolute w-4 h-4 rounded-full border-2 shadow-lg flex items-center justify-center transition-all ${basePos} ${
          isConnecting
            ? 'border-blue-500 bg-blue-50 cursor-wait'
            : type === 'inflow' 
              ? isConnected
                ? 'bg-green-500 border-white cursor-pointer hover:bg-green-600 hover:scale-110'
                : 'bg-white border-gray-500 cursor-pointer hover:bg-gray-50 hover:scale-110'
              : isSelected
                ? 'bg-red-500 border-white cursor-pointer hover:bg-red-600'
                : isConnected
                  ? 'bg-green-500 border-white cursor-pointer hover:bg-green-600'
                  : 'bg-gray-500 border-white cursor-pointer hover:bg-gray-600'
        }`}
        onClick={handlePortClick}
        title={
          isConnecting ? 'Connecting...' : isConnected 
            ? `Connected ${type} - click to disconnect`
            : type === 'inflow' ? 'Input connection' : 'Output connection'
        }
      >
        {isConnecting && (
          <div className="w-2.5 h-2.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
    );
  };

  // Handle creating connection in linear view - save to DB first, then update UI
  const handleLinearConnection = async (
    fromNodeId: string,
    toNodeId: string,
    crmBranch?: 'left' | 'right',
    triggersSnapshot?: TriggerNodeType[],
    actionsSnapshot?: ActionNodeType[]
  ): Promise<WireConnection | undefined> => {
    const trig = triggersSnapshot ?? triggers;
    const acts = actionsSnapshot ?? actions;

    const existingConnection = wireConnections.find(
      (conn) =>
        conn.fromNodeId === fromNodeId &&
        conn.toNodeId === toNodeId &&
        (conn.crm_branch || null) === (crmBranch || null)
    );

    if (existingConnection) {
      console.log('Connection already exists');
      return existingConnection;
    }
    
    setConnectingBetween({ fromNodeId, toNodeId });
    setSelectedOutflowPort(null);
    
    try {
      setIsSavingImmediately(true);
      
      const persistContext = getActivePersistContext();
      if (!persistContext) {
        throw new Error('No automation selected');
      }

      const { automationId } = persistContext;
      
      const [triggerIdMap, actionIdMap] = await Promise.all([
        saveAutomationTriggers(automationId, trig),
        saveAutomationActions(automationId, acts)
      ]);

      if (!isPersistContextValid(persistContext)) return;

      const allIdMap = new Map<string, string>([...triggerIdMap, ...actionIdMap]);
      
      const updatedTriggers = trig.map(t => {
        const dbId = allIdMap.get(t.id);
        return dbId && dbId !== t.id ? { ...t, id: dbId } : t;
      });
      const updatedActions = acts.map(a => {
        const dbId = allIdMap.get(a.id);
        return dbId && dbId !== a.id ? { ...a, id: dbId } : a;
      });
      if (updatedTriggers.some((t, i) => t.id !== trig[i]?.id) || updatedActions.some((a, i) => a.id !== acts[i]?.id)) {
        if (!isPersistContextValid(persistContext)) return;
        setTriggers(updatedTriggers);
        setActions(updatedActions);
      }
      
      const findActualNodeId = (tempId: string): string => {
        if (tempId.length === 36 && tempId.includes('-')) return allIdMap.get(tempId) || tempId;
        const dbId = allIdMap.get(tempId);
        if (dbId) return dbId;
        if (trig.find(t => t.id === tempId) || acts.find(a => a.id === tempId)) return tempId;
        return tempId;
      };
      
      const actualFromNodeId = findActualNodeId(fromNodeId);
      const actualToNodeId = findActualNodeId(toNodeId);

      if (!isPersistContextValid(persistContext)) return;
      
      const createdConnection = await createAutomationConnection({
        automation_id: automationId,
        source_node_id: actualFromNodeId,
        target_node_id: actualToNodeId,
        ...(crmBranch ? { crm_branch: crmBranch } : {})
      });

      if (!isPersistContextValid(persistContext)) return;

      const newConnection: WireConnection = {
        id: createdConnection.id,
        fromNodeId: actualFromNodeId,
        toNodeId: actualToNodeId,
        fromPort: 'outflow',
        toPort: 'inflow',
        crm_branch: crmBranch
      };
      
      setWireConnections(prev => [...prev, newConnection]);
      console.log(`🔗 Connected ${fromNodeId} → ${toNodeId} (saved)`);
      return newConnection;
    } catch (err: any) {
      console.error('Error creating linear connection:', err);
      setError(err?.message || 'Failed to save connection');
      return undefined;
    } finally {
      setConnectingBetween(null);
      setIsSavingImmediately(false);
    }
  };

  // Handle disconnecting in linear view
  const handleLinearDisconnect = (nodeId: string, portType: 'inflow' | 'outflow') => {
    if (portType === 'inflow') {
      // Remove connection where this node is the target
      setWireConnections(prev => prev.filter(conn => conn.toNodeId !== nodeId));
    } else {
      // Remove connection where this node is the source
      setWireConnections(prev => prev.filter(conn => conn.fromNodeId !== nodeId));
    }
    
    setDisconnectPopup(null);
    console.log(`🔗 Disconnected ${nodeId} (${portType})`);
  };

  // Render main content based on device type
  const renderWorkflowLoadingState = () => (
    <div className="flex flex-col items-center justify-center flex-1 py-24 min-h-[280px]">
      <div className="w-12 h-12 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="mt-4 text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>
        Loading workflow...
      </p>
    </div>
  );

  const renderMainContent = () => {
  if (isMobile) {
    return (
        <div 
          className="min-h-screen bg-gray-50" 
          data-automations-page
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}>
          
          {selectedAutomation ? (
            /* Mobile Automation Detail View */
            <div className="flex flex-col h-screen">
              {/* Mobile Header with Back Button */}
              <div className="bg-white border-b border-gray-200/60 px-4 py-4 sticky top-0 z-10" style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleClearSelectedAutomation}
                    className="p-2 -ml-2 active:bg-gray-100 rounded-[10px] transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent', minHeight: '44px', minWidth: '44px' }}
                  >
                    <ArrowLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>{selectedAutomation.name}</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className={`w-2 h-2 rounded-full ${selectedAutomation.is_live ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>{selectedAutomation.is_live ? 'Live' : 'Draft'}</span>
          </div>
            </div>
                  <button
                    onClick={handleDeleteAutomation}
                    className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation shrink-0"
                    title="Delete Automation"
                    style={{ WebkitTapHighlightColor: 'transparent', minHeight: '44px', minWidth: '44px' }}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
          </div>
                {/* Action row: full-width, thumb-friendly buttons */}
                <div className="flex items-center gap-2 mt-2.5">
                  <button
                    onClick={handleAiEditClick}
                    className="flex-1 px-3 py-2.5 rounded-[10px] text-[13px] font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 active:from-blue-700 active:to-indigo-700 text-white transition-all duration-150 touch-manipulation flex items-center justify-center gap-1.5"
                    style={{ minHeight: '40px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                  >
                    <Sparkles className="h-4 w-4" />
                    Edit with AI
                  </button>
                  <button
                    onClick={handleToggleLive}
                    className={`flex-1 px-3 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all duration-150 touch-manipulation ${
                      selectedAutomation.is_live
                        ? 'bg-red-600 active:bg-red-700 text-white'
                        : 'bg-green-600 active:bg-green-700 text-white'
                    }`}
                    style={{ 
                      minHeight: '40px',
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {selectedAutomation.is_live ? 'Turn Off' : 'Go Live'}
                  </button>
                </div>
              </div>

              {/* Mobile Tab Navigation */}
              <div className="bg-white border-b border-gray-200/60 px-4">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('workflow-new')}
                    className={`flex-1 px-4 py-3 text-[15px] font-semibold border-b-2 transition-all duration-150 touch-manipulation ${
                      activeTab === 'workflow-new'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 active:text-blue-600'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em',
                      minHeight: '44px'
                    }}
                  >
                    Workflow
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`flex-1 px-4 py-3 text-[15px] font-semibold border-b-2 transition-all duration-150 touch-manipulation ${
                      activeTab === 'history'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 active:text-blue-600'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em',
                      minHeight: '44px'
                    }}
                  >
                    History
                  </button>
                </div>
              </div>

              {/* Mobile Content Area */}
              <div className="flex-1 bg-gray-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                {activeTab === 'workflow-new' ? (
                  isWorkflowLoading ? (
                    renderWorkflowLoadingState()
                  ) : (
                    <div className="p-4">
                      {renderLinearWorkflow()}
                    </div>
                  )
                ) : (
                  /* Execution History */
                  <div className="p-4">
                    {executionSummary && (
                      /* Summary Stats */
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-blue-200/50">
                          <div className="text-xl font-bold text-blue-600" style={{ letterSpacing: '-0.01em' }}>
                            {executionSummary.running_executions}
                          </div>
                          <div className="text-[11px] text-gray-600 font-medium mt-1" style={{ letterSpacing: '-0.01em' }}>Running</div>
                        </div>
                        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-green-200/50">
                          <div className="text-xl font-bold text-green-600" style={{ letterSpacing: '-0.01em' }}>
                            {executionSummary.successful_executions}
                          </div>
                          <div className="text-[11px] text-gray-600 font-medium mt-1" style={{ letterSpacing: '-0.01em' }}>Successful</div>
                        </div>
                        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-red-200/50">
                          <div className="text-xl font-bold text-red-600" style={{ letterSpacing: '-0.01em' }}>
                            {executionSummary.failed_executions}
                          </div>
                          <div className="text-[11px] text-gray-600 font-medium mt-1" style={{ letterSpacing: '-0.01em' }}>Failed</div>
                        </div>
                      </div>
                    )}

                    {/* Execution List */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="p-4 border-b border-gray-200/60">
                        <h3 className="font-semibold text-gray-900 text-[15px]" style={{ letterSpacing: '-0.01em' }}>Recent Executions</h3>
                      </div>
                      <div className="divide-y divide-gray-200/60">
                        {executions.length === 0 ? (
                          <div className="p-6 text-center text-gray-500">
                            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-[15px] font-medium text-gray-900 mb-1" style={{ letterSpacing: '-0.01em' }}>No execution history yet.</p>
                            <p className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                              Executions will appear here when the automation runs.
                            </p>
                          </div>
                        ) : (
                          executions.map((execution) => {
                            const actualStatus = getActualExecutionStatus(execution);
                            return (
                              <div key={execution.id} className="p-4 active:bg-gray-50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {getStatusIcon(actualStatus)}
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-gray-900 text-[15px] capitalize" style={{ letterSpacing: '-0.01em' }}>
                                        {actualStatus}
                                      </div>
                                      <div className="text-[13px] text-gray-600 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                                        {formatRelativeTime(execution.started_at)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="text-right">
                                      <div className="text-[13px] font-medium text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                        {execution.executed_actions}/{execution.total_actions} actions
                                      </div>
                                      {execution.completed_at && (
                                        <div className="text-[11px] text-gray-500 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                                          {formatDuration(
                                            new Date(execution.completed_at).getTime() - 
                                            new Date(execution.started_at).getTime()
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleViewExecution(execution.id)}
                                      className="px-3 py-1.5 text-[13px] bg-blue-100 active:bg-blue-200 text-blue-700 rounded-[10px] transition-all duration-150 touch-manipulation flex items-center gap-1"
                                      style={{ 
                                        WebkitTapHighlightColor: 'transparent',
                                        minHeight: '36px',
                                        letterSpacing: '-0.01em'
                                      }}
                                    >
                                      <Eye className="h-3 w-3" />
                                      Details
                                    </button>
                                  </div>
                                </div>
                                {execution.error_message && (
                                  <div className="mt-2 text-[13px] text-red-600 bg-red-50 p-2 rounded-[8px]" style={{ letterSpacing: '-0.01em' }}>
                                    {execution.error_message}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Mobile Menu View */
            <div className="flex flex-col h-screen bg-gray-50">
              {/* Mobile Menu Header */}
              <div className="p-4 sm:p-6 border-b border-gray-200/60 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Automations</h2>
                    <p className="text-sm text-gray-600" style={{ letterSpacing: '-0.01em' }}>Workflow Management</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setIsCreateMenuOpen(true)}
                      className="w-10 h-10 bg-blue-600 active:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-sm transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      title="Create"
                      data-assistant-anchor="automations-create"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setIsSettingsOpen(true)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Settings"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Automation & Agent List */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <div className="mb-3">
                  <select
                    value={listFilter}
                    onChange={(e) => setListFilter(e.target.value as 'all' | 'agents' | 'automations')}
                    className="w-full appearance-none bg-white border border-gray-200/60 rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    <option value="all">All</option>
                    <option value="agents">Agents</option>
                    <option value="automations">Automations</option>
                  </select>
                </div>
                {loading && automations.length === 0 && agentsList.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading automations...</p>
                  </div>
                ) : visibleAutomationsList.length === 0 && visibleAgentsList.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="w-16 h-16 rounded-[12px] bg-blue-100 flex items-center justify-center mx-auto mb-4">
                      <Zap className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>
                      {listFilter === 'agents' ? 'No agents yet' : 'No automations yet'}
                    </h3>
                    <p className="text-[15px] text-gray-600 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                      {listFilter === 'agents'
                        ? 'Create your first agent to get started.'
                        : 'Create your first automation to get started.'}
          </p>
        </div>
                ) : (
                  <div className="space-y-2">
                    {visibleAgentsList.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => navigate(`/automations/agents/${agent.id}`)}
                        className="w-full text-left p-3.5 rounded-[12px] bg-white border border-gray-200/60 text-gray-900 active:bg-gray-50 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 touch-manipulation"
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <div className="w-7 h-7 rounded-[8px] bg-gray-900 flex items-center justify-center flex-shrink-0">
                              <Bot className="h-4 w-4 text-white" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[15px] font-semibold truncate text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                {agent.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Agent · {agent.kind === 'feedback' ? 'Feedback' : 'Receptionist'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              agent.status === 'live' ? 'bg-green-500' : 'bg-gray-300'
                            }`}></div>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </button>
                    ))}
                    {visibleAutomationsList.map((automation) => {
                      const isActive = selectedAutomation?.id === automation.id;
                      return (
                        <button
                          key={automation.id}
                          onClick={() => handleAutomationClick(automation)}
                          className={`w-full text-left p-3.5 rounded-[12px] transition-all duration-150 touch-manipulation ${
                            isActive
                              ? 'bg-blue-50 border border-blue-200 text-blue-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                              : 'bg-white border border-gray-200/60 text-gray-900 active:bg-gray-50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                          }`}
                          style={{ 
                            WebkitTapHighlightColor: 'transparent',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className={`text-[15px] font-semibold truncate ${
                                isActive ? 'text-blue-900' : 'text-gray-900'
                              }`} style={{ letterSpacing: '-0.01em' }}>
                                {automation.name}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                automation.is_live ? 'bg-green-500' : 'bg-gray-300'
                              }`}></div>
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    );
  }

    // Desktop view
  return (
      <div 
        className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" 
        data-automations-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200/60 flex flex-col shadow-sm">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200/60">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Automations</h2>
              <p className="text-sm text-gray-600" style={{ letterSpacing: '-0.01em' }}>Workflow Management</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setIsCreateMenuOpen(true)}
                className="w-10 h-10 bg-blue-600 hover:bg-blue-700 active:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-sm transition-colors"
                title="Create"
                data-assistant-anchor="automations-create"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Settings"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Automation & Agent List */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="mb-3">
            <select
              value={listFilter}
              onChange={(e) => setListFilter(e.target.value as 'all' | 'agents' | 'automations')}
              className="w-full appearance-none bg-white border border-gray-200/60 rounded-[10px] px-3.5 py-2.5 text-[15px] font-semibold text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus:ring-2 focus:ring-blue-500/40 cursor-pointer"
              style={{ letterSpacing: '-0.01em' }}
            >
              <option value="all">All</option>
              <option value="agents">Agents</option>
              <option value="automations">Automations</option>
            </select>
          </div>
          {loading && automations.length === 0 && agentsList.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading automations...</p>
            </div>
          ) : visibleAutomationsList.length === 0 && visibleAgentsList.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-[12px] bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>
                {listFilter === 'agents' ? 'No agents yet' : 'No automations yet'}
              </h3>
              <p className="text-[15px] text-gray-600 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                {listFilter === 'agents'
                  ? 'Create your first agent to get started.'
                  : 'Create your first automation to get started.'}
              </p>
            </div>
          ) : (
            <nav className="space-y-2">
              {visibleAgentsList.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => navigate(`/automations/agents/${agent.id}`)}
                  className="w-full text-left p-3.5 rounded-[12px] bg-white border border-gray-200/60 text-gray-900 hover:bg-gray-50 active:bg-gray-50 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 touch-manipulation"
                  style={{
                    WebkitTapHighlightColor: 'transparent',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className="w-7 h-7 rounded-[8px] bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold truncate text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                          {agent.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          Agent · {agent.kind === 'feedback' ? 'Feedback' : 'Receptionist'}
                        </div>
                      </div>
                    </div>
                    <div className={`w-2.5 h-2.5 rounded-full ml-2 flex-shrink-0 ${
                      agent.status === 'live' ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                  </div>
                </button>
              ))}
              {visibleAutomationsList.map((automation) => {
                const isActive = selectedAutomation?.id === automation.id;
                return (
                  <button
                  key={automation.id}
                  onClick={() => handleAutomationClick(automation)}
                    className={`w-full text-left p-3.5 rounded-[12px] transition-all duration-150 touch-manipulation ${
                      isActive
                        ? 'bg-blue-50 border border-blue-200 text-blue-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                        : 'bg-white border border-gray-200/60 text-gray-900 active:bg-gray-50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className={`text-[15px] font-semibold truncate ${
                          isActive ? 'text-blue-900' : 'text-gray-900'
                        }`} style={{ letterSpacing: '-0.01em' }}>
                        {automation.name}
                    </div>
                      </div>
                      <div className={`w-2.5 h-2.5 rounded-full ml-2 flex-shrink-0 ${
                        automation.is_live 
                          ? 'bg-green-500' 
                          : 'bg-gray-300'
                      }`}></div>
                      </div>
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {selectedAutomation ? (
          <>
            {/* Header with automation info and tabs */}
            <div className="bg-white border-b border-gray-200/60 px-4 md:px-6 py-4 sticky top-0 z-30" style={{
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}>
              <div className="pb-4 border-b border-gray-200/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                          {selectedAutomation.name}
                        </h2>
                        <button
                          onClick={handleEditTitle}
                          className="text-gray-400 active:text-blue-600 transition-all duration-150 p-1.5 active:bg-gray-100 rounded-[10px] touch-manipulation"
                          title="Edit title"
                          style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px', minWidth: '36px' }}
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      </div>

                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleAiEditClick}
                      className="px-3 lg:px-4 py-2.5 rounded-[10px] text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-sm bg-gradient-to-r from-blue-600 to-indigo-600 active:from-blue-700 active:to-indigo-700 text-white flex items-center gap-1.5"
                      style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                      title="Edit this workflow with AI"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span className="hidden lg:inline">Edit with AI</span>
                    </button>
                    <button
                      onClick={handleToggleLive}
                      className={`px-4 py-2.5 rounded-[10px] text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-sm ${
                        selectedAutomation.is_live
                          ? 'bg-red-600 active:bg-red-700 text-white'
                          : 'bg-green-600 active:bg-green-700 text-white'
                      }`}
                      style={{ 
                        minHeight: '36px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {selectedAutomation.is_live ? 'Turn Off' : 'Go Live'}
                    </button>
                    <button
                      onClick={handleDeleteAutomation}
                      className="p-2.5 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                      title="Delete automation"
                      style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px', minWidth: '36px' }}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200/60">
                <button
                  onClick={() => setActiveTab('workflow-new')}
                  className={`px-4 py-3 text-[15px] font-semibold border-b-2 transition-all duration-150 touch-manipulation ${
                    activeTab === 'workflow-new'
                      ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                      : 'border-transparent text-gray-600 active:text-blue-600 active:bg-gray-50'
                  }`}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    minHeight: '44px'
                  }}
                >
                  <Zap className="h-4 w-4 inline mr-2" />
                  Workflow
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-3 text-[15px] font-semibold border-b-2 transition-all duration-150 touch-manipulation ${
                    activeTab === 'history'
                      ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                      : 'border-transparent text-gray-600 active:text-blue-600 active:bg-gray-50'
                  }`}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    minHeight: '44px'
                  }}
                >
                  <History className="h-4 w-4 inline mr-2" />
                  History
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'workflow' ? (
              isWorkflowLoading ? (
                renderWorkflowLoadingState()
              ) : (
              /* Workflow Canvas */
              <div className="flex-1 relative bg-gradient-to-br from-gray-50/50 to-white/50">
                {/* Canvas Controls */}
                <div className="absolute top-6 left-6 z-10">
                  <button
                    onClick={handleAddTrigger}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center gap-2 shadow-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/20"
                  >
                    <Plus className="h-4 w-4" />
                    Add Trigger
                  </button>
                </div>

                <div className="absolute top-6 left-48 z-10">
                  <button
                    onClick={handleAddAction}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2.5 rounded-2xl font-semibold flex items-center gap-2 shadow-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/20"
                  >
                    <Plus className="h-4 w-4" />
                    Add Action
                  </button>
                </div>

                {/* Dotted Canvas */}
                <div 
                  ref={canvasRef}
                  data-canvas
                  className="w-full h-full relative overflow-hidden"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                >
                  {/* Trigger Nodes */}
                  {triggers.map((trigger) => (
                    <TriggerNode
                      key={trigger.id}
                      trigger={trigger}
                      onMouseDown={handleMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleTriggerSettings}
                      onWireDropped={handleWireDropped}
                      isConnectingOutflow={connectingBetween?.fromNodeId === trigger.id}
                    />
                  ))}

                  {/* Action Nodes */}
                  {actions.map((action) => (
                    <ActionNode
                      key={action.id}
                      action={action}
                      onMouseDown={handleActionMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleActionSettingsClick}
                      onWireDropped={handleWireDropped}
                      isConnectingInflow={connectingBetween?.toNodeId === action.id}
                      isConnectingOutflow={connectingBetween?.fromNodeId === action.id}
                    />
                  ))}

                  {/* Wire being dragged */}
                  {wireState.isDragging && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 1000,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {/* Connection wire */}
                      <line
                        x1={wireState.startPosition.x}
                        y1={wireState.startPosition.y}
                        x2={wireState.currentPosition.x || wireState.startPosition.x}
                        y2={wireState.currentPosition.y || wireState.startPosition.y}
                        stroke="#6b7280"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        opacity="0.8"
                      />
                      
                      {/* Start point indicator */}
                      <circle
                        cx={wireState.startPosition.x}
                        cy={wireState.startPosition.y}
                        r="4"
                        fill="#6b7280"
                        opacity="0.9"
                      />
                      
                      {/* End point indicator */}
                      {wireState.currentPosition.x && wireState.currentPosition.y && (
                        <circle
                          cx={wireState.currentPosition.x}
                          cy={wireState.currentPosition.y}
                          r="3"
                          fill="#ffffff"
                          stroke="#6b7280"
                          strokeWidth="2"
                          opacity="0.9"
                        />
                      )}
                    </svg>
                  )}

                  {/* Permanent wire connections */}
                  {wireConnections.length > 0 && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 500,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="permanent-arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {wireConnections.map((connection) => {
                        const fromPos = getNodePosition(connection.fromNodeId, 'outflow');
                        const toPos = getNodePosition(connection.toNodeId, 'inflow');
                        
                        return (
                          <g key={connection.id}>
                            {/* Connection line */}
                            <line
                              x1={fromPos.x}
                              y1={fromPos.y}
                              x2={toPos.x}
                              y2={toPos.y}
                              stroke="#6b7280"
                              strokeWidth="3"
                              opacity="0.8"
                            />
                            
                            {/* Connection points */}
                            <circle
                              cx={fromPos.x}
                              cy={fromPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                            <circle
                              cx={toPos.x}
                              cy={toPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                          </g>
                        );
                      })}
                    </svg>
                  )}

                  {/* Render nodes */}
                </div>
              </div>
              )
            ) : activeTab === 'workflow-new' ? (
              isWorkflowLoading ? (
                renderWorkflowLoadingState()
              ) : (
              /* Workflow New View - Linear Layout */
              <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 overflow-y-auto relative p-4 md:p-6">
                <div className="max-w-4xl mx-auto p-6">
                  {/* Linear Workflow Container */}
                  <div className="space-y-6">
                    {/* Render Linear Workflow */}
                    {renderLinearWorkflow()}
                  </div>
                </div>

                {/* Disconnect Popup */}
                {disconnectPopup && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-[99999]" 
                      onClick={() => setDisconnectPopup(null)}
                    />
                    
                    {/* Popup */}
                    <div
                      className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-xl p-3"
                      style={{
                        left: disconnectPopup.position.x - 50,
                        top: disconnectPopup.position.y - 40,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <button
                        onClick={() => handleLinearDisconnect(disconnectPopup.nodeId, disconnectPopup.portType)}
                        className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors text-sm font-medium"
                      >
                        <X className="h-4 w-4" />
                        Disconnect
                      </button>
                    </div>
                  </>
                )}
              </div>
              )
            ) : (
              /* Execution History */
              <div className="flex-1 bg-gradient-to-br from-gray-50/50 to-white/50 p-6">
                {executionSummary && (
                  /* Summary Stats */
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-blue-600">
                        {executionSummary.running_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Running</div>
                    </div>
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-green-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-green-600">
                        {executionSummary.successful_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Successful</div>
                    </div>
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl border border-red-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105" style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}>
                      <div className="text-2xl font-bold text-red-600">
                        {executionSummary.failed_executions}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Failed</div>
                    </div>

                  </div>
                )}

                {/* Execution List */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-medium text-gray-900">Recent Executions</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {executions.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No execution history yet.
                        <br />
                        Executions will appear here when the automation runs.
                      </div>
                    ) : (
                      executions.map((execution) => {
                        const actualStatus = getActualExecutionStatus(execution);
                        return (
                          <div key={execution.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(actualStatus)}
                                <div>
                                  <div className="font-medium text-gray-900 capitalize">
                                    {actualStatus}
                                  </div>
                                <div className="text-sm text-gray-500">
                                  {formatRelativeTime(execution.started_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm text-gray-900">
                                  {execution.executed_actions}/{execution.total_actions} actions
                                </div>
                                {execution.completed_at && (
                                  <div className="text-sm text-gray-500">
                                    {formatDuration(
                                      new Date(execution.completed_at).getTime() - 
                                      new Date(execution.started_at).getTime()
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleViewExecution(execution.id)}
                                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                              >
                                <Eye className="h-3 w-3" />
                                Details
                              </button>
                            </div>
                          </div>
                          {execution.error_message && (
                            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                              {execution.error_message}
                            </div>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Zap className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                No automation selected
              </h3>
              <p className="text-gray-600 leading-relaxed max-w-md">
                Choose an automation from the sidebar to view and edit its workflow.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}




      {/* Execution Details Modal */}
      {isExecutionDetailsOpen && selectedExecutionId && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-5/6">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Execution Details</h2>
              <button
                onClick={handleCloseExecutionDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 h-full overflow-y-auto">
              {loadingSteps ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading execution details...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Execution Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Execution Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            const actualStatus = execution ? getActualExecutionStatus(execution) : 'pending';
                            return (
                              <>
                                {getStatusIcon(actualStatus)}
                                <span className="font-medium capitalize">
                                  {actualStatus}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Started</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.started_at && 
                            new Date(executions.find(e => e.id.toString() === selectedExecutionId)!.started_at).toLocaleString()
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="font-medium mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            if (execution?.completed_at) {
                              return formatDuration(
                                new Date(execution.completed_at).getTime() - 
                                new Date(execution.started_at).getTime()
                              );
                            }
                            return 'Running...';
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Actions</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.executed_actions || 0}/
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.total_actions || 0}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Context Data */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Booking Information</h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      {(() => {
                        const execution = executions.find(e => e.id === selectedExecutionId);
                        const contextData = execution?.context_data;
                        const bookingData = contextData?.booking_data;

                        if (!bookingData) {
                          return (
                            <div className="text-gray-500 text-center py-4">
                              No booking data available for this execution
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            {/* Basic Booking Info */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-sm text-gray-500">Booking ID</div>
                                <div className="font-medium">{bookingData.booking_id || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Title</div>
                                <div className="font-medium">{bookingData.title || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Grand Total</div>
                                <div className="font-medium">${bookingData.grand_total || 0}</div>
                              </div>
                            </div>

                            {/* Customer Info */}
                            {bookingData.customer && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Customer</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Name</div>
                                    <div className="text-sm">{bookingData.customer.full_name || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Email</div>
                                    <div className="text-sm">{bookingData.customer.email || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Phone</div>
                                    <div className="text-sm">{bookingData.customer.phone || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Schedule Info */}
                            {bookingData.schedule && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Schedule</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Drop Off</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.drop_off_date || 'N/A'} {bookingData.schedule.drop_off_time || ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Start</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.start_date || 'N/A'} {bookingData.schedule.start_time || ''}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Services */}
                            {bookingData.services && bookingData.services.length > 0 && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Services</div>
                                <div className="space-y-2">
                                  {bookingData.services.map((service: any, index: number) => (
                                    <div key={index} className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="font-medium text-sm">{service.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {service.vehicle?.year} {service.vehicle?.make} {service.vehicle?.model}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium">${service.price}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Raw Context Data (collapsible) */}
                            <details className="border-t pt-4">
                              <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                                Raw Context Data (Advanced)
                              </summary>
                              <div className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-64">
                                <pre>{JSON.stringify(contextData, null, 2)}</pre>
                              </div>
                            </details>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Automation Flow Progress */}
                  <div>
                    <AutomationStepTimeline
                      steps={executionSteps as any}
                      execution={executions.find(e => e.id.toString() === selectedExecutionId)}
                    />
                    {false && (() => {
                      const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                      if (!execution) return null;

                      // Get all automation actions in order (using position.y as order since ActionNode doesn't have order)
                      const allActions = [...actions].sort((a, b) => a.position.y - b.position.y);
                      
                      if (allActions.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            No automation steps configured.
                          </div>
                        );
                      }

                      // Create a map of execution steps by action_id for quick lookup
                      const executionStepMap = new Map();
                      executionSteps.forEach(step => {
                        if (step.action_id) {
                          executionStepMap.set(step.action_id, step);
                        }
                      });

                      return (
                        <div className="space-y-4">
                          {/* Progress Bar */}
                          <div className="bg-gray-200 rounded-full h-2 mb-6">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ 
                                width: `${Math.max(5, (execution.executed_actions / Math.max(execution.total_actions, 1)) * 100)}%` 
                              }}
                            ></div>
                          </div>

                          {/* Step Flow */}
                          <div className="relative">
                            {allActions.map((action, index) => {
                              const executionStep = executionStepMap.get(action.id);
                              const isExecuted = !!executionStep;
                              const isCurrentStep = index === execution.executed_actions && !isExecuted;

                              
                              // Determine step status
                              let stepStatus = 'pending';
                              let stepIcon = <Clock className="h-4 w-4 text-gray-400" />;
                              let stepBgColor = 'bg-gray-100';
                              let stepBorderColor = 'border-gray-200';
                              let stepTextColor = 'text-gray-600';
                              
                              if (executionStep) {
                                stepStatus = executionStep.execution_status;
                                if (stepStatus === 'completed') {
                                  stepIcon = <CheckCircle className="h-4 w-4 text-green-600" />;
                                  stepBgColor = 'bg-green-50';
                                  stepBorderColor = 'border-green-200';
                                  stepTextColor = 'text-green-800';
                                } else if (stepStatus === 'failed') {
                                  stepIcon = <XCircle className="h-4 w-4 text-red-600" />;
                                  stepBgColor = 'bg-red-50';
                                  stepBorderColor = 'border-red-200';
                                  stepTextColor = 'text-red-800';
                                } else if (stepStatus === 'running') {
                                  stepIcon = <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
                                  stepBgColor = 'bg-blue-50';
                                  stepBorderColor = 'border-blue-200';
                                  stepTextColor = 'text-blue-800';
                                }
                              } else if (isCurrentStep) {
                                stepStatus = 'current';
                                stepIcon = <Clock className="h-4 w-4 text-blue-600 animate-pulse" />;
                                stepBgColor = 'bg-blue-50';
                                stepBorderColor = 'border-blue-300';
                                stepTextColor = 'text-blue-800';
                              }

                              return (
                                <div key={action.id} className="relative">
                                  {/* Connector Line */}
                                  {index < allActions.length - 1 && (
                                    <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200"></div>
                                  )}
                                  
                                  <div className={`relative flex items-start gap-4 p-4 rounded-lg border ${stepBorderColor} ${stepBgColor} transition-all duration-300`}>
                                    {/* Step Number & Icon */}
                                    <div className="flex-shrink-0 flex flex-col items-center">
                                      <div className={`w-8 h-8 rounded-full border-2 ${stepBorderColor} ${stepBgColor} flex items-center justify-center font-medium text-sm ${stepTextColor}`}>
                                        {isExecuted || isCurrentStep ? stepIcon : (index + 1)}
                                      </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-2">
                                        <div>
                                          <h4 className={`font-medium ${stepTextColor}`}>
                                            {action.type === 'email' && '📧 Send Email'}
                                            {action.type === 'sms' && '💬 Send SMS'}
                                            {action.type === 'wait' && '⏱️ Wait'}
                                            {action.type === 'webhook' && '🔗 Webhook'}
                                            {action.type === 'update_booking' && '📝 Update Booking'}
                                            {action.type === 'create_lead' && '🎯 Create Lead'}
                                            {action.type === 'conditional' && '🔀 Conditional'}
                                            {!['email', 'sms', 'wait', 'webhook', 'update_booking', 'create_lead', 'conditional'].includes(action.type) && 
                                              `${action.type.charAt(0).toUpperCase() + action.type.slice(1)} Action`
                                            }
                                          </h4>
                                          <div className="text-sm text-gray-500 space-y-1">
                                            {action.type === 'email' && (
                                              <div>
                                                <div>To: {action.config?.recipient || 'Customer'}</div>
                                                {action.config?.subject && <div>Subject: {action.config.subject}</div>}
                                              </div>
                                            )}
                                            {action.type === 'sms' && (
                                              <div>
                                                <div>To: {action.config?.recipient || 'Customer'}</div>
                                                {action.config?.message && (
                                                  <div className="truncate max-w-xs">Message: "{action.config.message}"</div>
                                                )}
                                                {executionStep?.result_data?.scheduled_for && (
                                                  <div className="text-blue-600 font-medium">
                                                    📅 Scheduled: {new Date(executionStep.result_data.scheduled_for).toLocaleString()}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {action.type === 'wait' && (
                                              <div>
                                                <div>Duration: {action.config?.duration || '1'} {action.config?.unit || 'minutes'}</div>
                                                {executionStep?.started_at && (
                                                  <div className="text-blue-600 font-medium">
                                                    ⏰ Completes: {new Date(
                                                      new Date(executionStep.started_at).getTime() + 
                                                      (parseInt(action.config?.duration || '1') * 
                                                       (action.config?.unit === 'hours' ? 3600000 : 
                                                        action.config?.unit === 'days' ? 86400000 : 60000))
                                                    ).toLocaleString()}
                                                  </div>
                                                )}
                                                {!executionStep && stepStatus === 'current' && (
                                                  <div className="text-orange-600 font-medium">
                                                    ⏳ Will start after previous step completes
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {action.type === 'webhook' && (
                                              <div>
                                                <div>URL: {action.config?.url || 'Not configured'}</div>
                                                {action.config?.method && <div>Method: {action.config.method}</div>}
                                              </div>
                                            )}
                                            {action.type === 'update_booking' && (
                                              <div>
                                                <div>Update booking properties</div>
                                                {action.config?.status && <div>Status: {action.config.status}</div>}
                                              </div>
                                            )}
                                            {action.type === 'create_lead' && (
                                              <div>
                                                <div>Create new lead</div>
                                                {action.config?.pipeline && <div>Pipeline: {action.config.pipeline}</div>}
                                              </div>
                                            )}
                                            {action.type === 'conditional' && (
                                              <div>
                                                <div>Conditional logic</div>
                                                {action.config?.condition && <div>Condition: {action.config.condition}</div>}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="flex flex-col items-end gap-1">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                              stepStatus === 'completed' ? 'bg-green-100 text-green-800' :
                                              stepStatus === 'failed' ? 'bg-red-100 text-red-800' :
                                              stepStatus === 'running' || stepStatus === 'current' ? 'bg-blue-100 text-blue-800' :
                                              'bg-gray-100 text-gray-600'
                                            }`}>
                                              {stepStatus === 'current' ? 'Next' : stepStatus.charAt(0).toUpperCase() + stepStatus.slice(1)}
                                            </span>
                                            {executionStep?.started_at && (
                                              <span className="text-xs text-gray-500">
                                                Started {formatRelativeTime(executionStep.started_at)}
                                              </span>
                                            )}
                                            {executionStep?.completed_at && (
                                              <span className="text-xs text-green-600">
                                                Completed {formatRelativeTime(executionStep.completed_at)}
                                              </span>
                                            )}
                                            {stepStatus === 'running' && executionStep?.started_at && (
                                              <span className="text-xs text-blue-600">
                                                Running for {(() => {
                                                  const duration = Date.now() - new Date(executionStep.started_at).getTime();
                                                  if (duration < 60000) return `${Math.floor(duration / 1000)}s`;
                                                  if (duration < 3600000) return `${Math.floor(duration / 60000)}m`;
                                                  return `${Math.floor(duration / 3600000)}h`;
                                                })()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Error Message */}
                                      {executionStep?.error_message && (
                                        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                                          <strong>Error:</strong> {executionStep.error_message}
                                        </div>
                                      )}

                                      {/* Result Data */}
                                      {executionStep?.result_data && Object.keys(executionStep.result_data).length > 0 && (
                                        <details className="mt-2">
                                          <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                                            View Result Data
                                          </summary>
                                          <div className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs overflow-auto max-h-32">
                                            <pre>{JSON.stringify(executionStep.result_data, null, 2)}</pre>
                                          </div>
                                        </details>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* REMOVED DUPLICATE Email Action Settings Modal */}
      {false && emailActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Email Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (emailActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== emailActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== emailActionSettingsModal.actionId && 
                        connection.toNodeId !== emailActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, useTemplate: false, selectedTemplateId: null }))}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      !emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom Message
                  </button>
                  <button
                    onClick={() => {
                      console.log('📧 Use Template button clicked!');
                      handleOpenTemplateSelector();
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Use Template
                  </button>
                </div>
                
                {/* Show selected template info */}
                {emailActionSettingsModal.useTemplate && emailActionSettingsModal.selectedTemplateId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="text-sm font-medium text-blue-900">
                      Template Selected: {emailTemplateSelector.templates.find(t => t.id === emailActionSettingsModal.selectedTemplateId)?.name || 'Unknown Template'}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Click "Use Template" again to change template
                    </div>
                  </div>
                )}
              </div>

              {/* Email Subject Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <input
                  ref={emailSubjectInputRef}
                  type="text"
                  value={emailActionSettingsModal.subject}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter the email subject line..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Subject Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Subject:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailSubjectVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Message
                </label>
                <div
                  contentEditable={!emailActionSettingsModal.useTemplate}
                  suppressContentEditableWarning
                  data-placeholder={emailActionSettingsModal.useTemplate ? "Template content will be used..." : "Enter the email message to send when this automation triggers..."}
                  className="w-full min-h-[180px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
                  onInput={(e) => {
                    emailIgnoreSyncRef.current = true;
                    const html = (e.target as HTMLDivElement).innerHTML;
                    setEmailActionSettingsModal(prev => ({ ...prev, message: html }));
                  }}
                />
                
                {/* Message Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Message:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailMessageVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                <p className="text-xs text-gray-500 mt-1">
                  Variables will be automatically replaced with actual data when the email is sent. Supports HTML formatting.
                </p>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send To
                </label>
                <select
                  value={emailActionSettingsModal.recipient}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select who to email...</option>
                  {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom email address input - only show when "custom" is selected */}
                {emailActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="email"
                    value={emailActionSettingsModal.customEmail || ''}
                    onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, customEmail: e.target.value }))}
                    placeholder="Enter custom email address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                  />
                )}
                
                {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').length === 1 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    💡 Connect this action to a trigger (like "Booking Created") to see more email options
                  </p>
                )}
              </div>

              {/* PDF Attachment - Available for both Custom Message and Use Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF Attachment (Optional)
                </label>
                <input
                  ref={pdfFileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handlePdfAttachmentChange}
                  disabled={emailActionSettingsModal.pdfUploading}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => pdfFileInputRef.current?.click()}
                    disabled={emailActionSettingsModal.pdfUploading}
                    className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {emailActionSettingsModal.pdfUploading ? 'Uploading...' : 'Add PDF'}
                  </button>
                  {emailActionSettingsModal.pdfAttachment && (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm text-gray-700 truncate" title={emailActionSettingsModal.pdfAttachment.filename}>
                        {emailActionSettingsModal.pdfAttachment.filename}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, pdfAttachment: null }))}
                        className="text-red-600 hover:text-red-800 text-sm font-medium shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Attach a PDF that will be included with every email sent by this action
                </p>
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === emailActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === emailActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Email', 'Booking Confirmation', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailMessage}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delay Action Settings Modal */}
      {false && delayActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Wait Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (delayActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== delayActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== delayActionSettingsModal.actionId && 
                        connection.toNodeId !== delayActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Wait Type Selection */}
            <div className="space-y-4">
              {(() => {
                const isCreatedTrigger = delayActionSettingsModal.actionId ? isConnectedToCreatedTrigger(delayActionSettingsModal.actionId) : false;
                
                return (
                  <>
                    {/* Wait Type Dropdown - only show if connected to 'created' trigger */}
                    {isCreatedTrigger && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Type
                        </label>
                        <select
                          value={delayActionSettingsModal.waitType}
                          onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                            ...prev, 
                            waitType: e.target.value as 'duration' | 'reminder'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="duration">Wait Duration</option>
                          <option value="reminder">Reminder</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose between a fixed wait duration or a scheduled reminder before appointment.
                        </p>
                      </div>
                    )}

                    {/* Duration Configuration - show for duration type or when not connected to created trigger */}
                    {(delayActionSettingsModal.waitType === 'duration' || !isCreatedTrigger) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Duration
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={delayActionSettingsModal.duration}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              duration: parseInt(e.target.value) || 1 
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <select
                            value={delayActionSettingsModal.unit}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              unit: e.target.value as 'minutes' | 'hours' | 'days' 
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The automation will pause for this duration before continuing to the next action.
                        </p>
                      </div>
                    )}

                    {/* Reminder Configuration - show only for reminder type */}
                    {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notify Time Before
                          </label>
                          <div className="flex gap-3 items-center">
                            <input
                              type="number"
                              min="1"
                              max="168"
                              value={delayActionSettingsModal.reminderHoursBefore}
                              onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                                ...prev, 
                                reminderHoursBefore: parseInt(e.target.value) || 1 
                              }))}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-sm text-gray-600">hours before</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Time
                          </label>
                          <select
                            value={delayActionSettingsModal.reminderReference}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderReference: e.target.value as 'drop_off_datetime' | 'start_datetime'
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="drop_off_datetime">Drop Off Date & Time</option>
                            <option value="start_datetime">Start Date & Time</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose which appointment time to calculate the reminder from.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-900">Preview:</div>
                      <div className="text-sm text-blue-700">
                        {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger ? (
                          <>
                            Send reminder {delayActionSettingsModal.reminderHoursBefore} hours before{' '}
                            {delayActionSettingsModal.reminderReference === 'drop_off_datetime' ? 'drop off' : 'start'} time
                            <br />
                            <span className="text-xs text-blue-600">
                              Example: For a 2 PM appointment, notification will be sent at{' '}
                              {delayActionSettingsModal.reminderHoursBefore === 24 ? '2 PM the day before' : 
                               delayActionSettingsModal.reminderHoursBefore >= 24 ? 
                               `${Math.floor(delayActionSettingsModal.reminderHoursBefore / 24)} days and ${delayActionSettingsModal.reminderHoursBefore % 24} hours before` :
                               `${2 - delayActionSettingsModal.reminderHoursBefore} ${delayActionSettingsModal.reminderHoursBefore === 1 ? 'PM' : delayActionSettingsModal.reminderHoursBefore === 2 ? 'PM' : 'AM'}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            Wait for {delayActionSettingsModal.duration} {delayActionSettingsModal.unit}
                            {(() => {
                              const duration = delayActionSettingsModal.duration;
                              const unit = delayActionSettingsModal.unit;
                              let totalMinutes = 0;
                              
                              if (unit === 'minutes') totalMinutes = duration;
                              else if (unit === 'hours') totalMinutes = duration * 60;
                              else if (unit === 'days') totalMinutes = duration * 24 * 60;
                              
                              if (totalMinutes < 60) {
                                return ` (${totalMinutes} minutes)`;
                              } else if (totalMinutes < 1440) {
                                return ` (${Math.round(totalMinutes / 60 * 10) / 10} hours)`;
                              } else {
                                return ` (${Math.round(totalMinutes / 1440 * 10) / 10} days)`;
                              }
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* UI Label Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const currentAction = actions.find(action => action.id === delayActionSettingsModal.actionId);
                          return currentAction?.config?.uiLabel || '';
                        })()}
                        onChange={(e) => {
                          const newUiLabel = e.target.value;
                          setActions(prev => prev.map(action => 
                            action.id === delayActionSettingsModal.actionId
                              ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                              : action
                          ));
                        }}
                        placeholder={
                          delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger 
                            ? "e.g. 'Appointment Reminder', '24hr Reminder', etc."
                            : "e.g. 'Cool Down Period', '24hr Delay', etc."
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a short label to help identify this action on the canvas
                      </p>
                    </div>

                    {/* Info Box */}
                    {!isCreatedTrigger && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">
                          <strong>💡 Tip:</strong> Connect this wait action to a "Booking Created" trigger to enable the reminder option for scheduled notifications before appointments!
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!delayActionSettingsModal.actionId || !selectedAutomation?.id) return;

                  try {
                    // Prepare configuration based on wait type
                    const waitConfig: any = {
                      wait_type: delayActionSettingsModal.waitType
                    };

                    if (delayActionSettingsModal.waitType === 'duration') {
                      // Convert to minutes for storage
                      let durationInMinutes = delayActionSettingsModal.duration;
                      if (delayActionSettingsModal.unit === 'hours') {
                        durationInMinutes = delayActionSettingsModal.duration * 60;
                      } else if (delayActionSettingsModal.unit === 'days') {
                        durationInMinutes = delayActionSettingsModal.duration * 24 * 60;
                      }

                      waitConfig.duration_minutes = durationInMinutes;
                      waitConfig.duration_type = delayActionSettingsModal.unit;
                      waitConfig.duration = durationInMinutes; // Legacy field
                      waitConfig.unit = delayActionSettingsModal.unit; // Legacy field
                      waitConfig.displayDuration = delayActionSettingsModal.duration; // Legacy field
                    } else if (delayActionSettingsModal.waitType === 'reminder') {
                      waitConfig.reminder_hours_before = delayActionSettingsModal.reminderHoursBefore;
                      waitConfig.reminder_reference = delayActionSettingsModal.reminderReference;
                    }

                    // Update the action with the new configuration
                    const updatedActions = actions.map(action => 
                      action.id === delayActionSettingsModal.actionId
                        ? { 
                            ...action, 
                            config: { 
                              ...action.config, 
                              ...waitConfig
                            }
                          }
                        : action
                    );

                    // Update local state
                    setActions(updatedActions);

                    // Save to database
                    const persistContext = getActivePersistContext();
                    if (!persistContext) {
                      setError('Unable to save — please try again.');
                      return;
                    }
                    await saveAutomationActions(persistContext.automationId, updatedActions);
                    
                    console.log('✅ Wait action configuration saved successfully');

                    // Close modal
                    setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                  } catch (error) {
                    console.error('❌ Error saving wait action configuration:', error);
                    setError('Failed to save wait action configuration');
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Check Action Settings Modal */}
      {false && checkActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Check Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (checkActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== checkActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== checkActionSettingsModal.actionId && 
                        connection.toNodeId !== checkActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setCheckActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        checkType: 'feedback',
                        ifCompleted: 'stop',
                        ifNotCompleted: 'continue'
                      });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setCheckActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    checkType: 'feedback',
                    ifCompleted: 'stop',
                    ifNotCompleted: 'continue'
                  })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Settings Content */}
            <div className="space-y-6">
                             {/* Check Type Selection */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Check Type
                 </label>
                 <select
                   value={checkActionSettingsModal.checkType}
                   onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                     ...prev, 
                     checkType: e.target.value as 'feedback' 
                   }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                 >
                   {(() => {
                     // Check if this action is connected to a workflow with a booking trigger
                     const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                       isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                     
                     if (isConnectedToBookingTrigger) {
                       return <option value="feedback">Feedback Status</option>;
                     } else {
                       return <option value="">No check types available</option>;
                     }
                   })()}
                 </select>
                 {(() => {
                   const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                     isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                   
                   if (!isConnectedToBookingTrigger) {
                     return (
                       <p className="text-xs text-gray-500 mt-1">
                         Connect this action to a workflow with a booking trigger to enable feedback checking.
                       </p>
                     );
                   }
                   return null;
                                })()}
             </div>

             {/* If Settings - only show when connected to booking trigger and feedback status is selected */}
             {(() => {
               const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                 isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
               
               if (isConnectedToBookingTrigger && checkActionSettingsModal.checkType === 'feedback') {
                 return (
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         If Feedback Completed
                       </label>
                       <select
                         value={checkActionSettingsModal.ifCompleted}
                         onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                           ...prev, 
                           ifCompleted: e.target.value as 'stop' | 'continue'
                         }))}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                       >
                         <option value="stop">Stop Automation</option>
                         <option value="continue">Continue to Next Action</option>
                       </select>
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         If Feedback Not Completed
                       </label>
                       <select
                         value={checkActionSettingsModal.ifNotCompleted}
                         onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                           ...prev, 
                           ifNotCompleted: e.target.value as 'stop' | 'continue'
                         }))}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                       >
                         <option value="stop">Stop Automation</option>
                         <option value="continue">Continue to Next Action</option>
                       </select>
                     </div>
                   </div>
                 );
               }
               return null;
             })()}

             {/* Feedback Info - only show when connected to booking trigger */}
               {(() => {
                 const isConnectedToBookingTrigger = checkActionSettingsModal.actionId ? 
                   isConnectedToCreatedTrigger(checkActionSettingsModal.actionId) : false;
                 
                 if (isConnectedToBookingTrigger) {
                   return null;
                 } else {
                   return (
                     <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                       <div className="flex items-start">
                         <div className="flex-shrink-0">
                           <Eye className="h-5 w-5 text-gray-600" />
                         </div>
                         <div className="ml-3">
                           <h4 className="text-sm font-medium text-gray-800">No Check Types Available</h4>
                           <p className="text-sm text-gray-700 mt-1">
                             This action needs to be connected to a workflow with a booking trigger to enable feedback checking.
                           </p>
                         </div>
                       </div>
                     </div>
                   );
                 }
               })()}


            </div>

            {/* Footer */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCheckActionSettingsModal({ 
                  isOpen: false, 
                  actionId: null, 
                  checkType: 'feedback'
                })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the check action configuration
                  if (checkActionSettingsModal.actionId) {
                    const isConnectedToBookingTrigger = isConnectedToCreatedTrigger(checkActionSettingsModal.actionId);
                    
                    if (isConnectedToBookingTrigger && checkActionSettingsModal.checkType === 'feedback') {
                      setActions(prev => prev.map(action => {
                        if (action.id === checkActionSettingsModal.actionId) {
                          return {
                            ...action,
                            config: {
                              checkType: checkActionSettingsModal.checkType,
                              ifCompleted: checkActionSettingsModal.ifCompleted,
                              ifNotCompleted: checkActionSettingsModal.ifNotCompleted,
                              uiLabel: 'Check Booking Feedback'
                            }
                          };
                        }
                        return action;
                      }));
                    }
                  }
                  
                  // Close modal
                  setCheckActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    checkType: 'feedback',
                    ifCompleted: 'stop',
                    ifNotCompleted: 'continue'
                  });
                }}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOVED: Create Task modal moved to main return section */}
      
      {false && createTaskActionSettingsModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setCreateTaskActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        selectedBoardId: '',
                        selectedColumnId: '',
                        taskTitle: '',
                        taskDescription: '',
                        taskUrgency: 'medium',
                        taskStatus: 'pending',
                        scheduleOffset: 1,
                        scheduleUnit: 'days',
                        boards: [],
                        columns: []
          })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              createTaskActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                <button
                  onClick={() => setCreateTaskActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    selectedBoardId: '',
                    selectedColumnId: '',
                    taskTitle: '',
                    taskDescription: '',
                    taskUrgency: 'medium',
                    taskStatus: 'pending',
                    scheduleOffset: 1,
                    scheduleUnit: 'days',
                    boards: [],
                    columns: []
                  })}
                      className="text-gray-400 hover:text-white transition-colors"
                >
                      <X className="h-6 w-6" />
                </button>
                    <h2 className="text-xl font-semibold text-white">
                      Create Task Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                  <button
                    onClick={() => {
                      if (createTaskActionSettingsModal.actionId) {
                        setActions(prev => prev.filter(action => action.id !== createTaskActionSettingsModal.actionId));
                        setWireConnections(prev => prev.filter(connection => 
                          connection.fromNodeId !== createTaskActionSettingsModal.actionId && 
                          connection.toNodeId !== createTaskActionSettingsModal.actionId
                        ));
                        setCreateTaskActionSettingsModal({ 
                          isOpen: false, 
                          actionId: null, 
                          selectedBoardId: '',
                          selectedColumnId: '',
                          taskTitle: '',
                          taskDescription: '',
                          taskUrgency: 'medium',
                          taskStatus: 'pending',
                          scheduleOffset: 1,
                          scheduleUnit: 'days',
                          boards: [],
                          columns: []
                        });
                      }
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Delete Action"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              {/* Board Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Board
                </label>
                <select
                  value={createTaskActionSettingsModal.selectedBoardId}
                  onChange={(e) => {
                    const boardId = e.target.value;
                    console.log('🎯 Board selected:', boardId);
                    setCreateTaskActionSettingsModal(prev => ({ 
                      ...prev, 
                      selectedBoardId: boardId,
                      selectedColumnId: '' // Reset column when board changes
                    }));
                    
                    // Load columns for the selected board
                    if (boardId && selectedBusiness?.id) {
                      console.log('🎯 Loading columns for board:', boardId);
                      getBoardWithColumns(boardId).then(boardWithColumns => {
                        console.log('🎯 Board with columns:', boardWithColumns);
                        console.log('🎯 Columns found:', boardWithColumns.columns.length);
                        setCreateTaskActionSettingsModal(prev => ({
                          ...prev,
                          columns: boardWithColumns.columns.map(column => ({
                            id: column.id,
                            title: column.title
                          }))
                        }));
                      }).catch(error => {
                        console.error('❌ Error loading columns:', error);
                      });
                    } else {
                      console.log('🎯 No board selected or no business ID');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Choose a board...</option>
                  {createTaskActionSettingsModal.boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.board_title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select List
                </label>
                {console.log('🎯 Rendering column dropdown with columns:', createTaskActionSettingsModal.columns.length)}
                <select
                  value={createTaskActionSettingsModal.selectedColumnId}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    selectedColumnId: e.target.value 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={!createTaskActionSettingsModal.selectedBoardId}
                >
                  <option value="">Choose a list...</option>
                  {createTaskActionSettingsModal.columns.map((column) => (
                    <option key={column.id} value={column.id}>
                      {column.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Available columns: {createTaskActionSettingsModal.columns.length}
                </p>
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={createTaskActionSettingsModal.taskTitle}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskTitle: e.target.value 
                  }))}
                  placeholder="Enter task title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                
                {/* Variable Dropdown for Task Title */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  console.log('🎯 Available variables for task title:', availableVariables.length);
                  console.log('🎯 Current columns state:', createTaskActionSettingsModal.columns.length);
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task title
                              const currentValue = createTaskActionSettingsModal.taskTitle;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                                ...prev, 
                                taskTitle: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Task Description
                  </label>
                  {(() => {
                    const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                    return availableVariables.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setTaskComponentsModal({
                          isOpen: true,
                          currentDescription: createTaskActionSettingsModal.taskDescription
                        })}
                        className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50"
                        title="Add Components"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    ) : null;
                  })()}
                </div>
                <textarea
                  value={createTaskActionSettingsModal.taskDescription}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskDescription: e.target.value 
                  }))}
                  placeholder="Enter task description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
                
                {/* Variable Dropdown for Task Description */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task description
                              const currentValue = createTaskActionSettingsModal.taskDescription;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                                ...prev, 
                                taskDescription: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Urgency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency Level
                </label>
                <select
                  value={createTaskActionSettingsModal.taskUrgency}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskUrgency: e.target.value as 'low' | 'medium' | 'high' | 'alert'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="alert">Alert</option>
                </select>
              </div>

              {/* Task Status and Scheduling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Status
                </label>
                <select
                  value={createTaskActionSettingsModal.taskStatus}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskStatus: e.target.value as 'pending' | 'scheduled'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {/* Scheduling Options - only show when status is scheduled */}
              {createTaskActionSettingsModal.taskStatus === 'scheduled' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Task
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={createTaskActionSettingsModal.scheduleOffset}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleOffset: parseInt(e.target.value) || 1
                        }))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <select
                        value={createTaskActionSettingsModal.scheduleUnit}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleUnit: e.target.value as 'hours' | 'days' | 'weeks'
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="hours">Hour(s)</option>
                        <option value="days">Day(s)</option>
                        <option value="weeks">Week(s)</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Task will be scheduled {createTaskActionSettingsModal.scheduleOffset} {createTaskActionSettingsModal.scheduleUnit} from when the automation runs
                    </p>
                  </div>
                </div>
              )}
                </div>
            </div>

            {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                onClick={() => setCreateTaskActionSettingsModal({ 
                  isOpen: false, 
                  actionId: null, 
                  selectedBoardId: '',
                  selectedColumnId: '',
                  taskTitle: '',
                  taskDescription: '',
                  taskUrgency: 'medium',
                  taskStatus: 'pending',
                  scheduleOffset: 1,
                  scheduleUnit: 'days',
                  boards: [],
                  columns: []
                })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the create task action configuration
                  if (createTaskActionSettingsModal.actionId) {
                    setActions(prev => prev.map(action => {
                      if (action.id === createTaskActionSettingsModal.actionId) {
                        return {
                          ...action,
                          config: {
                            selectedBoardId: createTaskActionSettingsModal.selectedBoardId,
                            selectedColumnId: createTaskActionSettingsModal.selectedColumnId,
                            taskTitle: createTaskActionSettingsModal.taskTitle,
                            taskDescription: createTaskActionSettingsModal.taskDescription,
                            taskUrgency: createTaskActionSettingsModal.taskUrgency,
                            taskStatus: createTaskActionSettingsModal.taskStatus,
                            scheduleOffset: createTaskActionSettingsModal.scheduleOffset,
                            scheduleUnit: createTaskActionSettingsModal.scheduleUnit,
                            uiLabel: `Create Task: ${createTaskActionSettingsModal.taskTitle || 'Untitled'}`
                          }
                        };
                      }
                      return action;
                    }));
                  }
                  
                  // Close modal
                  setCreateTaskActionSettingsModal({ 
                    isOpen: false, 
                    actionId: null, 
                    selectedBoardId: '',
                    selectedColumnId: '',
                    taskTitle: '',
                    taskDescription: '',
                    taskUrgency: 'medium',
                    taskStatus: 'pending',
                    scheduleOffset: 1,
                    scheduleUnit: 'days',
                    boards: [],
                    columns: []
                  });
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Title Modal */}
      {isEditTitleModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Automation Title</h2>
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter automation title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editTitle.trim()) {
                    handleSaveTitle();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={!editTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Automation Modal */}
      {false && isDeleteAutomationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Automation</h2>
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                This will permanently delete the automation "{selectedAutomation?.name}" and all of its:
              </p>
              <ul className="text-sm text-gray-600 mt-2 ml-4 list-disc">
                <li>Triggers and actions</li>
                <li>Connections and workflow</li>
                <li>Execution history</li>
                <li>Scheduled jobs</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Automation'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Task Components Modal */}
      {taskComponentsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Task Components</h3>
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Click on any component below to add it to your task description:
              </p>
              
              <div className="space-y-3">
                {/* Customer Follow Component */}
                <button
                  onClick={() => {
                    const customerFollowComponent = `
[customer_follow]
Customer: {customer_name}
Booking: {booking_number}
Vehicle: {vehicle_info}
Service: {booking_title}
Total: {grand_total}
[/customer_follow]`;
                    
                    const newDescription = taskComponentsModal.currentDescription + customerFollowComponent;
                    setCreateTaskActionSettingsModal(prev => ({
                      ...prev,
                      taskDescription: newDescription
                    }));
                    setTaskComponentsModal({ isOpen: false, currentDescription: '' });
                  }}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Customer Follow</span>
                        <p className="text-xs text-gray-600">Add customer and booking information</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Main component return with modals available for both desktop and mobile
  return (
    <>
      {renderMainContent()}

      {/* Single "+" entry point → Automation / AI create / Create agent */}
      <CreateMenuModal
        isOpen={isCreateMenuOpen}
        onClose={() => setIsCreateMenuOpen(false)}
        onCreateAutomation={handleCreateClick}
        onCreateWithAi={handleAiCreateClick}
        onCreateAgent={() => setIsAgentPickerOpen(true)}
      />

      {/* Create Agent (Agents V2) — type picker → receptionist builder */}
      {selectedBusiness?.id && (
        <AgentTypePickerModal
          isOpen={isAgentPickerOpen}
          onClose={() => setIsAgentPickerOpen(false)}
          businessId={selectedBusiness.id}
        />
      )}

      {/* Create Automation Modal - Available for both desktop and mobile */}
      {selectedBusiness?.id && (
        <AIAutomationBuilderModal
          isOpen={isAiBuilderOpen}
          onClose={() => setIsAiBuilderOpen(false)}
          businessId={selectedBusiness.id}
          editAutomation={aiBuilderEdit?.automation || null}
          editFlow={aiBuilderEdit?.flow || null}
          onSaved={handleAiWorkflowSaved}
        />
      )}

      {isCreateModalOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{
            zIndex: 99999,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onClick={handleCloseModal}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200"
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100000,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
              <h2 className="text-2xl font-bold text-gray-900" style={{ 
                letterSpacing: '-0.02em'
              }}>
                Create New Automation
              </h2>
            </div>
            
            {/* Content */}
            <div 
              className="flex-1 overflow-y-auto bg-white"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="p-6">
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Automation Name
                  </label>
                  <input
                    type="text"
                    value={automationTitle}
                    onChange={(e) => setAutomationTitle(e.target.value)}
                    placeholder="Enter automation name..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && automationTitle.trim()) {
                        handleSaveAutomation();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAutomation}
                disabled={!automationTitle.trim() || loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-400 text-white rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] disabled:cursor-not-allowed touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Automation'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create CRM Lead Action Settings Modal - Works on both desktop and mobile */}
      {crmLeadActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              crmLeadActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
            {/* Header - Clean Modern Design */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
              <button
                    onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                    className="text-gray-400 hover:text-white transition-colors"
              >
                    <X className="h-6 w-6" />
              </button>
                  <h2 className="text-xl font-semibold text-white">
                    Create CRM Lead Settings
                  </h2>
            </div>
                {/* Delete button on the right */}
              <button
                  onClick={() => {
                    if (crmLeadActionSettingsModal.actionId) {
                      setActions(prev => prev.filter(action => action.id !== crmLeadActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== crmLeadActionSettingsModal.actionId && 
                        connection.toNodeId !== crmLeadActionSettingsModal.actionId
                      ));
                      setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                    }
                  }}
                  className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                  <Trash2 className="h-5 w-5" />
              </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-6 space-y-6">
              {(() => {
                const actionId = crmLeadActionSettingsModal.actionId;
                if (!actionId) return false;
                const seen = new Set<string>();
                const stack = [actionId];
                while (stack.length) {
                  const id = stack.pop() as string;
                  if (seen.has(id)) continue;
                  seen.add(id);
                  if (triggers.some((t) => t.id === id && t.type === 'webhook')) return true;
                  for (const conn of wireConnections) {
                    if (conn.toNodeId === id) stack.push(conn.fromNodeId);
                  }
                }
                return false;
              })() && (
                <p className="text-sm text-gray-700 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
                  Zapier and Facebook leads do not need a special field mapping. As long as the submission contains an email or a phone number, the lead is created.
                </p>
              )}
              {!fieldMappings.useCrmAdvancedSplit && (
                <>
              {/* Pipeline Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pipeline *
                </label>
                <select
                  value={fieldMappings.selectedPipeline || ''}
                  onChange={(e) => {
                    const pipelineId = e.target.value ? parseInt(e.target.value) : null;
                    const selectedPipeline = pipelines.find(p => p.id === pipelineId);
                    console.log('🔧 CRM LEAD: Pipeline selection debug:', {
                      pipelineId,
                      availablePipelines: pipelines.map(p => ({ id: p.id, name: p.pipeline_name })),
                      selectedPipeline,
                      pipelineName: selectedPipeline?.pipeline_name
                    });
                    setFieldMappings(prev => ({
                      ...prev,
                      selectedPipeline: pipelineId,
                      selectedPipelineName: selectedPipeline?.pipeline_name || '',
                      selectedStage: null, // Reset stage when pipeline changes
                      selectedStageName: '' // Reset stage name when pipeline changes
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={loadingPipelines}
                >
                  <option value="">Select a pipeline...</option>
                  {pipelines.map(pipeline => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.pipeline_name}
                    </option>
                  ))}
                </select>
                {loadingPipelines && <p className="text-xs text-gray-500 mt-1">Loading pipelines...</p>}
                </div>

              {/* Stage Selection */}
              {fieldMappings.selectedPipeline && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stage *
                  </label>
                  <select
                    value={fieldMappings.selectedStage || ''}
                    onChange={(e) => {
                      const stageId = e.target.value ? parseInt(e.target.value) : null;
                      const selectedPipeline = pipelines.find(p => p.id === fieldMappings.selectedPipeline);
                      const selectedStage = selectedPipeline?.stages?.find(s => s.id === stageId);
                      setFieldMappings(prev => ({ 
                        ...prev, 
                        selectedStage: stageId,
                        selectedStageName: selectedStage?.name || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a stage...</option>
                    {pipelines
                      .find(p => p.id === fieldMappings.selectedPipeline)
                      ?.stages?.map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="pt-1">
                <button
                  type="button"
                  onClick={() =>
                    setFieldMappings((prev) => ({
                      ...prev,
                      useCrmAdvancedSplit: true,
                      crmSplitLeft: {
                        selectedPipeline: prev.selectedPipeline,
                        selectedPipelineName: prev.selectedPipelineName,
                        selectedStage: prev.selectedStage,
                        selectedStageName: prev.selectedStageName,
                      },
                      crmSplitRight: {
                        selectedPipeline: prev.selectedPipeline,
                        selectedPipelineName: prev.selectedPipelineName,
                        selectedStage: prev.selectedStage,
                        selectedStageName: prev.selectedStageName,
                      },
                    }))
                  }
                  className="text-sm font-medium text-purple-700 hover:text-purple-900 underline underline-offset-2"
                >
                  {'Advanced: AI routing & split paths'}
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Route leads to different pipelines/stages using your own prompt and incoming webhook or form data.
                </p>
              </div>
                </>
              )}

              {fieldMappings.useCrmAdvancedSplit && (
                <div className="space-y-4 rounded-xl border border-purple-200 bg-purple-50/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">{'Advanced: AI routing & split paths'}</h3>
                    <button
                      type="button"
                      onClick={() =>
                        setFieldMappings((prev) => ({
                          ...prev,
                          useCrmAdvancedSplit: false,
                          selectedPipeline: prev.crmSplitLeft.selectedPipeline,
                          selectedPipelineName: prev.crmSplitLeft.selectedPipelineName,
                          selectedStage: prev.crmSplitLeft.selectedStage,
                          selectedStageName: prev.crmSplitLeft.selectedStageName,
                        }))
                      }
                      className="text-xs font-medium text-gray-600 hover:text-gray-900"
                    >
                      {'Use simple pipeline & stage'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600">
                    When the automation runs, the model uses <strong>only your prompt</strong> to choose <strong>left</strong> or <strong>right</strong> (the two paths below).
                    Spell out which branch each case uses (e.g. vague inquiry → say branch right; detailed inquiry → say branch left). Left and right are only labels — the app does not assign meaning to them.
                    Uses AI tokens from your balance.
                  </p>
                  <p className="text-xs text-purple-800 bg-purple-100/80 border border-purple-200 rounded-lg px-3 py-2">
                    In the workflow view, when you connect another action <strong>from this Create CRM Lead</strong>, you will be asked to pick <strong>Left path</strong> or <strong>Right path</strong> in a dropdown so each branch can have its own actions.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={fieldMappings.useCrmAiRouting}
                      onChange={(e) =>
                        setFieldMappings((prev) => ({ ...prev, useCrmAiRouting: e.target.checked }))
                      }
                    />
                    <span className="text-sm text-gray-800">Use AI to choose left vs right</span>
                  </label>
                  {fieldMappings.useCrmAiRouting && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Routing prompt *
                      </label>
                      <textarea
                        value={fieldMappings.crmAiRoutingPrompt}
                        onChange={(e) =>
                          setFieldMappings((prev) => ({ ...prev, crmAiRoutingPrompt: e.target.value }))
                        }
                        rows={6}
                        placeholder={`Example: Examine the lead data. If the customer described what work they need in enough detail to quote, respond with branch "right". If they did not describe the job (only contact info or vague text), respond with branch "left".`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  )}
                  {!fieldMappings.useCrmAiRouting && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      AI is off: new leads will always use the <strong>left</strong> path until you turn AI on.
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Left path</h4>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Pipeline *</label>
                        <select
                          value={fieldMappings.crmSplitLeft.selectedPipeline || ''}
                          onChange={(e) => {
                            const pipelineId = e.target.value ? parseInt(e.target.value, 10) : null;
                            const sp = pipelines.find((p) => p.id === pipelineId);
                            setFieldMappings((prev) => ({
                              ...prev,
                              crmSplitLeft: {
                                ...prev.crmSplitLeft,
                                selectedPipeline: pipelineId,
                                selectedPipelineName: sp?.pipeline_name || '',
                                selectedStage: null,
                                selectedStageName: '',
                              },
                            }));
                          }}
                          className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          disabled={loadingPipelines}
                        >
                          <option value="">Pipeline…</option>
                          {pipelines.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.pipeline_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {fieldMappings.crmSplitLeft.selectedPipeline && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Stage *</label>
                          <select
                            value={fieldMappings.crmSplitLeft.selectedStage || ''}
                            onChange={(e) => {
                              const stageId = e.target.value ? parseInt(e.target.value, 10) : null;
                              const sp = pipelines.find((p) => p.id === fieldMappings.crmSplitLeft.selectedPipeline);
                              const st = sp?.stages?.find((s) => s.id === stageId);
                              setFieldMappings((prev) => ({
                                ...prev,
                                crmSplitLeft: {
                                  ...prev.crmSplitLeft,
                                  selectedStage: stageId,
                                  selectedStageName: st?.name || '',
                                },
                              }));
                            }}
                            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Stage…</option>
                            {pipelines
                              .find((p) => p.id === fieldMappings.crmSplitLeft.selectedPipeline)
                              ?.stages?.map((stage) => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Right path</h4>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Pipeline *</label>
                        <select
                          value={fieldMappings.crmSplitRight.selectedPipeline || ''}
                          onChange={(e) => {
                            const pipelineId = e.target.value ? parseInt(e.target.value, 10) : null;
                            const sp = pipelines.find((p) => p.id === pipelineId);
                            setFieldMappings((prev) => ({
                              ...prev,
                              crmSplitRight: {
                                ...prev.crmSplitRight,
                                selectedPipeline: pipelineId,
                                selectedPipelineName: sp?.pipeline_name || '',
                                selectedStage: null,
                                selectedStageName: '',
                              },
                            }));
                          }}
                          className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          disabled={loadingPipelines}
                        >
                          <option value="">Pipeline…</option>
                          {pipelines.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.pipeline_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {fieldMappings.crmSplitRight.selectedPipeline && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Stage *</label>
                          <select
                            value={fieldMappings.crmSplitRight.selectedStage || ''}
                            onChange={(e) => {
                              const stageId = e.target.value ? parseInt(e.target.value, 10) : null;
                              const sp = pipelines.find((p) => p.id === fieldMappings.crmSplitRight.selectedPipeline);
                              const st = sp?.stages?.find((s) => s.id === stageId);
                              setFieldMappings((prev) => ({
                                ...prev,
                                crmSplitRight: {
                                  ...prev.crmSplitRight,
                                  selectedStage: stageId,
                                  selectedStageName: st?.name || '',
                                },
                              }));
                            }}
                            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Stage…</option>
                            {pipelines
                              .find((p) => p.id === fieldMappings.crmSplitRight.selectedPipeline)
                              ?.stages?.map((stage) => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Field Mappings - Only show if connected to a trigger */}
              {crmLeadActionSettingsModal.actionId && isActionConnectedToTrigger(crmLeadActionSettingsModal.actionId) ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700 border-b pb-2">Field Mappings</h4>
                  
                  {/* Customer Info - Auto-handled with smart validation */}
                  {(() => {
                    // Get connected trigger and check customer fields
                    if (!crmLeadActionSettingsModal.actionId) return null;
                    
                    const connectedTrigger = triggers.find(t => 
                      wireConnections.some(conn => 
                        conn.fromNodeId === t.id && 
                        conn.toNodeId === crmLeadActionSettingsModal.actionId
                      )
                    );
                    
                    if (!connectedTrigger) return null;
                    
                    // Get available customer fields from the trigger
                    let customerFields = { hasEmail: false, hasPhone: false, hasName: false };
                    let triggerName = '';
                    let fieldsList: string[] = [];
                    let isOnlineBookingBanner = false;
                    
                    // Check if this is an online booking banner trigger - customer is pre-created
                    if (connectedTrigger.type === 'form_submission' && connectedTrigger.config?.formType === 'online_booking_banner') {
                      isOnlineBookingBanner = true;
                      triggerName = 'Online Booking Banner';
                      // Banner submissions create the customer before the automation runs
                      customerFields.hasEmail = true;
                      customerFields.hasPhone = true;
                      customerFields.hasName = true;
                      fieldsList.push('Customer (pre-created by banner submission)');
                    } else if (connectedTrigger.type === 'form_submission' && connectedTrigger.config?.formType === 'lead_form') {
                      // Get form fields from selected form
                      const formId = connectedTrigger.config?.selectedForm || connectedTrigger.config?.formId;
                      const form = leadForms.find(f => f.id === formId);
                      triggerName = form?.name || 'Lead Form';
                      
                      if (form?.form_config?.fields) {
                        form.form_config.fields.forEach((field: any) => {
                          if (field.type === 'customer_info') {
                            customerFields.hasEmail = true;
                            customerFields.hasPhone = true;
                            customerFields.hasName = true;
                            fieldsList.push('First Name', 'Last Name', 'Email', 'Phone');
                          }
                        });
                      }
                    } else if (connectedTrigger.type === 'webhook') {
                      triggerName = 'Webhook';
                      const discoveredFields = connectedTrigger.config?.discovered_fields || [];
                      
                      discoveredFields.forEach((fieldName: string) => {
                        const lowerName = fieldName.toLowerCase();
                        if (lowerName.includes('email')) {
                          customerFields.hasEmail = true;
                          fieldsList.push(fieldName);
                        }
                        if (lowerName.includes('phone')) {
                          customerFields.hasPhone = true;
                          fieldsList.push(fieldName);
                        }
                        if (lowerName.includes('name') || lowerName.includes('first') || lowerName.includes('last')) {
                          customerFields.hasName = true;
                          if (!fieldsList.includes(fieldName)) {
                            fieldsList.push(fieldName);
                          }
                        }
                      });
                    }
                    
                    // Validate if we have required fields
                    const hasRequiredFields = customerFields.hasEmail || customerFields.hasPhone;
                    const missingFields = [];
                    if (!customerFields.hasEmail && !customerFields.hasPhone) {
                      missingFields.push('Email or Phone (at least one required)');
                    }
                    
                    return (
                      <div className={`border rounded-lg p-3 ${
                        hasRequiredFields 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className={`text-sm font-medium mb-1 ${
                          hasRequiredFields ? 'text-green-900' : 'text-red-900'
                        }`}>
                          Customer Creation
                          </div>
                        <div className={`text-xs ${
                          hasRequiredFields ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {hasRequiredFields ? (
                            <>
                              <div className="mb-1">
                                <strong>Source:</strong> {triggerName}
                        </div>
                              <div>
                                <strong>Using fields:</strong> {fieldsList.length > 0 ? fieldsList.join(', ') : 'None detected'}
                        </div>
                              {!customerFields.hasName && (
                                <div className="mt-2 text-yellow-700">
                                  ⚠️ No name fields detected - customer will be created as "Unknown"
                      </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="mb-1">
                                <strong>⚠️ Cannot create customer</strong>
                    </div>
                              <div className="mb-1">
                                <strong>Missing required fields:</strong> {missingFields.join(', ')}
                </div>
                              <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded">
                                <strong>Action Required:</strong> Add email or phone field to your {triggerName.toLowerCase()} to enable customer creation.
                              </div>
                            </>
              )}
            </div>
                      </div>
                    );
                  })()}

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={fieldMappings.status || 'Open'}
                    onChange={(e) => setFieldMappings(prev => ({
                      ...prev,
                      status: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Open">Open</option>
                    <option value="Won">Won</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>

                {/* Opportunity Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Title *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.title || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        title: e.target.value
                      }))}
                      placeholder="Enter opportunity title"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
              <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('title', fieldMappings.title || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

                {/* Opportunity Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Value
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.value || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        value: e.target.value
                      }))}
                      placeholder="0"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('value', fieldMappings.value || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
        </div>
    </div>

                {/* Lead Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lead Info
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={fieldMappings.lead_info || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        lead_info: e.target.value
                      }))}
                      placeholder="Additional lead information (auto-populated from form fields)"
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('lead_info', fieldMappings.lead_info || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 self-start"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This field can auto-populate with data from your webhook/form submission
                  </p>
                </div>

                {/* Opportunity Source */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity Source
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={fieldMappings.source || ''}
                      onChange={(e) => setFieldMappings(prev => ({
                        ...prev,
                        source: e.target.value
                      }))}
                      placeholder="e.g. Website Form, Lead Magnet"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
              <button
                      type="button"
                      onClick={() => handleOpenVariablesModal('source', fieldMappings.source || '')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Insert form field variable"
                    >
                      <Plus className="h-4 w-4" />
              </button>
                  </div>
            </div>
            
                {/* Custom Fields */}
                {customFieldDefinitions.length > 0 && (
                  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Fields
                </label>
                    <div className="space-y-3">
                      {customFieldDefinitions.map(fieldDef => (
                        <div key={fieldDef.id}>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {fieldDef.name}
                          </label>
                          <div className="flex gap-2">
                <input
                  type="text"
                              value={fieldMappings.custom_fields[fieldDef.id] || ''}
                              onChange={(e) => setFieldMappings(prev => ({
                                ...prev,
                                custom_fields: {
                                  ...prev.custom_fields,
                                  [fieldDef.id]: e.target.value
                                }
                              }))}
                              placeholder={`Enter ${fieldDef.name.toLowerCase()}`}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <button
                              type="button"
                              onClick={() => handleOpenVariablesModal(`custom_fields.${fieldDef.id}`, fieldMappings.custom_fields[fieldDef.id] || '')}
                              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              title="Insert form field variable"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              ) : (
                /* Not connected to trigger - Show message */
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <div className="text-yellow-800 mb-2">
                    <Zap className="h-12 w-12 mx-auto mb-3 text-yellow-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-yellow-900 mb-2">Connect a Trigger First</h4>
                  <p className="text-sm text-yellow-700">
                    Please connect this action to a Form Submission trigger to configure field mappings. 
                    Connect the trigger's output port to this action's input port to get started.
                  </p>
                </div>
              )}

              {/* UI Label Input - Always visible */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === crmLeadActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === crmLeadActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Create Lead', 'Add to Pipeline', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-lg">
              <div className="flex gap-3">
              <button
                  onClick={() => setCrmLeadActionSettingsModal({ isOpen: false, actionId: null })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!crmLeadActionSettingsModal.actionId) return;

                  // Check if connected to a trigger
                  if (!isActionConnectedToTrigger(crmLeadActionSettingsModal.actionId)) {
                    alert('Please connect this action to a Form Submission or Webhook trigger first');
                    return;
                  }

                  // Validate customer creation fields
                  const connectedTrigger = triggers.find(t => 
                    wireConnections.some(conn => 
                      conn.fromNodeId === t.id && 
                      conn.toNodeId === crmLeadActionSettingsModal.actionId
                    )
                  );
                  
                  if (connectedTrigger) {
                    // Skip validation for online booking banner - customer is pre-created
                    const isOnlineBookingBanner = connectedTrigger.type === 'form_submission' && 
                      connectedTrigger.config?.formType === 'online_booking_banner';
                    
                    if (!isOnlineBookingBanner && connectedTrigger.type === 'form_submission' && connectedTrigger.config?.formType === 'lead_form') {
                      const formId = connectedTrigger.config?.selectedForm || connectedTrigger.config?.formId;
                      const form = leadForms.find(f => f.id === formId);
                      const hasCustomerInfo = !!form?.form_config?.fields?.some((f: any) => f.type === 'customer_info');
                      if (!hasCustomerInfo) {
                        alert('⚠️ Customer creation will fail: this lead form needs a Customer Info field (email or phone) before you can save.');
                        return;
                      }
                    }
                  }

                  // Validate required fields
                  if (!fieldMappings.useCrmAdvancedSplit) {
                    if (!fieldMappings.selectedPipeline || !fieldMappings.selectedStage) {
                      alert('Please select a pipeline and stage');
                      return;
                    }
                  } else {
                    const L = fieldMappings.crmSplitLeft;
                    const R = fieldMappings.crmSplitRight;
                    if (!L.selectedPipeline || !L.selectedStage || !R.selectedPipeline || !R.selectedStage) {
                      alert('Advanced mode: select pipeline and stage for both left and right paths.');
                      return;
                    }
                    if (fieldMappings.useCrmAiRouting && !fieldMappings.crmAiRoutingPrompt.trim()) {
                      alert('Advanced mode: add a routing prompt, or turn off "Use AI to choose left vs right".');
                      return;
                    }
                  }

                  if (!fieldMappings.title) {
                    alert('Please provide an opportunity title');
                    return;
                  }

                  // Update the action with the field mappings configuration
                  setActions(prev => prev.map(action => 
                    action.id === crmLeadActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config,
                            selectedPipeline: fieldMappings.selectedPipeline,
                            selectedPipelineName: fieldMappings.selectedPipelineName,
                            selectedStage: fieldMappings.selectedStage,
                            selectedStageName: fieldMappings.selectedStageName,
                            status: fieldMappings.status || 'Open',
                            title: fieldMappings.title,
                            value: fieldMappings.value,
                            lead_info: fieldMappings.lead_info,
                            source: fieldMappings.source,
                            custom_fields: fieldMappings.custom_fields,
                            useCrmAdvancedSplit: fieldMappings.useCrmAdvancedSplit,
                            useCrmAiRouting: fieldMappings.useCrmAiRouting,
                            crmAiRoutingPrompt: fieldMappings.crmAiRoutingPrompt,
                            crmSplitLeft: fieldMappings.crmSplitLeft,
                            crmSplitRight: fieldMappings.crmSplitRight,
                            formFields: selectedFormFields // Store available form fields for reference
                          }
                        }
                      : action
                  ));

                  // Close modal
                  setCrmLeadActionSettingsModal({ isOpen: false, actionId: null });
                }}
                  disabled={
                    !fieldMappings.title ||
                    (!fieldMappings.useCrmAdvancedSplit &&
                      (!fieldMappings.selectedPipeline || !fieldMappings.selectedStage)) ||
                    (fieldMappings.useCrmAdvancedSplit &&
                      (!fieldMappings.crmSplitLeft.selectedPipeline ||
                        !fieldMappings.crmSplitLeft.selectedStage ||
                        !fieldMappings.crmSplitRight.selectedPipeline ||
                        !fieldMappings.crmSplitRight.selectedStage ||
                        (fieldMappings.useCrmAiRouting && !fieldMappings.crmAiRoutingPrompt.trim())))
                  }
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                >
                  Save
                </button>
                  </div>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Variables Modal - Slide-in from right - Works on both desktop and mobile */}
      {variablesModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[999999] flex items-center justify-center md:items-start md:justify-end"
          onClick={handleCloseVariablesModal}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[500px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              variablesModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleCloseVariablesModal}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Insert Form Field Variable
                    </h2>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Click on any form field below to insert it as a variable:
                  </p>
                  
                  <div className="space-y-2">
                {selectedFormFields.length > 0 ? (
                  selectedFormFields.map(field => {
                    // Create simple variable names based on field labels
                    let displayVariable = `{{${field.id}}}`;
                    
                    // Map to simple variable names based on label
                    if (field.label === 'First Name') {
                      displayVariable = '{{first_name}}';
                    } else if (field.label === 'Last Name') {
                      displayVariable = '{{last_name}}';
                    } else if (field.label === 'Email Address') {
                      displayVariable = '{{email}}';
                    } else if (field.label === 'Phone Number') {
                      displayVariable = '{{phone}}';
                    } else {
                      // For other fields, use a cleaned version of the label
                      const cleanLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                      displayVariable = `{{${cleanLabel}}}`;
                    }
                    
                    return (
                      <button
                        key={field.id}
                        onClick={() => insertVariableIntoField(displayVariable)}
                        className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{field.label}</span>
                          <span className="text-xs text-gray-500">{field.type}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Variable: <code className="bg-gray-100 px-1 rounded">{displayVariable}</code>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No form fields available.</p>
                    <p className="text-sm mt-1">Make sure you have a Form Submission trigger connected to this action.</p>
                  </div>
                )}
                </div>
              </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseVariablesModal}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Close
              </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Trigger Library Modal - Available for both desktop and mobile */}
      {isTriggerLibraryOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] md:flex md:items-center md:justify-center md:p-4">
          <div className="bg-white h-full md:h-auto md:rounded-[12px] w-full md:w-full md:max-w-2xl md:mx-4 md:shadow-xl flex flex-col" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}>
            {/* Header */}
            <div className="bg-white border-b border-gray-200/60 px-4 sm:px-6 py-4 md:rounded-t-[12px] flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Add Trigger</h2>
                <button
                  onClick={() => setIsTriggerLibraryOpen(false)}
                  className="p-2 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 md:rounded-b-[12px]" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-3">
                {availableTriggers.map((trigger) => {
                  // Get trigger color for icon background
                  const getTriggerIconBg = (triggerId: string) => {
                    switch (triggerId) {
                      case 'missed_call': return 'bg-red-600';
                      case 'feedback': return 'bg-yellow-600';
                      case 'enter_automation': return 'bg-teal-600';
                      case 'enter_crm_stage': return 'bg-indigo-600';
                      default: return 'bg-purple-600';
                    }
                  };

                  return (
                    <div
                      key={trigger.id}
                      onClick={() => handleSelectTrigger(trigger)}
                      className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 p-4 cursor-pointer touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon with circular background */}
                        <div className={`w-12 h-12 ${getTriggerIconBg(trigger.id)} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <div className="text-white">
                            {trigger.id === 'booking' && <Zap className="h-6 w-6 text-white" />}
                            {trigger.id === 'form_submission' && <Zap className="h-6 w-6 text-white" />}
                            {trigger.id === 'webhook' && <Zap className="h-6 w-6 text-white" />}
                            {trigger.id === 'enter_automation' && <GitBranch className="h-6 w-6 text-white" />}
                            {trigger.id === 'enter_crm_stage' && <Layers className="h-6 w-6 text-white" />}
                            {trigger.id === 'feedback' && <Star className="h-6 w-6 text-white" />}
                            {trigger.id === 'missed_call' && <Phone className="h-6 w-6 text-white" />}
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-[16px] mb-1" style={{ letterSpacing: '-0.01em' }}>{trigger.name}</h3>
                          <p className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>{trigger.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Action Library Modal - Available for both desktop and mobile */}
      {isActionLibraryOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] md:flex md:items-center md:justify-center md:p-4">
          <div className="bg-white h-full md:h-auto md:rounded-[12px] w-full md:w-full md:max-w-2xl md:mx-4 md:shadow-xl flex flex-col" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}>
            {/* Header */}
            <div className="bg-white border-b border-gray-200/60 px-4 sm:px-6 py-4 md:rounded-t-[12px] flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Add Action</h2>
                <button
                  onClick={() => setIsActionLibraryOpen(false)}
                  className="p-2 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 md:rounded-b-[12px]" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-3">
                {(crmLiveIntakeFormsEnabled
                  ? [
                      ...availableActions,
                      {
                        id: 'send_live_intake_form',
                        name: 'Send Live Form',
                        description: 'Send a live intake link after Create CRM Lead runs (wire this step after that action)',
                        icon: <ClipboardList className="h-6 w-6 text-cyan-600" />,
                        color: 'bg-cyan-100 border-cyan-300'
                      }
                    ]
                  : availableActions
                ).map((action) => {
                  // Get action color for icon background
                  const getActionIconBg = (actionId: string) => {
                    switch (actionId) {
                      case 'sms': return 'bg-green-600';
                      case 'email': return 'bg-blue-600';
                      case 'delay': return 'bg-orange-600';
                      case 'create_crm_lead': return 'bg-purple-600';
                      case 'check': return 'bg-yellow-600';
                      case 'create_task': return 'bg-indigo-600';
                      case 'assign_tags': return 'bg-pink-600';
                      case 'add_to_automation': return 'bg-teal-600';
                      case 'send_live_intake_form': return 'bg-cyan-600';
                      case 'feedback_agent': return 'bg-amber-500';
                      default: return 'bg-gray-600';
                    }
                  };

                  return (
                    <div
                      key={action.id}
                      onClick={() => handleSelectAction(action)}
                      className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 p-4 cursor-pointer touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon with circular background */}
                        <div className={`w-12 h-12 ${getActionIconBg(action.id)} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <div className="text-white">
                            {action.id === 'sms' && <MessageCircle className="h-6 w-6 text-white" />}
                            {action.id === 'email' && <Mail className="h-6 w-6 text-white" />}
                            {action.id === 'delay' && <Clock className="h-6 w-6 text-white" />}
                            {action.id === 'create_crm_lead' && <Plus className="h-6 w-6 text-white" />}
                            {action.id === 'check' && <Eye className="h-6 w-6 text-white" />}
                            {action.id === 'create_task' && <CheckSquare className="h-6 w-6 text-white" />}
                            {action.id === 'assign_tags' && <Tag className="h-6 w-6 text-white" />}
                            {action.id === 'add_to_automation' && <Share2 className="h-6 w-6 text-white" />}
                            {action.id === 'send_live_intake_form' && <ClipboardList className="h-6 w-6 text-white" />}
                            {action.id === 'feedback_agent' && <Phone className="h-6 w-6 text-white" />}
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-[16px] mb-1" style={{ letterSpacing: '-0.01em' }}>{action.name}</h3>
                          <p className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>{action.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* SMS Action Settings Modal - Available for both desktop and mobile */}
      {smsActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              smsActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      SMS Action Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                <button
                  onClick={() => {
                    if (smsActionSettingsModal.actionId) {
                      setActions(prev => prev.filter(action => action.id !== smsActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== smsActionSettingsModal.actionId && connection.toNodeId !== smsActionSettingsModal.actionId
                      ));
                    setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
                      }
                  }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Message
                </label>
                <div
                  ref={messageEditorRef}
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Enter the SMS message to send when this automation triggers..."
                  className="w-full min-h-[120px] max-h-[300px] overflow-y-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none"
                  onInput={(e) => {
                    smsIgnoreSyncRef.current = true;
                    const html = (e.target as HTMLDivElement).innerHTML;
                    setSmsActionSettingsModal(prev => ({ ...prev, message: html }));
                  }}
                />
                
                {/* Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(smsActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                <p className="text-xs text-gray-500 mt-1">
                  Variables will be automatically replaced with actual booking data when the SMS is sent.
                </p>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notify
                </label>
                <select
                  value={smsActionSettingsModal.recipient}
                  onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select who to notify...</option>
                  {getAvailableRecipients(smsActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom phone number input - only show when "custom" is selected */}
                {smsActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="tel"
                    value={smsActionSettingsModal.customPhone || ''}
                    onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, customPhone: e.target.value }))}
                    placeholder="Enter custom phone number..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent mt-2"
                  />
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const action = actions.find(a => a.id === smsActionSettingsModal.actionId);
                    return action?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === smsActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Message', 'Reminder SMS', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
              </div>
            </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSmsMessage}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Email Action Settings Modal - Available for both desktop and mobile */}
      {emailActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              emailActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Email Action Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                <button
                  onClick={() => {
                    if (emailActionSettingsModal.actionId) {
                      setActions(prev => prev.filter(action => action.id !== emailActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== emailActionSettingsModal.actionId && 
                        connection.toNodeId !== emailActionSettingsModal.actionId
                      ));
                      setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false });
                    }
                  }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, useTemplate: false, selectedTemplateId: null }))}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      !emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom Message
                  </button>
                  <button
                    onClick={() => {
                      console.log('📧 Use Template button clicked!');
                      handleOpenTemplateSelector();
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      emailActionSettingsModal.useTemplate 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Use Template
                  </button>
                </div>
                
                {/* Show selected template info */}
                {emailActionSettingsModal.useTemplate && emailActionSettingsModal.selectedTemplateId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="text-sm font-medium text-blue-900">
                      Template Selected: {emailTemplateSelector.templates.find(t => t.id === emailActionSettingsModal.selectedTemplateId)?.name || 'Unknown Template'}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Click "Use Template" again to change template
                    </div>
                  </div>
                )}
              </div>

              {/* Email Subject Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <input
                  ref={emailSubjectInputRef}
                  type="text"
                  value={emailActionSettingsModal.subject}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter the email subject line..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={emailActionSettingsModal.useTemplate}
                />
                
                {/* Variable Dropdown for Subject */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0 && !emailActionSettingsModal.useTemplate) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Subject:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailSubjectVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (!emailActionSettingsModal.useTemplate) {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Email Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Message
                </label>
                {/* Formatting Toolbar */}
                {!emailActionSettingsModal.useTemplate && (
                  <div className="flex items-center gap-1 mb-0 px-1 py-1 bg-gray-50 border border-gray-300 border-b-0 rounded-t-lg">
                    {(['bold', 'italic', 'underline'] as const).map(cmd => (
                      <button
                        key={cmd}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          emailMessageEditorRef.current?.focus();
                          document.execCommand(cmd, false);
                          const el = emailMessageEditorRef.current;
                          if (el) { emailIgnoreSyncRef.current = true; setEmailActionSettingsModal(prev => ({ ...prev, message: el.innerHTML })); }
                        }}
                        className={`w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded text-sm ${cmd === 'bold' ? 'font-bold' : ''} ${cmd === 'italic' ? 'italic' : ''} ${cmd === 'underline' ? 'underline' : ''}`}
                        title={cmd.charAt(0).toUpperCase() + cmd.slice(1)}
                      >{cmd.charAt(0).toUpperCase()}</button>
                    ))}
                    <div className="w-px h-5 bg-gray-300 mx-1" />
                    {[
                      { cmd: 'insertUnorderedList', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 6h13M8 12h13M8 18h16" /><circle cx="3" cy="6" r="1.5" fill="currentColor" /><circle cx="3" cy="12" r="1.5" fill="currentColor" /><circle cx="3" cy="18" r="1.5" fill="currentColor" /></svg>, title: 'Bullet List' },
                      { cmd: 'insertOrderedList', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>, title: 'Numbered List' }
                    ].map(({ cmd, icon, title }) => (
                      <button
                        key={cmd}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          emailMessageEditorRef.current?.focus();
                          document.execCommand(cmd, false);
                          const el = emailMessageEditorRef.current;
                          if (el) { emailIgnoreSyncRef.current = true; setEmailActionSettingsModal(prev => ({ ...prev, message: el.innerHTML })); }
                        }}
                        className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 rounded text-sm"
                        title={title}
                      >{icon}</button>
                    ))}
                  </div>
                )}
                <div
                  ref={emailMessageEditorRef}
                  contentEditable={!emailActionSettingsModal.useTemplate}
                  suppressContentEditableWarning
                  data-placeholder="Enter the email message content..."
                  className={`w-full min-h-[180px] max-h-[400px] overflow-y-auto px-3 py-2 border border-gray-300 ${!emailActionSettingsModal.useTemplate ? 'rounded-b-lg' : 'rounded-lg'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none ${emailActionSettingsModal.useTemplate ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                  onInput={(e) => {
                    emailIgnoreSyncRef.current = true;
                    const html = (e.target as HTMLDivElement).innerHTML;
                    setEmailActionSettingsModal(prev => ({ ...prev, message: html }));
                  }}
                />
                
                {/* Variable Dropdown for Message */}
                {(() => {
                  const availableVariables = getAvailableVariables(emailActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0 && !emailActionSettingsModal.useTemplate) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable in Message:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertEmailMessageVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (!emailActionSettingsModal.useTemplate) {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                {!emailActionSettingsModal.useTemplate && (
                  <p className="text-xs text-gray-500 mt-1">
                    Variables will be automatically replaced with actual booking data when the email is sent.
                  </p>
                )}
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send To
                </label>
                <select
                  value={emailActionSettingsModal.recipient}
                  onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select who to email...</option>
                  {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom email input - only show when "custom" is selected */}
                {emailActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="email"
                    value={emailActionSettingsModal.customEmail || ''}
                    onChange={(e) => setEmailActionSettingsModal(prev => ({ ...prev, customEmail: e.target.value }))}
                    placeholder="Enter custom email address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                  />
                )}
                
                {getAvailableEmailRecipients(emailActionSettingsModal.actionId || '').length === 1 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    💡 Connect this action to a trigger (like "Booking Created") to see more email options
                  </p>
                )}
              </div>

              {/* PDF Attachment - Available for both Custom Message and Use Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF Attachment (Optional)
                </label>
                <input
                  ref={pdfFileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handlePdfAttachmentChange}
                  disabled={emailActionSettingsModal.pdfUploading}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => pdfFileInputRef.current?.click()}
                    disabled={emailActionSettingsModal.pdfUploading}
                    className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {emailActionSettingsModal.pdfUploading ? 'Uploading...' : 'Add PDF'}
                  </button>
                  {emailActionSettingsModal.pdfAttachment && (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm text-gray-700 truncate" title={emailActionSettingsModal.pdfAttachment.filename}>
                        {emailActionSettingsModal.pdfAttachment.filename}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEmailActionSettingsModal(prev => ({ ...prev, pdfAttachment: null }))}
                        className="text-red-600 hover:text-red-800 text-sm font-medium shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Attach a PDF that will be included with every email sent by this action
                </p>
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === emailActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === emailActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Email', 'Booking Confirmation', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
              </div>
            </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                onClick={() => setEmailActionSettingsModal({ isOpen: false, actionId: null, subject: '', message: '', recipient: '', customEmail: '', useTemplate: false, selectedTemplateId: null, pdfAttachment: null, pdfUploading: false })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailMessage}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delay Action Settings Modal - Available for both desktop and mobile */}

      {/* Create Task Action Settings Modal - Available for both desktop and mobile */}
      {delayActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              delayActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Wait Action Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                <button
                  onClick={() => {
                    if (delayActionSettingsModal.actionId) {
                      setActions(prev => prev.filter(action => action.id !== delayActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== delayActionSettingsModal.actionId && 
                        connection.toNodeId !== delayActionSettingsModal.actionId
                      ));
                      setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                    }
                  }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              {(() => {
                const isCreatedTrigger = delayActionSettingsModal.actionId ? isConnectedToCreatedTrigger(delayActionSettingsModal.actionId) : false;
                
                return (
                  <>
                    {/* Wait Type Dropdown - only show if connected to 'created' trigger */}
                    {isCreatedTrigger && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Type
                        </label>
                        <select
                          value={delayActionSettingsModal.waitType}
                          onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                            ...prev, 
                            waitType: e.target.value as 'duration' | 'reminder'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="duration">Wait Duration</option>
                          <option value="reminder">Reminder</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose between a fixed wait duration or a scheduled reminder before appointment.
                        </p>
                      </div>
                    )}

                    {/* Duration Configuration - show for duration type or when not connected to created trigger */}
                    {(delayActionSettingsModal.waitType === 'duration' || !isCreatedTrigger) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Duration
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={delayActionSettingsModal.duration}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              duration: parseInt(e.target.value) || 1 
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <select
                            value={delayActionSettingsModal.unit}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              unit: e.target.value as 'minutes' | 'hours' | 'days' 
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The automation will pause for this duration before continuing to the next action.
                        </p>
                      </div>
                    )}

                    {/* Reminder Configuration - only show for reminder type and created trigger */}
                    {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hours Before Appointment
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="168"
                            value={delayActionSettingsModal.reminderHoursBefore}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderHoursBefore: parseInt(e.target.value) || 24 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            How many hours before the appointment should the reminder be sent? (1-168 hours)
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Time
                          </label>
                          <select
                            value={delayActionSettingsModal.reminderReference}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderReference: e.target.value as 'drop_off_datetime' | 'start_datetime'
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="drop_off_datetime">Drop Off Date & Time</option>
                            <option value="start_datetime">Start Date & Time</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose which appointment time to calculate the reminder from.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-900">Preview:</div>
                      <div className="text-sm text-blue-700">
                        {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger ? (
                          <>
                            Send reminder {delayActionSettingsModal.reminderHoursBefore} hours before{' '}
                            {delayActionSettingsModal.reminderReference === 'drop_off_datetime' ? 'drop off' : 'start'} time
                            <br />
                            <span className="text-xs text-blue-600">
                              Example: For a 2 PM appointment, notification will be sent at{' '}
                              {delayActionSettingsModal.reminderHoursBefore === 24 ? '2 PM the day before' : 
                               delayActionSettingsModal.reminderHoursBefore >= 24 ? 
                               `${Math.floor(delayActionSettingsModal.reminderHoursBefore / 24)} days and ${delayActionSettingsModal.reminderHoursBefore % 24} hours before` :
                               `${2 - delayActionSettingsModal.reminderHoursBefore} ${delayActionSettingsModal.reminderHoursBefore === 1 ? 'PM' : delayActionSettingsModal.reminderHoursBefore === 2 ? 'PM' : 'AM'}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            Wait for {delayActionSettingsModal.duration} {delayActionSettingsModal.unit}
                            {(() => {
                              const duration = delayActionSettingsModal.duration;
                              const unit = delayActionSettingsModal.unit;
                              let totalMinutes = 0;
                              
                              if (unit === 'minutes') totalMinutes = duration;
                              else if (unit === 'hours') totalMinutes = duration * 60;
                              else if (unit === 'days') totalMinutes = duration * 24 * 60;
                              
                              if (totalMinutes < 60) {
                                return ` (${totalMinutes} minutes)`;
                              } else if (totalMinutes < 1440) {
                                return ` (${Math.round(totalMinutes / 60 * 10) / 10} hours)`;
                              } else {
                                return ` (${Math.round(totalMinutes / 1440 * 10) / 10} days)`;
                              }
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* UI Label Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const currentAction = actions.find(action => action.id === delayActionSettingsModal.actionId);
                          return currentAction?.config?.uiLabel || '';
                        })()}
                        onChange={(e) => {
                          const newUiLabel = e.target.value;
                          setActions(prev => prev.map(action => 
                            action.id === delayActionSettingsModal.actionId
                              ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                              : action
                          ));
                        }}
                        placeholder={
                          delayActionSettingsModal.waitType === 'reminder' && delayActionSettingsModal.actionId && isConnectedToCreatedTrigger(delayActionSettingsModal.actionId)
                            ? "e.g. 'Appointment Reminder', '24hr Reminder', etc."
                            : "e.g. 'Cool Down Period', '24hr Delay', etc."
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a short label to help identify this action on the canvas
                      </p>
                    </div>

                    {/* Info Box */}
                    {!isCreatedTrigger && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">
                          <strong>💡 Tip:</strong> Connect this wait action to a "Booking Created" trigger to enable the reminder option for scheduled notifications before appointments!
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
                </div>
            </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!delayActionSettingsModal.actionId || !selectedAutomation?.id) return;

                  try {
                    // Prepare configuration based on wait type
                    const waitConfig: any = {
                      wait_type: delayActionSettingsModal.waitType
                    };

                    if (delayActionSettingsModal.waitType === 'duration') {
                      // Convert to minutes for storage
                      let durationInMinutes = delayActionSettingsModal.duration;
                      if (delayActionSettingsModal.unit === 'hours') {
                        durationInMinutes = delayActionSettingsModal.duration * 60;
                      } else if (delayActionSettingsModal.unit === 'days') {
                        durationInMinutes = delayActionSettingsModal.duration * 24 * 60;
                      }

                      waitConfig.duration_minutes = durationInMinutes;
                      waitConfig.duration_type = delayActionSettingsModal.unit;
                      waitConfig.duration = durationInMinutes; // Legacy field
                      waitConfig.unit = delayActionSettingsModal.unit; // Legacy field
                      waitConfig.displayDuration = delayActionSettingsModal.duration; // Legacy field
                    } else if (delayActionSettingsModal.waitType === 'reminder') {
                      waitConfig.reminder_hours_before = delayActionSettingsModal.reminderHoursBefore;
                      waitConfig.reminder_reference = delayActionSettingsModal.reminderReference;
                    }

                    // Update the action with the configuration
                    const updatedActions = actions.map(action => 
                      action.id === delayActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, ...waitConfig } }
                        : action
                    );

                    // Update local state
                    setActions(updatedActions);

                    // Save to database
                    const persistContext = getActivePersistContext();
                    if (!persistContext) {
                      setError('Unable to save — please try again.');
                      return;
                    }
                    await saveAutomationActions(persistContext.automationId, updatedActions);
                    
                        console.log('✅ Wait action configuration saved successfully');

                    // Close modal
                    setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                  } catch (error) {
                        console.error('❌ Error saving wait action configuration:', error);
                    setError('Failed to save wait action configuration');
                  }
                }}
                    className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CRM advanced split: choose left/right when drawing a connection from Create CRM Lead */}
      {crmBranchConnectModal &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 px-4"
            onClick={() => setCrmBranchConnectModal(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose path branch</h3>
              <p className="text-sm text-gray-600 mb-4">
                Connections from an AI-split <strong>Create CRM Lead</strong> must be labeled so the correct
                downstream actions run after the model picks left or right.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch for this connection</label>
              <select
                value={crmBranchConnectModal.branch}
                onChange={(e) =>
                  setCrmBranchConnectModal({
                    ...crmBranchConnectModal,
                    branch: e.target.value as 'left' | 'right'
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-6 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="left">Left path</option>
                <option value="right">Right path</option>
              </select>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
                  onClick={() => setCrmBranchConnectModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                  onClick={async () => {
                    const m = crmBranchConnectModal;
                    if (!m || !selectedAutomation?.id) return;
                    setCrmBranchConnectModal(null);
                    try {
                      await handleLinearConnection(m.fromNodeId, m.toNodeId, m.branch);
                      const wires = await getAutomationConnections(selectedAutomation.id);
                      setWireConnections(wires);
                    } catch (e) {
                      console.error(e);
                      setError('Failed to save connection');
                    }
                  }}
                >
                  Connect
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Add to Automation — settings drawer */}
      {addToAutomationActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() =>
            setAddToAutomationActionSettingsModal({
              isOpen: false,
              actionId: null,
              targetAutomationId: '',
              receiverOptions: [],
              loadError: null
            })
          }
        >
          <div
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              addToAutomationActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() =>
                        setAddToAutomationActionSettingsModal({
                          isOpen: false,
                          actionId: null,
                          targetAutomationId: '',
                          receiverOptions: [],
                          loadError: null
                        })
                      }
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">Add to Automation</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const aid = addToAutomationActionSettingsModal.actionId;
                      if (aid) {
                        setActions((prev) => prev.filter((a) => a.id !== aid));
                        setWireConnections((prev) =>
                          prev.filter((c) => c.fromNodeId !== aid && c.toNodeId !== aid)
                        );
                      }
                      setAddToAutomationActionSettingsModal({
                        isOpen: false,
                        actionId: null,
                        targetAutomationId: '',
                        receiverOptions: [],
                        loadError: null
                      });
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Delete Action"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-4">
                  {addToAutomationActionSettingsModal.loadError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                      {addToAutomationActionSettingsModal.loadError}
                    </div>
                  )}
                  <p className="text-sm text-gray-600">
                    Choose an automation that has an <strong>Enter Automation</strong> trigger. The target must be turned{' '}
                    <strong>live</strong> to run from this action.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target automation</label>
                    <select
                      value={addToAutomationActionSettingsModal.targetAutomationId}
                      onChange={(e) =>
                        setAddToAutomationActionSettingsModal((prev) => ({
                          ...prev,
                          targetAutomationId: e.target.value
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select…</option>
                      {addToAutomationActionSettingsModal.receiverOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                    {addToAutomationActionSettingsModal.receiverOptions.length === 0 &&
                      !addToAutomationActionSettingsModal.loadError && (
                        <p className="text-xs text-amber-700 mt-2">
                          No receiver automations yet. Create another automation, add an Enter Automation trigger, save, then
                          refresh this list.
                        </p>
                      )}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setAddToAutomationActionSettingsModal({
                        isOpen: false,
                        actionId: null,
                        targetAutomationId: '',
                        receiverOptions: [],
                        loadError: null
                      })
                    }
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const aid = addToAutomationActionSettingsModal.actionId;
                      if (!aid || !selectedAutomation?.id) return;
                      const tid = addToAutomationActionSettingsModal.targetAutomationId;
                      if (!tid) {
                        setError('Select a target automation.');
                        return;
                      }
                      const opt = addToAutomationActionSettingsModal.receiverOptions.find((o) => o.id === tid);
                      try {
                        const updatedActions = actions.map((action) =>
                          action.id === aid
                            ? {
                                ...action,
                                config: {
                                  ...action.config,
                                  targetAutomationId: tid,
                                  targetAutomationName: opt?.name || ''
                                }
                              }
                            : action
                        );
                        setActions(updatedActions);
                        const persistContext = getActivePersistContext();
                        if (!persistContext) {
                          setError('Unable to save — please try again.');
                          return;
                        }
                        await saveAutomationActions(persistContext.automationId, updatedActions);
                        setAddToAutomationActionSettingsModal({
                          isOpen: false,
                          actionId: null,
                          targetAutomationId: '',
                          receiverOptions: [],
                          loadError: null
                        });
                      } catch (err) {
                        console.error(err);
                        setError('Failed to save Add to Automation settings');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Send Live Intake Form — settings drawer */}
      {sendLiveIntakeFormSettingsModal.isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() =>
            setSendLiveIntakeFormSettingsModal({
              isOpen: false,
              actionId: null,
              sendMode: 'email',
              intakeTemplateId: '',
              templates: [],
              loadingTemplates: false,
              intakeSmsBody: '',
              intakeEmailSubject: '',
              intakeEmailBody: ''
            })
          }
        >
          <div
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              sendLiveIntakeFormSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() =>
                        setSendLiveIntakeFormSettingsModal({
                          isOpen: false,
                          actionId: null,
                          sendMode: 'email',
                          intakeTemplateId: '',
                          templates: [],
                          loadingTemplates: false,
                          intakeSmsBody: '',
                          intakeEmailSubject: '',
                          intakeEmailBody: ''
                        })
                      }
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">Send Live Form</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const aid = sendLiveIntakeFormSettingsModal.actionId;
                      if (aid) {
                        setActions((prev) => prev.filter((a) => a.id !== aid));
                        setWireConnections((prev) =>
                          prev.filter((c) => c.fromNodeId !== aid && c.toNodeId !== aid)
                        );
                      }
                      setSendLiveIntakeFormSettingsModal({
                        isOpen: false,
                        actionId: null,
                        sendMode: 'email',
                        intakeTemplateId: '',
                        templates: [],
                        loadingTemplates: false,
                        intakeSmsBody: '',
                        intakeEmailSubject: '',
                        intakeEmailBody: ''
                      });
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Delete Action"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-600">
                    Wire this step <strong>after</strong> <strong>Create CRM Lead</strong> so the new opportunity and customer
                    exist when the link is sent.
                  </p>
                  {!hasUpstreamCreateCrmLead(sendLiveIntakeFormSettingsModal.actionId || '') && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                      No incoming path from <strong>Create CRM Lead</strong> was found. Connect Create CRM Lead to this
                      action (directly or through intermediate steps).
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Send via</label>
                    <select
                      value={sendLiveIntakeFormSettingsModal.sendMode}
                      onChange={(e) =>
                        setSendLiveIntakeFormSettingsModal((prev) => ({
                          ...prev,
                          sendMode: e.target.value as 'sms' | 'email' | 'both'
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="both">SMS and email</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Live intake form</label>
                    {sendLiveIntakeFormSettingsModal.loadingTemplates ? (
                      <p className="text-sm text-gray-500">Loading templates…</p>
                    ) : (
                      <select
                        value={sendLiveIntakeFormSettingsModal.intakeTemplateId}
                        onChange={(e) =>
                          setSendLiveIntakeFormSettingsModal((prev) => ({
                            ...prev,
                            intakeTemplateId: e.target.value
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        <option value="">Select a form…</option>
                        {sendLiveIntakeFormSettingsModal.templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.title}
                          </option>
                        ))}
                      </select>
                    )}
                    {!sendLiveIntakeFormSettingsModal.loadingTemplates &&
                      sendLiveIntakeFormSettingsModal.templates.length === 0 && (
                        <p className="text-xs text-amber-700 mt-2">No intake templates yet. Create one in CRM → Intake forms.</p>
                      )}
                  </div>

                  {(sendLiveIntakeFormSettingsModal.sendMode === 'sms' ||
                    sendLiveIntakeFormSettingsModal.sendMode === 'both') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SMS message</label>
                      <textarea
                        ref={intakeSmsTextareaRef}
                        rows={5}
                        value={sendLiveIntakeFormSettingsModal.intakeSmsBody}
                        onChange={(e) =>
                          setSendLiveIntakeFormSettingsModal((prev) => ({
                            ...prev,
                            intakeSmsBody: e.target.value
                          }))
                        }
                        placeholder="Leave blank for the default SMS, or write your own…"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      {(() => {
                        const availableVariables = getAvailableVariables(
                          sendLiveIntakeFormSettingsModal.actionId || ''
                        );
                        if (availableVariables.length > 0) {
                          return (
                            <div className="mt-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Insert variable</label>
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    insertSendLiveIntakeVariable('intakeSmsBody', e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent"
                                defaultValue=""
                              >
                                <option value="">Choose a variable to insert…</option>
                                {availableVariables.map((variable) => (
                                  <option key={variable.value} value={variable.value} title={variable.description}>
                                    {variable.label} — {variable.value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }
                        return (
                          <p className="text-xs text-yellow-700 mt-2">
                            Connect this action upstream to a trigger (or paths that carry data) to see variables here.
                          </p>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty to use the built-in message. Variables are replaced when the automation runs.
                      </p>
                    </div>
                  )}

                  {(sendLiveIntakeFormSettingsModal.sendMode === 'email' ||
                    sendLiveIntakeFormSettingsModal.sendMode === 'both') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email subject</label>
                        <input
                          ref={intakeEmailSubjectInputRef}
                          type="text"
                          value={sendLiveIntakeFormSettingsModal.intakeEmailSubject}
                          onChange={(e) =>
                            setSendLiveIntakeFormSettingsModal((prev) => ({
                              ...prev,
                              intakeEmailSubject: e.target.value
                            }))
                          }
                          placeholder="Leave blank for the default subject…"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        {(() => {
                          const availableVariables = getAvailableVariables(
                            sendLiveIntakeFormSettingsModal.actionId || ''
                          );
                          if (availableVariables.length > 0) {
                            return (
                              <div className="mt-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Insert variable</label>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      insertSendLiveIntakeVariable('intakeEmailSubject', e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent"
                                  defaultValue=""
                                >
                                  <option value="">Choose a variable to insert…</option>
                                  {availableVariables.map((variable) => (
                                    <option key={variable.value} value={variable.value} title={variable.description}>
                                      {variable.label} — {variable.value}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email body</label>
                        <textarea
                          ref={intakeEmailBodyTextareaRef}
                          rows={8}
                          value={sendLiveIntakeFormSettingsModal.intakeEmailBody}
                          onChange={(e) =>
                            setSendLiveIntakeFormSettingsModal((prev) => ({
                              ...prev,
                              intakeEmailBody: e.target.value
                            }))
                          }
                          placeholder="Leave blank for the default email body, or write plain text…"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        {(() => {
                          const availableVariables = getAvailableVariables(
                            sendLiveIntakeFormSettingsModal.actionId || ''
                          );
                          if (availableVariables.length > 0) {
                            return (
                              <div className="mt-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Insert variable</label>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      insertSendLiveIntakeVariable('intakeEmailBody', e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent"
                                  defaultValue=""
                                >
                                  <option value="">Choose a variable to insert…</option>
                                  {availableVariables.map((variable) => (
                                    <option key={variable.value} value={variable.value} title={variable.description}>
                                      {variable.label} — {variable.value}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <p className="text-xs text-gray-500 mt-1">
                          Plain text; line breaks are kept. Leave empty for the built-in body. Use{' '}
                          <span className="font-mono">{'{form_url}'}</span> for a clickable link.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setSendLiveIntakeFormSettingsModal({
                        isOpen: false,
                        actionId: null,
                        sendMode: 'email',
                        intakeTemplateId: '',
                        templates: [],
                        loadingTemplates: false,
                        intakeSmsBody: '',
                        intakeEmailSubject: '',
                        intakeEmailBody: ''
                      })
                    }
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const aid = sendLiveIntakeFormSettingsModal.actionId;
                      if (!aid || !selectedAutomation?.id) return;
                      if (!hasUpstreamCreateCrmLead(aid)) {
                        setError('Connect Create CRM Lead before this action in the flow.');
                        return;
                      }
                      const tid = sendLiveIntakeFormSettingsModal.intakeTemplateId;
                      if (!tid) {
                        setError('Select a live intake form.');
                        return;
                      }
                      const title =
                        sendLiveIntakeFormSettingsModal.templates.find((t) => t.id === tid)?.title || '';
                      const sendMode = sendLiveIntakeFormSettingsModal.sendMode;
                      const intakeSmsBody = sendLiveIntakeFormSettingsModal.intakeSmsBody.trim();
                      const intakeEmailSubject = sendLiveIntakeFormSettingsModal.intakeEmailSubject.trim();
                      const intakeEmailBody = sendLiveIntakeFormSettingsModal.intakeEmailBody.trim();
                      try {
                        const updatedActions = actions.map((action) =>
                          action.id === aid
                            ? {
                                ...action,
                                config: {
                                  ...action.config,
                                  intake_template_id: tid,
                                  intake_template_title: title,
                                  send_mode: sendMode,
                                  intake_sms_body: intakeSmsBody,
                                  intake_email_subject: intakeEmailSubject,
                                  intake_email_body: intakeEmailBody
                                }
                              }
                            : action
                        );
                        setActions(updatedActions);
                        const persistContext = getActivePersistContext();
                        if (!persistContext) {
                          setError('Unable to save — please try again.');
                          return;
                        }
                        await saveAutomationActions(persistContext.automationId, updatedActions);
                        setSendLiveIntakeFormSettingsModal({
                          isOpen: false,
                          actionId: null,
                          sendMode: 'email',
                          intakeTemplateId: '',
                          templates: [],
                          loadingTemplates: false,
                          intakeSmsBody: '',
                          intakeEmailSubject: '',
                          intakeEmailBody: ''
                        });
                      } catch (err) {
                        console.error(err);
                        setError('Failed to save Send Live Form settings');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Task Action Settings Modal - Available for both desktop and mobile */}
      {createTaskActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setCreateTaskActionSettingsModal({ 
            isOpen: false, 
            actionId: null, 
            selectedBoardId: '',
            selectedColumnId: '',
            taskTitle: '',
            taskDescription: '',
            taskUrgency: 'medium',
            taskStatus: 'pending',
            scheduleOffset: 1,
            scheduleUnit: 'days',
            boards: [],
            columns: []
          })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              createTaskActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setCreateTaskActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        selectedBoardId: '',
                        selectedColumnId: '',
                        taskTitle: '',
                        taskDescription: '',
                        taskUrgency: 'medium',
                        taskStatus: 'pending',
                        scheduleOffset: 1,
                        scheduleUnit: 'days',
                        boards: [],
                        columns: []
                      })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Create Task Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                <button
                  onClick={() => {
                      if (createTaskActionSettingsModal.actionId) {
                        setActions(prev => prev.filter(action => action.id !== createTaskActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                          connection.fromNodeId !== createTaskActionSettingsModal.actionId && 
                          connection.toNodeId !== createTaskActionSettingsModal.actionId
                        ));
                        setCreateTaskActionSettingsModal({ 
                          isOpen: false, 
                          actionId: null, 
                          selectedBoardId: '',
                          selectedColumnId: '',
                          taskTitle: '',
                          taskDescription: '',
                          taskUrgency: 'medium',
                          taskStatus: 'pending',
                          scheduleOffset: 1,
                          scheduleUnit: 'days',
                          boards: [],
                          columns: []
                        });
                      }
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              {/* Board Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Board
                </label>
                <select
                  value={createTaskActionSettingsModal.selectedBoardId}
                  onChange={(e) => {
                    const boardId = e.target.value;
                    console.log('🎯 Board selected:', boardId);
                    setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                      selectedBoardId: boardId,
                      selectedColumnId: '' // Reset column when board changes
                    }));
                    
                    // Load columns for the selected board
                    if (boardId && selectedBusiness?.id) {
                      console.log('🎯 Loading columns for board:', boardId);
                      getBoardWithColumns(boardId).then(boardWithColumns => {
                        console.log('🎯 Board with columns:', boardWithColumns);
                        console.log('🎯 Columns found:', boardWithColumns.columns.length);
                        setCreateTaskActionSettingsModal(prev => ({
                          ...prev,
                          columns: boardWithColumns.columns.map(column => ({
                            id: column.id,
                            title: column.title
                          }))
                        }));
                      }).catch(error => {
                        console.error('❌ Error loading columns:', error);
                      });
                    } else {
                      console.log('🎯 No board selected or no business ID');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Choose a board...</option>
                  {createTaskActionSettingsModal.boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.board_title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select List
                </label>
                {console.log('🎯 Rendering column dropdown with columns:', createTaskActionSettingsModal.columns.length)}
                <select
                  value={createTaskActionSettingsModal.selectedColumnId}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    selectedColumnId: e.target.value 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={!createTaskActionSettingsModal.selectedBoardId}
                >
                  <option value="">Choose a list...</option>
                  {createTaskActionSettingsModal.columns.map((column) => (
                    <option key={column.id} value={column.id}>
                      {column.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Available columns: {createTaskActionSettingsModal.columns.length}
                </p>
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={createTaskActionSettingsModal.taskTitle}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskTitle: e.target.value 
                  }))}
                  placeholder="Enter task title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                
                {/* Variable Dropdown for Task Title */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  console.log('🎯 Available variables for task title:', availableVariables.length);
                  console.log('🎯 Current columns state:', createTaskActionSettingsModal.columns.length);
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                </label>
                <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task title
                              const currentValue = createTaskActionSettingsModal.taskTitle;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                                taskTitle: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Task Description
                  </label>
                  {(() => {
                    const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                    return availableVariables.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setTaskComponentsModal({
                          isOpen: true,
                          currentDescription: createTaskActionSettingsModal.taskDescription
                        })}
                        className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50"
                        title="Add Components"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    ) : null;
                  })()}
                </div>
                <textarea
                  value={createTaskActionSettingsModal.taskDescription}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskDescription: e.target.value 
                  }))}
                  placeholder="Enter task description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
                
                {/* Variable Dropdown for Task Description */}
                {(() => {
                  const availableVariables = getAvailableVariables(createTaskActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              // Insert variable into task description
                              const currentValue = createTaskActionSettingsModal.taskDescription;
                              const newValue = currentValue + e.target.value;
                              setCreateTaskActionSettingsModal(prev => ({ 
                                ...prev, 
                                taskDescription: newValue 
                              }));
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
              </div>

              {/* Task Urgency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency Level
                </label>
                <select
                  value={createTaskActionSettingsModal.taskUrgency}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskUrgency: e.target.value as 'low' | 'medium' | 'high' | 'alert'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="alert">Alert</option>
                </select>
              </div>

              {/* Task Status and Scheduling */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Status
                </label>
                <select
                  value={createTaskActionSettingsModal.taskStatus}
                  onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                    ...prev, 
                    taskStatus: e.target.value as 'pending' | 'scheduled'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {/* Scheduling Options - only show when status is scheduled */}
              {createTaskActionSettingsModal.taskStatus === 'scheduled' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Task
                    </label>
                    <div className="flex gap-2">
                <input
                        type="number"
                        min="1"
                        value={createTaskActionSettingsModal.scheduleOffset}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleOffset: parseInt(e.target.value) || 1
                        }))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <select
                        value={createTaskActionSettingsModal.scheduleUnit}
                        onChange={(e) => setCreateTaskActionSettingsModal(prev => ({ 
                          ...prev, 
                          scheduleUnit: e.target.value as 'hours' | 'days' | 'weeks'
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="hours">Hour(s)</option>
                        <option value="days">Day(s)</option>
                        <option value="weeks">Week(s)</option>
                      </select>
                    </div>
                <p className="text-xs text-gray-500 mt-1">
                      Task will be scheduled {createTaskActionSettingsModal.scheduleOffset} {createTaskActionSettingsModal.scheduleUnit} from when the automation runs
                </p>
                  </div>
                </div>
              )}
              </div>
            </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                    onClick={() => setCreateTaskActionSettingsModal({ 
                      isOpen: false, 
                      actionId: null, 
                      selectedBoardId: '',
                      selectedColumnId: '',
                      taskTitle: '',
                      taskDescription: '',
                      taskUrgency: 'medium',
                      taskStatus: 'pending',
                      scheduleOffset: 1,
                      scheduleUnit: 'days',
                      boards: [],
                      columns: []
                    })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                      // Save the create task action configuration
                      if (createTaskActionSettingsModal.actionId) {
                        setActions(prev => prev.map(action => {
                          if (action.id === createTaskActionSettingsModal.actionId) {
                            return {
                          ...action, 
                          config: { 
                                selectedBoardId: createTaskActionSettingsModal.selectedBoardId,
                                selectedColumnId: createTaskActionSettingsModal.selectedColumnId,
                                taskTitle: createTaskActionSettingsModal.taskTitle,
                                taskDescription: createTaskActionSettingsModal.taskDescription,
                                taskUrgency: createTaskActionSettingsModal.taskUrgency,
                                taskStatus: createTaskActionSettingsModal.taskStatus,
                                scheduleOffset: createTaskActionSettingsModal.scheduleOffset,
                                scheduleUnit: createTaskActionSettingsModal.scheduleUnit,
                                uiLabel: `Create Task: ${createTaskActionSettingsModal.taskTitle || 'Untitled'}`
                              }
                            };
                          }
                          return action;
                        }));
                      }

                  // Close modal
                      setCreateTaskActionSettingsModal({ 
                        isOpen: false, 
                        actionId: null, 
                        selectedBoardId: '',
                        selectedColumnId: '',
                        taskTitle: '',
                        taskDescription: '',
                        taskUrgency: 'medium',
                        taskStatus: 'pending',
                        scheduleOffset: 1,
                        scheduleUnit: 'days',
                        boards: [],
                        columns: []
                      });
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Check Action Settings Modal - Available for both desktop and mobile */}
      {checkActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' })}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              checkActionSettingsModal.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Check Action Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                <button
                  onClick={() => {
                      if (checkActionSettingsModal.actionId) {
                        setActions(prev => prev.filter(action => action.id !== checkActionSettingsModal.actionId));
                      setWireConnections(prev => prev.filter(connection => 
                          connection.fromNodeId !== checkActionSettingsModal.actionId && 
                          connection.toNodeId !== checkActionSettingsModal.actionId
                      ));
                        setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' });
                    }
                  }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  title="Delete Action"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              {/* Check Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Type
                </label>
                <select
                  value={checkActionSettingsModal.checkType}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    checkType: e.target.value as 'feedback'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="feedback">Customer Feedback</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose what type of check to perform
                </p>
              </div>

              {/* If Completed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If Check Completed
                </label>
                <select
                  value={checkActionSettingsModal.ifCompleted}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    ifCompleted: e.target.value as 'stop' | 'continue'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="stop">Stop Automation</option>
                  <option value="continue">Continue to Next Action</option>
                </select>
              </div>

              {/* If Not Completed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If Check Not Completed
                </label>
                <select
                  value={checkActionSettingsModal.ifNotCompleted}
                  onChange={(e) => setCheckActionSettingsModal(prev => ({ 
                    ...prev, 
                    ifNotCompleted: e.target.value as 'stop' | 'continue'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="stop">Stop Automation</option>
                  <option value="continue">Continue to Next Action</option>
                </select>
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === checkActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === checkActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Feedback Check', 'Quality Control', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
              </div>
            </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
              <button
                    onClick={() => setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                      if (!checkActionSettingsModal.actionId) return;

                  // Update the action with the configuration
                  setActions(prev => prev.map(action => 
                        action.id === checkActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config,
                                checkType: checkActionSettingsModal.checkType,
                                ifCompleted: checkActionSettingsModal.ifCompleted,
                                ifNotCompleted: checkActionSettingsModal.ifNotCompleted
                          } 
                        }
                      : action
                  ));

                  // Close modal
                      setCheckActionSettingsModal({ isOpen: false, actionId: null, checkType: 'feedback', ifCompleted: 'stop', ifNotCompleted: 'continue' });
                }}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Feedback Agent Action Settings Modal */}
      {feedbackAgentActionSettingsModal.isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setFeedbackAgentActionSettingsModal({ isOpen: false, actionId: null, agentId: '', agents: [], loading: false })}
        >
          <div
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl translate-x-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setFeedbackAgentActionSettingsModal({ isOpen: false, actionId: null, agentId: '', agents: [], loading: false })}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Feedback Agent Settings
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      if (feedbackAgentActionSettingsModal.actionId) {
                        setActions(prev => prev.filter(action => action.id !== feedbackAgentActionSettingsModal.actionId));
                        setWireConnections(prev => prev.filter(connection =>
                          connection.fromNodeId !== feedbackAgentActionSettingsModal.actionId &&
                          connection.toNodeId !== feedbackAgentActionSettingsModal.actionId
                        ));
                        setFeedbackAgentActionSettingsModal({ isOpen: false, actionId: null, agentId: '', agents: [], loading: false });
                      }
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Delete Action"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-900 leading-relaxed">
                      When this step runs, the selected AI agent places <span className="font-semibold">one call</span> to the booking's customer, asks how the service went, and records their star rating in your feedback system — pair it with a Check action to stop once feedback is in.
                    </p>
                  </div>

                  {/* The agent writes into the feedback system, so calls are skipped
                      while it's off — surface that status here so skips aren't a mystery. */}
                  {feedbackAgentActionSettingsModal.feedbackEnabled === false ? (
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-900">Feedback system is turned off</p>
                        <p className="text-sm text-red-800 mt-0.5 leading-relaxed">
                          This step will be skipped on every run — the agent has nowhere to save ratings. Turn it on in <span className="font-medium">Settings → Feedback</span> to let the agent call.
                        </p>
                      </div>
                    </div>
                  ) : feedbackAgentActionSettingsModal.feedbackEnabled === true ? (
                    <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2.5">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-900">
                        Feedback system is on — ratings the agent collects will be recorded.
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Feedback agent
                    </label>
                    {feedbackAgentActionSettingsModal.loading ? (
                      <p className="text-sm text-gray-500">Loading agents…</p>
                    ) : feedbackAgentActionSettingsModal.agents.length === 0 ? (
                      <div className="p-4 rounded-lg bg-white border border-gray-200">
                        <p className="text-sm text-gray-700">
                          No feedback agents yet. Create one first: tap the <span className="font-semibold">+</span> on the Automations page → <span className="font-semibold">Create agent</span> → <span className="font-semibold">Feedback Agent</span>.
                        </p>
                      </div>
                    ) : (
                      <>
                        <select
                          value={feedbackAgentActionSettingsModal.agentId}
                          onChange={(e) => setFeedbackAgentActionSettingsModal(prev => ({ ...prev, agentId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        >
                          <option value="">Choose a feedback agent…</option>
                          {feedbackAgentActionSettingsModal.agents.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}{a.status !== 'live' ? ' (draft)' : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Only live agents place calls — if the agent is in draft when this step runs, the call is skipped and the automation continues.
                        </p>
                        {(() => {
                          const chosen = feedbackAgentActionSettingsModal.agents.find(a => a.id === feedbackAgentActionSettingsModal.agentId);
                          return chosen && chosen.status !== 'live' ? (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                              "{chosen.name}" is currently a draft — set it live in the agent builder so this step actually calls.
                            </p>
                          ) : null;
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
                  <button
                    onClick={() => setFeedbackAgentActionSettingsModal({ isOpen: false, actionId: null, agentId: '', agents: [], loading: false })}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!feedbackAgentActionSettingsModal.actionId) return;
                      const chosen = feedbackAgentActionSettingsModal.agents.find(a => a.id === feedbackAgentActionSettingsModal.agentId);
                      setActions(prev => prev.map(action =>
                        action.id === feedbackAgentActionSettingsModal.actionId
                          ? {
                              ...action,
                              config: {
                                ...action.config,
                                agentId: feedbackAgentActionSettingsModal.agentId,
                                agentName: chosen?.name || ''
                              }
                            }
                          : action
                      ));
                      setFeedbackAgentActionSettingsModal({ isOpen: false, actionId: null, agentId: '', agents: [], loading: false });
                    }}
                    disabled={!feedbackAgentActionSettingsModal.agentId}
                    className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Task Components Modal - Available for both desktop and mobile */}
      {taskComponentsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Task Components</h3>
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Click on any component below to add it to your task description:
              </p>
              
              <div className="space-y-3">
                {/* Customer Follow Component */}
                <button
                  onClick={() => {
                    const customerFollowComponent = `
[customer_follow]
Customer: {customer_name}
Booking: {booking_number}
Vehicle: {vehicle_info}
Service: {booking_title}
Total: {grand_total}
[/customer_follow]`;
                    
                    const newDescription = taskComponentsModal.currentDescription + customerFollowComponent;
                    setCreateTaskActionSettingsModal(prev => ({
                      ...prev,
                      taskDescription: newDescription
                    }));
                    setTaskComponentsModal({ isOpen: false, currentDescription: '' });
                  }}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Customer Follow</span>
                        <p className="text-xs text-gray-600">Add customer and booking information</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setTaskComponentsModal({ isOpen: false, currentDescription: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Email Template Selector Modal - Available for both desktop and mobile */}
      {emailTemplateSelector.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[999999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              emailTemplateSelector.isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
              <button
                      onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                      className="text-gray-400 hover:text-white transition-colors"
              >
                      <X className="h-6 w-6" />
              </button>
                    <h2 className="text-xl font-semibold text-white">
                      Select Email Template
                    </h2>
            </div>
          </div>
        </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6">
              {emailTemplateSelector.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading templates...</div>
                </div>
              ) : emailTemplateSelector.templates.filter((template) => !template.one_off).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No Email Templates Found</p>
                  <p className="text-sm">Create email templates in the Marketing page first.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailTemplateSelector.templates.filter((template) => !template.one_off).map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          {template.subject && (
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Subject:</strong> {template.subject}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              template.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : template.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {template.status}
                            </span>
                            {template.category && (
                              <span className="text-xs text-gray-500">
                                {template.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {new Date(template.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
            </div>

            {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setEmailTemplateSelector(prev => ({ ...prev, isOpen: false }))}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Title Modal - Available for both desktop and mobile */}
      {isEditTitleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Automation Title</h2>
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter automation title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editTitle.trim()) {
                    handleSaveTitle();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={!editTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Automation Modal - Available for both desktop and mobile */}
      {isDeleteAutomationModalOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setIsDeleteAutomationModalOpen(false)}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[500px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              isDeleteAutomationModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setIsDeleteAutomationModalOpen(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Delete Automation
                    </h2>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to delete this automation? This action cannot be undone.
                  </p>
                  {selectedAutomation && (
                    <div className="p-4 bg-white border border-gray-200 rounded-lg">
                      <p className="font-medium text-gray-900 mb-1">{selectedAutomation.name}</p>
                      <p className="text-sm text-gray-600">
                        Status: {selectedAutomation.is_live ? 'Live' : 'Draft'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsDeleteAutomationModalOpen(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteLoading}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Execution Details Modal - Available for both desktop and mobile */}
      {isExecutionDetailsOpen && selectedExecutionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-5/6">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Execution Details</h2>
              <button
                onClick={handleCloseExecutionDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 h-full overflow-y-auto">
              {loadingSteps ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading execution details...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Execution Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Execution Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            const actualStatus = execution ? getActualExecutionStatus(execution) : 'pending';
                            return (
                              <>
                                {getStatusIcon(actualStatus)}
                                <span className="font-medium capitalize">
                                  {actualStatus}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Started</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id.toString() === selectedExecutionId)?.started_at && 
                            new Date(executions.find(e => e.id.toString() === selectedExecutionId)!.started_at).toLocaleString()
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="font-medium mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id.toString() === selectedExecutionId);
                            if (!execution?.started_at) return 'N/A';
                            
                            const start = new Date(execution.started_at);
                            const end = execution.completed_at ? new Date(execution.completed_at) : new Date();
                            const duration = Math.round((end.getTime() - start.getTime()) / 1000);
                            
                            if (duration < 60) return `${duration}s`;
                            if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
                            return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Steps</div>
                        <div className="font-medium mt-1">
                          {executionSteps.length} total
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Execution Steps */}
                  <div>
                    <AutomationStepTimeline
                      steps={executionSteps as any}
                      execution={executions.find(e => e.id.toString() === selectedExecutionId)}
                    />
                    {false && (
                    <div className="space-y-4">
                      {executionSteps.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No execution steps found</p>
                        </div>
                      ) : (
                        executionSteps.map((step, index) => {
                          const isCompleted = step.status === 'completed';
                          const isFailed = step.status === 'failed';
                          const isRunning = step.status === 'running';
                          
                          return (
                            <div key={step.id} className="relative">
                              {/* Connector Line */}
                              {index < executionSteps.length - 1 && (
                                <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200"></div>
                              )}
                              
                              <div className={`relative flex items-start gap-4 p-4 rounded-lg border ${
                                isFailed ? 'border-red-200 bg-red-50' :
                                isCompleted ? 'border-green-200 bg-green-50' :
                                isRunning ? 'border-blue-200 bg-blue-50' :
                                'border-gray-200 bg-gray-50'
                              } transition-all duration-300`}>
                                {/* Step Number & Icon */}
                                <div className="flex-shrink-0 flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-medium text-sm ${
                                    isFailed ? 'border-red-300 bg-red-100 text-red-800' :
                                    isCompleted ? 'border-green-300 bg-green-100 text-green-800' :
                                    isRunning ? 'border-blue-300 bg-blue-100 text-blue-800' :
                                    'border-gray-300 bg-gray-100 text-gray-800'
                                  }`}>
                                    {isFailed ? <XCircle className="h-4 w-4" /> :
                                     isCompleted ? <CheckCircle className="h-4 w-4" /> :
                                     isRunning ? <Clock className="h-4 w-4 animate-spin" /> :
                                     (index + 1)}
                                  </div>
                                </div>

                                {/* Step Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h4 className={`font-medium ${
                                        isFailed ? 'text-red-800' :
                                        isCompleted ? 'text-green-800' :
                                        isRunning ? 'text-blue-800' :
                                        'text-gray-800'
                                      }`}>
                                        {step.action_type === 'email' && '📧 Send Email'}
                                        {step.action_type === 'sms' && '💬 Send SMS'}
                                        {step.action_type === 'delay' && '⏱️ Wait'}
                                        {step.action_type === 'create_crm_lead' && '🎯 Create CRM Lead'}
                                        {step.action_type === 'check' && '✅ Check'}
                                        {step.action_type === 'create_task' && '📋 Create Task'}
                                        {step.action_type === 'assign_tags' && '🏷️ Assign Tags'}
                                        {step.action_type === 'add_to_automation' && '🔗 Add to Automation'}
                                        {step.action_type === 'send_live_intake_form' && '📋 Send Live Form'}
                                        {step.action_type === 'feedback_agent' && '⭐ Feedback Agent'}
                                        {!['email', 'sms', 'delay', 'create_crm_lead', 'check', 'create_task', 'assign_tags', 'add_to_automation', 'send_live_intake_form', 'feedback_agent'].includes(step.action_type) &&
                                          `${step.action_type.charAt(0).toUpperCase() + step.action_type.slice(1)} Action`
                                        }
                                      </h4>
                                      <div className="text-sm text-gray-500 space-y-1">
                                        {step.started_at && (
                                          <div>Started: {new Date(step.started_at).toLocaleString()}</div>
                                        )}
                                        {step.completed_at && (
                                          <div>Completed: {new Date(step.completed_at).toLocaleString()}</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      Step {index + 1}
                                    </div>
                                  </div>

                                  {/* Error Message */}
                                  {step.error_message && (
                                    <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                                      <strong>Error:</strong> {step.error_message}
                                    </div>
                                  )}

                                  {/* Result Data */}
                                  {step.result_data && Object.keys(step.result_data).length > 0 && (
                                    <details className="mt-2">
                                      <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                                        View Result Data
                                      </summary>
                                      <div className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs overflow-auto max-h-32">
                                        <pre>{JSON.stringify(step.result_data, null, 2)}</pre>
                                      </div>
                                    </details>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trigger Settings Modal - Available for both desktop and mobile */}
      {isTriggerSettingsOpen && selectedTrigger && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={handleCloseTriggerSettings}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              isTriggerSettingsOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header - Clean Modern Design */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
              <button
                onClick={handleCloseTriggerSettings}
                      className="text-gray-400 hover:text-white transition-colors"
              >
                      <X className="h-6 w-6" />
              </button>
                    <h2 className="text-xl font-semibold text-white">
                      Trigger Settings
                    </h2>
                  </div>
                  {/* Delete button on the right */}
                  <button
                    onClick={() => selectedTriggerId && handleDeleteTrigger(selectedTriggerId)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    title="Delete Trigger"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
            </div>
            
              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
              {selectedTrigger.type === 'booking' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Booking Event Type
                  </label>
                  <select
                    value={selectedTrigger.config.eventType}
                    onChange={(e) => {
                      const newEventType = e.target.value;
                      setTriggers(prev => prev.map(trigger => 
                        trigger.id === selectedTriggerId 
                          ? { ...trigger, config: { ...trigger.config, eventType: newEventType } }
                          : trigger
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="created">Created</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="canceled">Canceled</option>
                    <option value="deleted">Deleted</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              )}

              {selectedTrigger.type === 'feedback' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Star Rating
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    This trigger will activate when a customer submits feedback with an average rating equal to or greater than the selected value.
                  </p>
                  <select
                    value={selectedTrigger.config.minRating || 5}
                    onChange={(e) => {
                      const newMinRating = parseInt(e.target.value);
                      setTriggers(prev => prev.map(trigger => 
                        trigger.id === selectedTriggerId 
                          ? { ...trigger, config: { ...trigger.config, minRating: newMinRating } }
                          : trigger
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="1">1 Star or More</option>
                    <option value="2">2 Stars or More</option>
                    <option value="3">3 Stars or More</option>
                    <option value="4">4 Stars or More</option>
                    <option value="5">5 Stars</option>
                  </select>
                  <div className="mt-3 flex items-center space-x-1 text-yellow-600">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= (selectedTrigger.config.minRating || 5)
                            ? 'fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">
                      or more
                    </span>
                  </div>
                </div>
              )}

              {selectedTrigger.type === 'form_submission' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Form Type
                    </label>
                    <select
                      value={selectedTrigger.config.formType || ''}
                      onChange={(e) => {
                        const newFormType = e.target.value;
                        setTriggers(prev => prev.map(trigger => 
                          trigger.id === selectedTriggerId 
                            ? { 
                                ...trigger, 
                                config: { 
                                  ...trigger.config, 
                                  formType: newFormType,
                                  formId: '' // Reset form ID when changing form type
                                } 
                              }
                            : trigger
                        ));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select form type...</option>
                      <option value="lead_form">Lead Form</option>
                      <option value="online_booking_banner">Online Booking Banner</option>
                    </select>
                  </div>

                  {selectedTrigger.config.formType === 'lead_form' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Form
                      </label>
                      <select
                        value={selectedTrigger.config.formId || ''}
                        onChange={(e) => {
                          const newFormId = e.target.value;
                          setTriggers(prev => prev.map(trigger => 
                            trigger.id === selectedTriggerId 
                              ? { ...trigger, config: { ...trigger.config, formId: newFormId } }
                              : trigger
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select lead form...</option>
                        {leadForms.map((form) => (
                          <option key={form.id} value={form.id}>
                            {form.name || form.title || 'Untitled Form'}
                          </option>
                        ))}
                      </select>
                      
                      {loadingLeadForms && (
                        <p className="text-xs text-gray-500 mt-1">
                          Loading lead forms...
                        </p>
                      )}
                      
                      {!loadingLeadForms && leadForms.length === 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          No lead forms found. Create one in CRM app settings first to use this trigger.
                        </p>
                      )}
                    </div>
                  )}

                  {selectedTrigger.config.formType === 'online_booking_banner' && (
                    <div className="mb-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-blue-800">
                              Online Booking Banner Selected
                            </h4>
                            <p className="text-sm text-blue-700 mt-1">
                              This trigger will activate when customers submit the lead form in your online booking popup banner. 
                              Make sure you have enabled the popup banner in your Online Bookings settings.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {selectedTrigger.type === 'enter_automation' && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <GitBranch className="h-5 w-5 text-teal-700" />
                    <h4 className="font-medium text-teal-900">Receiver trigger</h4>
                  </div>
                  <p className="text-sm text-teal-800">
                    This automation does not start from bookings, forms, or webhooks. It runs when another automation uses the
                    <span className="font-semibold"> Add to Automation </span>
                    action and selects this flow. All data from the parent run is passed into this automation&apos;s context.
                  </p>
                </div>
              )}

              {selectedTrigger.type === 'enter_crm_stage' && (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="h-5 w-5 text-indigo-700" />
                      <h4 className="font-medium text-indigo-900">Enter CRM Stage</h4>
                    </div>
                    <p className="text-sm text-indigo-800">
                      Runs when an opportunity enters the selected pipeline stage (including new leads created directly in that stage).
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pipeline *</label>
                    <select
                      value={selectedTrigger.config?.pipeline_id ?? ''}
                      onChange={(e) => {
                        const pipelineId = e.target.value ? parseInt(e.target.value, 10) : null;
                        const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
                        setTriggers((prev) =>
                          prev.map((trigger) =>
                            trigger.id === selectedTriggerId
                              ? {
                                  ...trigger,
                                  config: {
                                    ...trigger.config,
                                    pipeline_id: pipelineId,
                                    pipeline_name: selectedPipeline?.pipeline_name || '',
                                    stage_id: null,
                                    stage_name: '',
                                    uiLabel: '',
                                  },
                                }
                              : trigger
                          )
                        );
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={loadingPipelines}
                    >
                      <option value="">Select a pipeline...</option>
                      {pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.pipeline_name}
                        </option>
                      ))}
                    </select>
                    {loadingPipelines && (
                      <p className="text-xs text-gray-500 mt-1">Loading pipelines...</p>
                    )}
                  </div>

                  {selectedTrigger.config?.pipeline_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stage *</label>
                      <select
                        value={selectedTrigger.config?.stage_id ?? ''}
                        onChange={(e) => {
                          const stageId = e.target.value ? parseInt(e.target.value, 10) : null;
                          const selectedPipeline = pipelines.find(
                            (p) => p.id === selectedTrigger.config?.pipeline_id
                          );
                          const selectedStage = selectedPipeline?.stages?.find((s) => s.id === stageId);
                          const pipelineName = selectedPipeline?.pipeline_name || '';
                          const stageName = selectedStage?.name || '';
                          const uiLabel =
                            pipelineName && stageName ? `${pipelineName} → ${stageName}` : '';
                          setTriggers((prev) =>
                            prev.map((trigger) =>
                              trigger.id === selectedTriggerId
                                ? {
                                    ...trigger,
                                    config: {
                                      ...trigger.config,
                                      stage_id: stageId,
                                      stage_name: stageName,
                                      pipeline_name: pipelineName,
                                      uiLabel,
                                    },
                                  }
                                : trigger
                            )
                          );
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Select a stage...</option>
                        {pipelines
                          .find((p) => p.id === selectedTrigger.config?.pipeline_id)
                          ?.stages?.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {selectedTrigger.type === 'webhook' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Connection type</label>
                    <select
                      value={selectedTrigger.config?.webhook_delivery === 'zapier' ? 'zapier' : 'http'}
                      onChange={(e) => {
                        const mode = e.target.value as 'http' | 'zapier';
                        setTriggers((prev) =>
                          prev.map((t) => {
                            if (t.id !== selectedTriggerId) return t;
                            const nextConfig = { ...t.config };
                            if (mode === 'zapier') nextConfig.webhook_delivery = 'zapier';
                            else delete nextConfig.webhook_delivery;
                            return { ...t, config: nextConfig };
                          })
                        );
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                    >
                      <option value="http">Webhook (custom forms &amp; HTTP POST)</option>
                      <option value="zapier">Zapier webhook (Facebook Lead Ads, etc.)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Both use the same field discovery and mapping below. Zapier uses a URL with your business ID and a secret (matches the existing automation Zapier endpoint).
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium text-blue-900">
                        {selectedTrigger.config?.webhook_delivery === 'zapier' ? 'Zapier webhook URL' : 'Webhook URL'}
                      </h4>
                    </div>

                    {selectedTrigger.config?.webhook_delivery === 'zapier' ? (
                      <>
                        <div className="bg-white border border-gray-300 rounded p-3 font-mono text-xs break-all mb-2">
                          {selectedBusiness?.id && selectedTrigger.config?.webhook_secret
                            ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zapier-automation-webhook?business_id=${selectedBusiness.id}&secret=${selectedTrigger.config.webhook_secret}`
                            : 'Save the automation with a business selected to see the full URL.'}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedBusiness?.id || !selectedTrigger.config?.webhook_secret) return;
                            const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zapier-automation-webhook?business_id=${selectedBusiness.id}&secret=${selectedTrigger.config.webhook_secret}`;
                            navigator.clipboard.writeText(url);
                            alert('Zapier webhook URL copied!');
                          }}
                          disabled={!selectedBusiness?.id || !selectedTrigger.config?.webhook_secret}
                          className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium"
                        >
                          Copy Zapier webhook URL
                        </button>
                        <p className="text-xs text-gray-600 mt-3">
                          In Zapier, use <span className="font-medium">Webhooks by Zapier</span> → POST and send the lead through as-is. Field names do not need to match Nexus. Email, phone, and name are picked up automatically, including Facebook Lead Ads. This automation must be <span className="font-medium">Live</span> for runs to start.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="bg-white border border-gray-300 rounded p-3 font-mono text-xs break-all mb-2">
                          {`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-webhook/${selectedTrigger.config.webhook_id}`}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/automation-webhook/${selectedTrigger.config.webhook_id}`;
                            navigator.clipboard.writeText(url);
                            alert('Webhook URL copied!');
                          }}
                          className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Copy Webhook URL
                        </button>
                        <p className="text-xs text-gray-600 mt-3">
                          Add this URL to your website form&apos;s action or use it with AJAX/fetch to trigger this automation.
                          Spam protection is on: random-name bots are dropped before a CRM lead is created.
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Extra protection: add a hidden empty field named{' '}
                          <span className="font-mono text-gray-700">_gotcha</span> to the form. Leave it blank —
                          bots that fill every field are rejected.
                        </p>
                      </>
                    )}
                  </div>

                  {selectedTrigger.config.discovered_fields ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <h4 className="font-medium text-green-900">Fields Discovered</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedTrigger.config.discovered_fields.map((field: string) => (
                          <span key={field} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                            {field}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        These fields can be mapped into the lead title and notes. Email, phone, and name are read automatically even when the labels do not match.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-medium text-yellow-900">Waiting for First Submission</h4>
                      </div>
                      <p className="text-xs text-yellow-700">
                        Submit a test form to auto-discover fields
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={selectedTrigger.config.uiLabel || ''}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setTriggers(prev => prev.map(trigger => 
                      trigger.id === selectedTriggerId 
                        ? { ...trigger, config: { ...trigger.config, uiLabel: newUiLabel } }
                        : trigger
                    ));
                  }}
                  placeholder="e.g. 'New Customer SMS', 'Send Reminder', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this trigger on the canvas
                </p>
              </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseTriggerSettings}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Assign Tags Action Settings Modal */}
      {isSettingsOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
          onClick={() => setIsSettingsOpen(false)}
        >
          <div 
            style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              isSettingsOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setIsSettingsOpen(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Automation Settings
                    </h2>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="p-6 space-y-6">
                  {/* Customer Deliverability Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Deliverability</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Restrict when automated SMS &amp; emails are sent to customers. Messages scheduled
                      outside these hours are held and delivered at the start of the next open window
                      (in your business timezone). When off, messages send anytime.
                    </p>

                    {/* Master toggle */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Limit sending to delivery hours</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {deliveryHoursEnabled ? 'Sends are restricted to the windows below.' : 'Sends are not restricted (send anytime).'}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={deliveryHoursEnabled}
                        onClick={() => setDeliveryHoursEnabled((v) => !v)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${deliveryHoursEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${deliveryHoursEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    <div className={`space-y-3 transition-opacity ${deliveryHoursEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
                      {[
                        { key: 'monday', label: 'Monday' },
                        { key: 'tuesday', label: 'Tuesday' },
                        { key: 'wednesday', label: 'Wednesday' },
                        { key: 'thursday', label: 'Thursday' },
                        { key: 'friday', label: 'Friday' },
                        { key: 'saturday', label: 'Saturday' },
                        { key: 'sunday', label: 'Sunday' }
                      ].map((day) => {
                        const daySettings = deliverabilitySettings[day.key];
                        return (
                          <div key={day.key} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={daySettings.enabled}
                                  onChange={(e) => {
                                    setDeliverabilitySettings(prev => ({
                                      ...prev,
                                      [day.key]: {
                                        ...prev[day.key],
                                        enabled: e.target.checked
                                      }
                                    }));
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-900">{day.label}</span>
                              </label>
                            </div>
                            
                            {daySettings.enabled && (
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                  <label className="text-xs text-gray-600">From:</label>
                                  <select
                                    value={daySettings.startHour}
                                    onChange={(e) => {
                                      setDeliverabilitySettings(prev => ({
                                        ...prev,
                                        [day.key]: {
                                          ...prev[day.key],
                                          startHour: parseInt(e.target.value)
                                        }
                                      }));
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => (
                                      <option key={i} value={i}>
                                        {i.toString().padStart(2, '0')}:00
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={daySettings.startMinute}
                                    onChange={(e) => {
                                      setDeliverabilitySettings(prev => ({
                                        ...prev,
                                        [day.key]: {
                                          ...prev[day.key],
                                          startMinute: parseInt(e.target.value)
                                        }
                                      }));
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {[0, 15, 30, 45].map(min => (
                                      <option key={min} value={min}>
                                        {min.toString().padStart(2, '0')}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                <span className="text-xs text-gray-500">to</span>
                                
                                <div className="flex items-center space-x-2">
                                  <label className="text-xs text-gray-600">To:</label>
                                  <select
                                    value={daySettings.endHour}
                                    onChange={(e) => {
                                      setDeliverabilitySettings(prev => ({
                                        ...prev,
                                        [day.key]: {
                                          ...prev[day.key],
                                          endHour: parseInt(e.target.value)
                                        }
                                      }));
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => (
                                      <option key={i} value={i}>
                                        {i.toString().padStart(2, '0')}:00
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={daySettings.endMinute}
                                    onChange={(e) => {
                                      setDeliverabilitySettings(prev => ({
                                        ...prev,
                                        [day.key]: {
                                          ...prev[day.key],
                                          endMinute: parseInt(e.target.value)
                                        }
                                      }));
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {[0, 15, 30, 45].map(min => (
                                      <option key={min} value={min}>
                                        {min.toString().padStart(2, '0')}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center gap-3">
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  disabled={savingDeliveryHours}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDeliveryHours}
                  disabled={savingDeliveryHours}
                  className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {savingDeliveryHours ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {assignTagsActionSettingsModal.isOpen && ReactDOM.createPortal(
        <AssignTagsActionModal
          isOpen={assignTagsActionSettingsModal.isOpen}
          onClose={() => setAssignTagsActionSettingsModal({ 
            isOpen: false, 
            actionId: null, 
            conditions: [],
            selectedTags: []
          })}
          onSave={handleSaveAssignTagsAction}
          conditions={assignTagsActionSettingsModal.conditions}
          selectedTags={assignTagsActionSettingsModal.selectedTags}
          onConditionsChange={(conditions) => setAssignTagsActionSettingsModal(prev => ({ ...prev, conditions }))}
          onTagsChange={(selectedTags) => setAssignTagsActionSettingsModal(prev => ({ ...prev, selectedTags }))}
          businessId={selectedBusiness?.id || ''}
        />,
        document.body
      )}
    </>
  );
};

// Assign Tags Action Modal Component
interface AssignTagsActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  conditions: Array<{
    type: 'services';
    selectedServices: string[];
  }>;
  selectedTags: string[];
  onConditionsChange: (conditions: Array<{ type: 'services'; selectedServices: string[] }>) => void;
  onTagsChange: (selectedTags: string[]) => void;
  businessId: string;
}

const AssignTagsActionModal: React.FC<AssignTagsActionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  conditions,
  selectedTags,
  onConditionsChange,
  onTagsChange,
  businessId
}) => {
  const [services, setServices] = useState<any[]>([]);
  const [customerTags, setCustomerTags] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showConditionMenu, setShowConditionMenu] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState<number | null>(null);
  const [showCreateTagForm, setShowCreateTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  React.useEffect(() => {
    if (isOpen && businessId) {
      loadServices();
      loadCustomerTags();
    }
  }, [isOpen, businessId]);

  const loadServices = async () => {
    if (!businessId) return;
    try {
      setLoadingServices(true);
      const data = await getServices(businessId);
      setServices(data);
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const loadCustomerTags = async () => {
    if (!businessId) return;
    try {
      setLoadingTags(true);
      const data = await getCustomerTags(businessId);
      setCustomerTags(data);
    } catch (err) {
      console.error('Error loading customer tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleAddCondition = (type: 'services') => {
    const newCondition = {
      type,
      selectedServices: [] as string[]
    };
    onConditionsChange([...conditions, newCondition]);
    setShowConditionMenu(false);
    setShowServicesDropdown(conditions.length);
  };

  const handleRemoveCondition = (index: number) => {
    onConditionsChange(conditions.filter((_, i) => i !== index));
  };

  const handleToggleService = (conditionIndex: number, serviceId: string) => {
    const updatedConditions = [...conditions];
    const condition = updatedConditions[conditionIndex];
    if (condition.selectedServices.includes(serviceId)) {
      condition.selectedServices = condition.selectedServices.filter(id => id !== serviceId);
    } else {
      condition.selectedServices = [...condition.selectedServices, serviceId];
    }
    onConditionsChange(updatedConditions);
  };

  const handleToggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!businessId || !newTagName.trim()) return;

    try {
      setIsCreatingTag(true);
      const newTag = await createCustomerTag(businessId, newTagName, newTagColor);
      
      // Add the new tag to the list and sort
      const updatedTags = [...customerTags, newTag].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setCustomerTags(updatedTags);
      
      // Automatically select the newly created tag
      onTagsChange([...selectedTags, newTag.id]);
      
      // Reset form
      setNewTagName('');
      setNewTagColor('#3B82F6');
      setShowCreateTagForm(false);
    } catch (err) {
      console.error('Error creating tag:', err);
      alert(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setIsCreatingTag(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center md:items-start md:justify-end"
      onClick={onClose}
    >
      <div 
        style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Assign Tags Action Settings
                </h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="p-6 space-y-6">
              {/* Conditions Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If Contains
                </label>
                <div className="space-y-3">
                  {conditions.map((condition, index) => (
                    <div key={index} className="bg-white border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          {condition.type === 'services' ? 'Services' : condition.type}
                        </span>
                        <button
                          onClick={() => handleRemoveCondition(index)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      {condition.type === 'services' && (
                        <div className="relative">
                          <button
                            onClick={() => setShowServicesDropdown(showServicesDropdown === index ? null : index)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm bg-white hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span>
                              {condition.selectedServices.length > 0
                                ? `${condition.selectedServices.length} service(s) selected`
                                : 'Select services...'}
                            </span>
                            <ChevronRight className={`h-4 w-4 transition-transform ${showServicesDropdown === index ? 'rotate-90' : ''}`} />
                          </button>
                          {showServicesDropdown === index && (
                            <div className="mt-2 border border-gray-300 rounded-lg bg-white max-h-60 overflow-y-auto">
                              {loadingServices ? (
                                <div className="p-4 text-center text-sm text-gray-500">Loading services...</div>
                              ) : services.length === 0 ? (
                                <div className="p-4 text-center text-sm text-gray-500">No services available</div>
                              ) : (() => {
                                // Group services by category
                                const groupedServices = services.reduce((acc: any, service: any) => {
                                  const categoryId = service.category_id || 'uncategorized';
                                  const categoryName = service.category?.name || 'Uncategorized';
                                  
                                  if (!acc[categoryId]) {
                                    acc[categoryId] = {
                                      name: categoryName,
                                      services: []
                                    };
                                  }
                                  acc[categoryId].services.push(service);
                                  return acc;
                                }, {});

                                // Sort categories: Uncategorized last, others by name
                                const sortedCategories = Object.entries(groupedServices).sort(([idA, catA]: any, [idB, catB]: any) => {
                                  if (idA === 'uncategorized') return 1;
                                  if (idB === 'uncategorized') return -1;
                                  return catA.name.localeCompare(catB.name);
                                });

                                return (
                                  <div>
                                    {sortedCategories.map(([categoryId, category]: any) => (
                                      <div key={categoryId}>
                                        <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 sticky top-0">
                                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                            {category.name}
                                          </span>
                                        </div>
                                        {category.services.map((service: any) => (
                                          <label
                                            key={service.id}
                                            className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={condition.selectedServices.includes(service.id)}
                                              onChange={() => handleToggleService(index, service.id)}
                                              className="mr-3"
                                            />
                                            <span className="text-sm text-gray-700">{service.name}</span>
                                          </label>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="relative">
                    <button
                      onClick={() => setShowConditionMenu(!showConditionMenu)}
                      className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Condition
                    </button>
                    {showConditionMenu && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-[200px]">
                        <button
                          onClick={() => handleAddCondition('services')}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700"
                        >
                          Services
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags Selection Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Tags to Assign
                  </label>
                  <button
                    onClick={() => setShowCreateTagForm(!showCreateTagForm)}
                    className="flex items-center gap-1 px-2 py-1 text-sm text-pink-600 hover:text-pink-700 hover:bg-pink-50 rounded transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Create Tag
                  </button>
                </div>
                
                {/* Create Tag Form */}
                {showCreateTagForm && (
                  <div className="mb-3 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tag Name
                        </label>
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          placeholder="Enter tag name..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTagName.trim()) {
                              handleCreateTag();
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={newTagColor}
                            onChange={(e) => setNewTagColor(e.target.value)}
                            className="h-8 w-16 border border-gray-300 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={newTagColor}
                            onChange={(e) => setNewTagColor(e.target.value)}
                            placeholder="#3B82F6"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCreateTag}
                          disabled={!newTagName.trim() || isCreatingTag}
                          className="flex-1 px-3 py-2 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {isCreatingTag ? 'Creating...' : 'Create Tag'}
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateTagForm(false);
                            setNewTagName('');
                            setNewTagColor('#3B82F6');
                          }}
                          className="px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {loadingTags ? (
                  <div className="text-sm text-gray-500">Loading tags...</div>
                ) : customerTags.length === 0 && !showCreateTagForm ? (
                  <div className="text-sm text-gray-500">No tags available. Click "Create Tag" to add one.</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg bg-white">
                    {customerTags.map((tag) => (
                      <label
                        key={tag.id}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag.id)}
                          onChange={() => handleToggleTag(tag.id)}
                          className="mr-3"
                        />
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: tag.color || '#3B82F6' }}
                        />
                        <span className="text-sm text-gray-700">{tag.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={selectedTags.length === 0 || conditions.length === 0}
                className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationsV2; 